import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Login from '../components/Login';
import NotificationToaster from '../components/NotificationToaster';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import ComponentsPage from '../pages/ComponentsPage';
import PropertiesPage from '../pages/PropertiesPage';
import OptimizationResultsPage from '../pages/OptimizationResultsPage';
import UserManagement from '../components/UserManagement';
import { useData } from '../contexts/DataContext';
import ModelBasisPage from '../pages/ModelBasisPage';
import '../styles.css';
import '../button-styles.css';


const apiBase ='https://api.processoptimizer.ai/api';
const apiBasepy ='https://api.processoptimizer.ai/python/api';

// const isProduction = process.env.NODE_ENV === 'production';
// const apiBase = 'http://localhost:8001/api';
// const apiBasepy = 'http://localhost:8000/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FF6B6B', '#48DBFB', '#F368E0', '#1DD1A1'];

const api = {
  testConnectivity: () => fetch(`${apiBase}/test-connectivity`).then(res => res.json()),
  components: {
    getAll: () => fetch(`${apiBase}/components`).then(res => res.json()),
    getById: (id) => fetch(`${apiBase}/components/${id}`).then(res => res.json()),
    create: (data) => fetch(`${apiBase}/components`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    update: (id, data) => fetch(`${apiBase}/components/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    delete: (id) => fetch(`${apiBase}/components/${id}`, { method: 'DELETE' }).then(res => res.json()),
    importExcel: (formData) => fetch(`${apiBase}/components/import/excel`, {
      method: 'POST',
      body: formData
    }).then(res => res.json())
  },
  gradeProperties: {
    add: (data) => fetch(`${apiBase}/gradeProperties/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    getList: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return fetch(`${apiBase}/gradeProperties/getList?${queryString}`).then(res => res.json());
    },
    getById: (id) => fetch(`${apiBase}/gradeProperties/getById/${id}`).then(res => res.json()),
    edit: (id, data) => fetch(`${apiBase}/gradeProperties/editGradeProperties/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    delete: (id) => fetch(`${apiBase}/gradeProperties/removeGradeProperties/${id}`, { method: 'DELETE' }).then(res => res.json())
  },
  optimizedResults: {
    create: (data) => fetch(`${apiBase}/optimizedResults/createOptimizedResult`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(async res => {
      const responseText = await res.text();
      console.log("Raw API response:", responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error("Failed to parse JSON response:", e);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}, message: ${responseData.message}`);
      }
      return responseData;
    }),
    getResults: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return fetch(`${apiBase}/optimizedResults/getOptimizedResults?${queryString}`).then(res => res.json());
    },
    getComponentsWithOptimizeName: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return fetch(`${apiBase}/optimizedResults/componentsWithOptimizeName?${queryString}`).then(res => res.json());
    },
    getPropertyConstraintsWithOptimizeName: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return fetch(`${apiBase}/optimizedResults/getPropertyConstraintsWithOptimizeName?${queryString}`).then(res => res.json());
    },
    update: (id, data) => fetch(`${apiBase}/optimizedResults/updateOptimizedResult/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    delete: (id) => fetch(`${apiBase}/optimizedResults/delete/${id}`, { 
      method: 'DELETE' 
    }).then(res => res.json())
  },
  propertyConstraints: {
    create: (data) => fetch(`${apiBase}/propertyConstraints/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json())
  },
  globalProperties: {
    create: (data) => fetch(`${apiBase}/globalProperties/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    getAll: () => fetch(`${apiBase}/globalProperties`).then(res => res.json()),
    update: (id, data) => fetch(`${apiBase}/globalProperties/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    delete: (id) => fetch(`${apiBase}/globalProperties/${id}`, {
      method: 'DELETE'
    }).then(res => res.json())
  }
};

const num = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

function App() {
  // User State
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Navigation State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('optimizer');
  
  // Model Name & Management
  const [modelName, setModelName] = useState(() => localStorage.getItem('blendOptimizerModelName') || '');
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState(() => localStorage.getItem('blendOptimizerLastSaved') || '');
  const [currentModelId, setCurrentModelId] = useState(() => localStorage.getItem('blendOptimizerCurrentModelId') || null); // Track current model for Save vs Save As
  const [rightPanelTab, setRightPanelTab] = useState('components'); // components | properties | results | diagnostics

  // Bulk Action States
  const [bulkMinValue, setBulkMinValue] = useState('');
  const [bulkMaxValue, setBulkMaxValue] = useState('');
  const [bulkCostValue, setBulkCostValue] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Validation States
  const [validationErrors, setValidationErrors] = useState([]);

  // Page States
  const [showComponentsPage, setShowComponentsPage] = useState(false);
  const [showPropertiesPage, setShowPropertiesPage] = useState(false);
  const [showOptimizationResultsPage, setShowOptimizationResultsPage] = useState(false);
  const [showModelBasisPage, setShowModelBasisPage] = useState(false);
  
  // Model
  const [modelBasis, setModelBasis] = useState('volume');
  const [currency, setCurrency] = useState('INR');
  const [blendingType, setBlendingType] = useState('linear');
  const [indexFormula, setIndexFormula] = useState('');
  const [reverseFormula, setReverseFormula] = useState('');
  const [volumeUnit, setVolumeUnit] = useState('bbl');
  const [massUnit, setMassUnit] = useState('kg');
  const [componentBasis, setComponentBasis] = useState('volume');
  const [componentBlendingType, setComponentBlendingType] = useState('linear');
  
  // Optimization Inputs
  const [volume, setVolume] = useState(5000);
  const [gap, setGap] = useState(0.01);
  const [threads, setThreads] = useState(1);
  const [customTextField, setCustomTextField] = useState("");
  
  // Data State
  const [components, setComponents] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [propertySpecs, setPropertySpecs] = useState([]);
  const [selectedPropertyColumns, setSelectedPropertyColumns] = useState([]);
  const [customColumns, setCustomColumns] = useState([]);
  
  // Results State 
  const [blend, setBlend] = useState({});
  const [totalCost, setTotalCost] = useState(0);
  const [weight, setWeight] = useState(0);        // total mass in massUnit
  const [totalVolume, setTotalVolume] = useState(0); // total volume in volumeUnit
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [reportB64, setReportB64] = useState("");
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [propertyResults, setPropertyResults] = useState({});
  const [propertyResultsForDisplay, setPropertyResultsForDisplay] = useState({}); 

  // ── Soft-constraint / infeasibility-break state ──────────────────────
  const [violations, setViolations] = useState({});          // per-property violation report
  const [penaltyCost, setPenaltyCost] = useState(0);         // total penalty contribution
  const [blendCost, setBlendCost] = useState(0);             // pure blend cost (no penalty)
  const [infeasibilityDrivers, setInfeasibilityDrivers] = useState([]); // which props drove infeasibility
  const [autoRelax, setAutoRelax] = useState(false);         // auto_infeasibility_break flag

  // ── Specific Gravity constraint state (editable row) ──────────────────
  const [sgMin, setSgMin] = useState("");
  const [sgMax, setSgMax] = useState("");
  const [sgRelax, setSgRelax] = useState(false);
  const [sgPenalty, setSgPenalty] = useState("");
  
  // File 
  const [file, setFile] = useState(null);
  const [loadedFromExcel, setLoadedFromExcel] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState([]);
  
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showAddPropertyPopup, setShowAddPropertyPopup] = useState(false);
  const [showBlendComponentsModal, setShowBlendComponentsModal] = useState(false);
  const [showPropertyColumnModal, setShowPropertyColumnModal] = useState(false);
  const [showGlobalPropertiesModal, setShowGlobalPropertiesModal] = useState(false);
  const [showSaveAllPopup, setShowSaveAllPopup] = useState(false);
  const [showSaveAsPopup, setShowSaveAsPopup] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  
  const [globalComponents, setGlobalComponents] = useState([]);
  const [selectedGlobalComponents, setSelectedGlobalComponents] = useState([]);
  const [globalProperties, setGlobalProperties] = useState([]);
  const [selectedGlobalProperties, setSelectedGlobalProperties] = useState([]);
  const [blendComponentsModalData, setBlendComponentsModalData] = useState([]);
  const [selectedBlendComponents, setSelectedBlendComponents] = useState([]);
  const [savedGroups, setSavedGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [savedPropertySets, setSavedPropertySets] = useState([]);
  const [selectedPropertySet, setSelectedPropertySet] = useState('');
  const [componentGroups, setComponentGroups] = useState([]);
  const [selectedComponentGroup, setSelectedComponentGroup] = useState('');
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [componentGroupProperties, setComponentGroupProperties] = useState([]);
  
  const [newProperty, setNewProperty] = useState({
    property: '',
    uom: '',
    min: '',
    max: ''
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [saveAllName, setSaveAllName] = useState('');
  const [saveAsName, setSaveAsName] = useState('');
  
  const [apiConnected, setApiConnected] = useState(false);
  
  // Loading States
  const [loadingComponentGroups, setLoadingComponentGroups] = useState(false);
  const [loadingPropertyGroups, setLoadingPropertyGroups] = useState(false);
  const [loadingComponentGroup, setLoadingComponentGroup] = useState(false);
  const [loadingPropertyGroup, setLoadingPropertyGroup] = useState(false);
  const [loadingPropertySet, setLoadingPropertySet] = useState(false);
  const [loadingAddComponent, setLoadingAddComponent] = useState(false);
  const [loadingAddProperty, setLoadingAddProperty] = useState(false);
  const [loadingPropertyColumn, setLoadingPropertyColumn] = useState(false);
  const [loadingGlobalProperties, setLoadingGlobalProperties] = useState(false);
  const [loadingSaveAll, setLoadingSaveAll] = useState(false);
  const [loadingOptimization, setLoadingOptimization] = useState(false);
  const [loadingDownloadTemplate, setLoadingDownloadTemplate] = useState(false);
  const [loadingDownloadReport, setLoadingDownloadReport] = useState(false);
  
  // Refs
  const propertyColumnModalScrollRef = useRef(null);
  const globalPropertiesModalScrollRef = useRef(null);
  const saveAllNameInputRef = useRef(null);
  const { saveCostBreakdown, saveVisualization, saveComponents: saveComponentsToStorage, saveProperties: savePropertiesToStorage } = useData();

  // Edit mode state
  const [editingResult, setEditingResult] = useState(null);
  const [editingComponent, setEditingComponent] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);

  const loadEditData = (data) => {
    console.log("🔧 Loading edit data:", data);
    
    if (data.type === 'edit') {
      if (data.resultData) {
        const result = data.resultData;
        console.log("📥 Loading optimization result for editing:", result.customName);
        
        setEditingResult(result);
        setModelName(result.customName || result.optimizeName || result.customTextField || '');
        setSaveAsName(result.customName || result.customTextField || `Edit - ${formatDate(result.timestamp)}`);
        
        if (result.components && result.components.length > 0) {
          console.log("📥 Loading components from result:", result.components.length);
          const formattedComponents = result.components.map((comp, index) => {
            let costValue = comp.cost;
            if (costValue === null || costValue === undefined || costValue === '') {
              costValue = ''; 
            } else {
              const numCost = Number(costValue);
              if (isNaN(numCost) || numCost <= 0) {
                costValue = ''; 
              } else {
                costValue = numCost; 
              }
            }
            
            const formattedComp = {
              id: Date.now() + index + Math.random(),
              name: comp.originalName || comp.name || `Component_${index + 1}`,
              cost: costValue, 
              min: comp.min || comp.minVolume || 0,
              max: comp.max || comp.maxVolume || 1000,
              availability: comp.availableVolume || comp.availability || 1000,
              density: comp.density || 0,
              gravity: comp.gravity || 0,
              basis: comp.basis || 'volume',
              blending_type: comp.blending_type || comp.blendingType || 'linear',
              costUnit: comp.costUnit || currency || 'USD',
              volumeUnit: comp.volumeUnit || getCurrentUnit()
            };

            if (result.componentPropertyValues && result.componentPropertyValues.length > 0) {
              const componentPropValues = result.componentPropertyValues.find(
                cpv => (cpv.componentName || '').toLowerCase() === (comp.originalName || comp.name || '').toLowerCase()
              );
              if (componentPropValues && componentPropValues.propertyValues) {
                Object.keys(componentPropValues.propertyValues).forEach(propName => {
                  formattedComp[propName] = componentPropValues.propertyValues[propName] || 0;
                });
              }
            }

            Object.keys(comp).forEach(key => {
              if (!['name', 'originalName', 'cost', 'costUnit', 'min', 'max', 'minVolume', 'maxVolume', 'availability', 'availableVolume', 'volumeUnit', 'density', 'gravity', 'basis', 'blending_type', 'blendingType', '_id'].includes(key)) {
                formattedComp[key] = comp[key] || 0;
              }
            });

            return formattedComp;
          });
          
          setComponents(formattedComponents);
          setSelectedComponents(new Array(formattedComponents.length).fill(true));
          console.log(`Loaded ${formattedComponents.length} components from result`);
        }
        
        if (result.constraintsProperties && result.constraintsProperties.length > 0) {
          console.log("Loading properties from result:", result.constraintsProperties.length);
          const formattedProperties = result.constraintsProperties.map((prop, index) => ({
            id: Date.now() + index + Math.random(),
            property: prop.originalName || prop.property || prop.name || `Property_${index + 1}`,
            uom: prop.uom || prop.volumeUnit || 'units',
            min: prop.min || prop.minVolume || 0,
            max: prop.max || prop.maxVolume || 100,
            basis: prop.basis || 'volume',
            linear: prop.linear !== undefined ? prop.linear : true,
            index_formula: prop.indexFormula || prop.index_formula || '',
            reverse_formula: prop.reverseFormula || prop.reverse_formula || '',
            actualResult: prop.actualResult || 0
          }));
          
          setPropertySpecs(formattedProperties);
          
          const propertyColumns = formattedProperties
            .filter(p => p.property && p.property.trim() !== '')
            .map(p => p.property.trim());
          
          setSelectedPropertyColumns(prev => {
            const newColumns = [...prev];
            propertyColumns.forEach(col => {
              if (!newColumns.includes(col)) {
                newColumns.push(col);
              }
            });
            return newColumns;
          });
          console.log(`Loaded ${formattedProperties.length} properties from result`);
        }
        
        if (result.selectedPropertyColumns && Array.isArray(result.selectedPropertyColumns)) {
          setSelectedPropertyColumns(result.selectedPropertyColumns);
          setCustomColumns(result.selectedPropertyColumns);
        }
        
        if (result.blend && Object.keys(result.blend).length > 0) {
          setBlend(result.blend);
          setTotalCost(result.totalCost || 0);
          setStatus(result.status || "Loaded for Editing");
          
          const totalVolume = Object.values(result.blend).reduce((a, b) => a + Number(b || 0), 0);
          setVolume(totalVolume);
        }
        
        if (result.customTextField) {
          setCustomTextField(result.customTextField);
        }
        
        if (result.modelBasis) {
          setModelBasis(result.modelBasis);
        }
        if (result.currency) {
          setCurrency(result.currency);
        }
        if (result.blendingType) {
          setBlendingType(result.blendingType);
        }
        if (result.componentBasis) {
          setComponentBasis(result.componentBasis);
        }
        if (result.componentBlendingType) {
          setComponentBlendingType(result.componentBlendingType);
        }
        
        addNotification("success", `Loaded "${result.customName || result.optimizeName}" for editing with components and properties. Make changes and click "Save As" to update.`);

        const persistentEditData = {
          type: 'edit',
          resultData: result,
          itemId: result._id,
          itemType: 'result'
        };
        localStorage.setItem('blendOptimizerLoadedEditData', JSON.stringify(persistentEditData));

      } else {
        setComponents([]);
        setPropertySpecs([]);
        setSelectedPropertyColumns([]);
        setCustomColumns([]);
        
        const componentsToEdit = localStorage.getItem('componentsToEdit');
        const propertiesToEdit = localStorage.getItem('propertiesToEdit');
        
        if (componentsToEdit) {
          try {
            const componentsData = JSON.parse(componentsToEdit);
            console.log("Loading components for editing:", componentsData.customName);
            
            if (componentsData.components && componentsData.components.length > 0) {
              const validatedComponents = componentsData.components.map(comp => {
                const validatedComp = { ...comp };
                if (validatedComp.cost === null || validatedComp.cost === undefined || validatedComp.cost === '') {
                  validatedComp.cost = '';
                } else {
                  const numCost = Number(validatedComp.cost);
                  if (isNaN(numCost) || numCost <= 0) {
                    validatedComp.cost = '';
                  }
                }
                return validatedComp;
              });
              
              setComponents(validatedComponents);
              setSelectedComponents(new Array(validatedComponents.length).fill(true));
              
              if (componentsData.selectedPropertyColumns) {
                setSelectedPropertyColumns(componentsData.selectedPropertyColumns);
                setCustomColumns(componentsData.selectedPropertyColumns);
              }
              
              if (componentsData.modelBasis) {
                setModelBasis(componentsData.modelBasis);
              }
              
              console.log(`Loaded ${componentsData.components.length} components for editing`);
            }
          } catch (error) {
            console.error('Error loading components to edit:', error);
          }
        }
        
        if (propertiesToEdit) {
          try {
            const propertiesData = JSON.parse(propertiesToEdit);
            console.log("Loading properties for editing:", propertiesData.customName);
            
            if (propertiesData.properties && propertiesData.properties.length > 0) {
              setPropertySpecs(propertiesData.properties);
              
              const propertyColumns = propertiesData.properties
                .filter(p => p.property && p.property.trim() !== '')
                .map(p => p.property.trim());
              
              setSelectedPropertyColumns(prev => {
                const newColumns = [...prev];
                propertyColumns.forEach(col => {
                  if (!newColumns.includes(col)) {
                    newColumns.push(col);
                  }
                });
                return newColumns;
              });
              
              console.log(`Loaded ${propertiesData.properties.length} properties for editing`);
            }
          } catch (error) {
            console.error('Error loading properties to edit:', error);
          }
        }
      }
      
      if (data.componentData) {
        const componentData = data.componentData;
        console.log("Loading component for editing:", componentData.customName);
        
        setEditingComponent(componentData);
        setSaveAsName(componentData.customName || `Edit Component - ${new Date().toLocaleDateString()}`);
        
        addNotification("success", `Loaded "${componentData.customName}" for editing.`);
        
      } else if (data.propertyData) {
        const propertyData = data.propertyData;
        console.log("Loading property for editing:", propertyData.customName);
        
        setEditingProperty(propertyData);
        setSaveAsName(propertyData.customName || `Edit Property - ${new Date().toLocaleDateString()}`);
        
        addNotification("success", `Loaded "${propertyData.customName}" for editing.`);
      }
    }
  };

  const calculatePropertyResults = () => {
    if (Object.keys(blend).length === 0 || components.length === 0) {
      setPropertyResults({});
      setPropertyResultsForDisplay({});
      return;
    }

    const filteredComponents = components;
    const newPropertyResults = {};
    const newPropertyResultsForDisplay = {};

    propertySpecs.forEach(prop => {
      if (prop.property && prop.property.trim() !== '') {
        let totalPropertyValue = 0;
        let totalWeight = 0;

        // prop.basis determines whether to weight by volume or mass
        // blend values are in the model basis unit (volume if volume basis, mass if mass basis)
        // For volume-basis properties when model is mass-basis: convert mass -> volume using SG
        const propBasis = String(prop.basis || 'volume').toLowerCase();

        Object.entries(blend).forEach(([name, blendQty]) => {
          const component = filteredComponents.find(c => c.name === name);
          if (component && component[prop.property] !== undefined && component[prop.property] !== null) {
            // Determine weighting quantity (w) based on prop.basis vs modelBasis
            let w;
            const blendQtyNum = Number(blendQty) || 0;
            if (modelBasis === 'mass') {
              if (propBasis === 'volume') {
                // blend is in mass units; convert to volume using SG
                const sg = Number(component.gravity || component['specific_gravity'] || component['specific gravity'] || component.sg || 1.0) || 1.0;
                w = sg > 0 ? blendQtyNum / sg : blendQtyNum;
              } else {
                // mass-basis property: use mass directly
                w = blendQtyNum;
              }
            } else {
              // volume model basis
              if (propBasis === 'mass') {
                // volume-basis model, mass-basis property: convert volume -> mass using SG
                const sg = Number(component.gravity || component['specific_gravity'] || component['specific gravity'] || component.sg || 1.0) || 1.0;
                w = blendQtyNum * sg;
              } else {
                // volume-basis property: use volume directly
                w = blendQtyNum;
              }
            }

            if (prop.linear === false && prop.index_formula) {
              try {
                const propValue = Number(component[prop.property]) || 0;
                const x = propValue;
                let indexValue;
                
                if (prop.index_formula) {
                  if (prop.index_formula.toLowerCase().includes('log')) {
                    indexValue = Math.log10(x || 1);
                  } else {
                    indexValue = x; 
                  }
                } else {
                  indexValue = x;
                }
                
                totalPropertyValue += indexValue * w;
                totalWeight += w;
              } catch (error) {
                console.error(`Error calculating non-linear property ${prop.property}:`, error);
                totalPropertyValue += Number(component[prop.property]) * w;
                totalWeight += w;
              }
            } else {
              totalPropertyValue += Number(component[prop.property]) * w;
              totalWeight += w;
            }
          }
        });

        if (totalWeight > 0) {
          let resultValue = totalPropertyValue / totalWeight;
          
          if (prop.linear === false && prop.reverse_formula) {
            try {
              if (prop.reverse_formula.toLowerCase().includes('10**') || prop.reverse_formula.toLowerCase().includes('pow')) {
                resultValue = Math.pow(10, resultValue);
              }
            } catch (error) {
              console.error(`Error applying reverse formula for ${prop.property}:`, error);
            }
          }
          
          newPropertyResults[prop.property] = resultValue;
          newPropertyResultsForDisplay[prop.property] = resultValue;
        } else {
          newPropertyResults[prop.property] = 0;
          newPropertyResultsForDisplay[prop.property] = 0;
        }
      }
    });

    setPropertyResults(newPropertyResults);
    setPropertyResultsForDisplay(newPropertyResultsForDisplay);
  };

  const calculateAllPropertyResults = () => {
    if (components.length === 0 || propertySpecs.length === 0) {
      setPropertyResults({});
      setPropertyResultsForDisplay({});
      return;
    }

    const filteredComponents = components;
    const newPropertyResults = {};
    const newPropertyResultsForDisplay = {};

    propertySpecs.forEach(prop => {
      if (prop.property && prop.property.trim() !== '') {
        let totalPropertyValue = 0;
        let totalWeight = 0;

        // prop.basis determines whether to weight by volume or mass
        const propBasis = String(prop.basis || 'volume').toLowerCase();

        if (Object.keys(blend).length > 0) {
          Object.entries(blend).forEach(([name, blendQty]) => {
            const component = filteredComponents.find(c => c.name === name);
            if (component && component[prop.property] !== undefined && component[prop.property] !== null) {
              // Determine weighting quantity (w) based on prop.basis vs modelBasis
              let w;
              const blendQtyNum = Number(blendQty) || 0;
              if (modelBasis === 'mass') {
                if (propBasis === 'volume') {
                  // blend is in mass units; convert to volume using SG
                  const sg = Number(component.gravity || component['specific_gravity'] || component['specific gravity'] || component.sg || 1.0) || 1.0;
                  w = sg > 0 ? blendQtyNum / sg : blendQtyNum;
                } else {
                  w = blendQtyNum;
                }
              } else {
                if (propBasis === 'mass') {
                  const sg = Number(component.gravity || component['specific_gravity'] || component['specific gravity'] || component.sg || 1.0) || 1.0;
                  w = blendQtyNum * sg;
                } else {
                  w = blendQtyNum;
                }
              }

              if (prop.linear === false && prop.index_formula) {
                try {
                  const propValue = Number(component[prop.property]) || 0;
                  const x = propValue;
                  let indexValue;
                  
                  if (prop.index_formula.toLowerCase().includes('log')) {
                    indexValue = Math.log10(x || 1);
                  } else {
                    indexValue = x;
                  }
                  
                  totalPropertyValue += indexValue * w;
                  totalWeight += w;
                } catch (error) {
                  totalPropertyValue += Number(component[prop.property]) * w;
                  totalWeight += w;
                }
              } else {
                totalPropertyValue += Number(component[prop.property]) * w;
                totalWeight += w;
              }
            }
          });
        } else {
          // No blend yet: use equal weight for preview (equal volume or equal mass)
          const equalWeight = 100;
          filteredComponents.forEach(component => {
            if (component && component[prop.property] !== undefined && component[prop.property] !== null) {
              if (prop.linear === false && prop.index_formula) {
                try {
                  const propValue = Number(component[prop.property]) || 0;
                  const x = propValue;
                  let indexValue;
                  
                  if (prop.index_formula.toLowerCase().includes('log')) {
                    indexValue = Math.log10(x || 1);
                  } else {
                    indexValue = x;
                  }
                  
                  totalPropertyValue += indexValue * equalWeight;
                  totalWeight += equalWeight;
                } catch (error) {
                  totalPropertyValue += Number(component[prop.property]) * equalWeight;
                  totalWeight += equalWeight;
                }
              } else {
                totalPropertyValue += Number(component[prop.property]) * equalWeight;
                totalWeight += equalWeight;
              }
            }
          });
        }

        if (totalWeight > 0) {
          let resultValue = totalPropertyValue / totalWeight;
          
          if (prop.linear === false && prop.reverse_formula) {
            try {
              if (prop.reverse_formula.toLowerCase().includes('10**') || prop.reverse_formula.toLowerCase().includes('pow')) {
                resultValue = Math.pow(10, resultValue);
              }
            } catch (error) {
              console.error(`Error applying reverse formula for ${prop.property}:`, error);
            }
          }
          
          newPropertyResults[prop.property] = resultValue;
          newPropertyResultsForDisplay[prop.property] = resultValue;
        } else {
          newPropertyResults[prop.property] = 0;
          newPropertyResultsForDisplay[prop.property] = 0;
        }
      }
    });

    setPropertyResults(newPropertyResults);
    setPropertyResultsForDisplay(newPropertyResultsForDisplay);
  };

  const calculateBlendedPropertyValue = (propertyName, propertySpec) => {
    if (!propertyName || !blend || Object.keys(blend).length === 0) {
      return 0;
    }

    const filteredComponents = components;
    let totalPropertyValue = 0;
    let totalWeight = 0;

    // prop.basis determines volume vs mass weighting
    const propBasis = String((propertySpec && propertySpec.basis) || 'volume').toLowerCase();

    Object.entries(blend).forEach(([compName, blendQty]) => {
      const component = filteredComponents.find(c => c.name === compName);
      if (component && component[propertyName] !== undefined) {
        const propValue = Number(component[propertyName]) || 0;
        const blendQtyNum = Number(blendQty) || 0;

        // Determine weighting quantity (w) based on prop.basis vs modelBasis
        let w;
        if (modelBasis === 'mass') {
          if (propBasis === 'volume') {
            const sg = Number(component.gravity || component['specific_gravity'] || component['specific gravity'] || component.sg || 1.0) || 1.0;
            w = sg > 0 ? blendQtyNum / sg : blendQtyNum;
          } else {
            w = blendQtyNum;
          }
        } else {
          if (propBasis === 'mass') {
            const sg = Number(component.gravity || component['specific_gravity'] || component['specific gravity'] || component.sg || 1.0) || 1.0;
            w = blendQtyNum * sg;
          } else {
            w = blendQtyNum;
          }
        }
        
        if (propertySpec && propertySpec.linear === false && propertySpec.index_formula) {
          try {
            let indexValue = propValue;
            if (propertySpec.index_formula.toLowerCase().includes('log')) {
              indexValue = Math.log10(propValue || 1);
            }
            totalPropertyValue += indexValue * w;
          } catch (error) {
            totalPropertyValue += propValue * w;
          }
        } else {
          totalPropertyValue += propValue * w;
        }
        totalWeight += w;
      }
    });

    if (totalWeight > 0) {
      let result = totalPropertyValue / totalWeight;
      
      if (propertySpec && propertySpec.linear === false && propertySpec.reverse_formula) {
        try {
          if (propertySpec.reverse_formula.toLowerCase().includes('10**')) {
            result = Math.pow(10, result);
          }
        } catch (error) {
          console.error('Error applying reverse formula:', error);
        }
      }
      
      return result;
    }
    
    return 0;
  };

  useEffect(() => {
    const componentsToLoad = localStorage.getItem('componentsToLoad');
    if (componentsToLoad) {
      try {
        const loadedComponents = JSON.parse(componentsToLoad);
        console.log("Loaded components from storage:", loadedComponents);
        
        if (loadedComponents.components && loadedComponents.components.length > 0) {
          const formattedComponents = loadedComponents.components.map(comp => {
            const formattedComp = {
              name: comp.name || '',
              cost: comp.cost || '',
              min: comp.min || '',
              max: comp.max || '',
              availability: comp.availability || 1000,
              density: comp.density || '',
              gravity: comp.gravity || ''
            };
            
            Object.keys(comp).forEach(key => {
              if (!['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'].includes(key.toLowerCase())) {
                formattedComp[key] = comp[key] || 0;
              }
            });
            
            return formattedComp;
          });
          
          setComponents(formattedComponents);
          setSelectedComponents(new Array(formattedComponents.length).fill(true));
          
          if (loadedComponents.selectedPropertyColumns) {
            setSelectedPropertyColumns(loadedComponents.selectedPropertyColumns);
          }
          
          addNotification("success", `Loaded ${formattedComponents.length} components from saved data`);

          // Persist loaded components data for refresh
          const persistentComponentsData = {
            ...loadedComponents,
            components: formattedComponents
          };
          localStorage.setItem('blendOptimizerLoadedComponents', JSON.stringify(persistentComponentsData));

          localStorage.removeItem('componentsToLoad');
        }
      } catch (error) {
        console.error('Error loading components from storage:', error);
        addNotification("error", "Failed to load components from storage");
      }
    }

    // Load persisted components data on refresh
    const persistedComponents = localStorage.getItem('blendOptimizerLoadedComponents');
    if (persistedComponents && !componentsToLoad) {
      try {
        const loadedComponents = JSON.parse(persistedComponents);
        console.log("Loaded persisted components from storage:", loadedComponents);

        if (loadedComponents.components && loadedComponents.components.length > 0) {
          setComponents(loadedComponents.components);
          setSelectedComponents(new Array(loadedComponents.components.length).fill(true));

          if (loadedComponents.selectedPropertyColumns) {
            setSelectedPropertyColumns(loadedComponents.selectedPropertyColumns);
          }

          console.log(`✅ Loaded persisted ${loadedComponents.components.length} components from refresh`);
        }
      } catch (error) {
        console.error('Error loading persisted components:', error);
        localStorage.removeItem('blendOptimizerLoadedComponents');
      }
    }

    const propertiesToLoad = localStorage.getItem('propertiesToLoad');
    if (propertiesToLoad) {
      try {
        const loadedProperties = JSON.parse(propertiesToLoad);
        console.log("Loaded properties from storage:", loadedProperties);
        
        if (loadedProperties.properties && loadedProperties.properties.length > 0) {
          const formattedProperties = loadedProperties.properties.map(prop => ({
            property: prop.property || '',
            uom: prop.uom || '',
            min: prop.min || '',
            max: prop.max || '',
            basis: prop.basis || 'volume',
            linear: prop.linear !== undefined ? prop.linear : true,
            index_formula: prop.index_formula || '',
            reverse_formula: prop.reverse_formula || ''
          }));
          
          setPropertySpecs(formattedProperties);
          
          const propertyColumns = formattedProperties
            .filter(p => p.property && p.property.trim() !== '')
            .map(p => p.property.trim());
          
          setSelectedPropertyColumns(prev => {
            const newColumns = [...prev];
            propertyColumns.forEach(col => {
              if (!newColumns.includes(col)) {
                newColumns.push(col);
              }
            });
            return newColumns;
          });
          
          addNotification("success", `Loaded ${formattedProperties.length} properties from saved data`);

          // Persist loaded properties data for refresh
          const persistentPropertiesData = {
            ...loadedProperties,
            properties: formattedProperties
          };
          localStorage.setItem('blendOptimizerLoadedProperties', JSON.stringify(persistentPropertiesData));

          localStorage.removeItem('propertiesToLoad');
        }
      } catch (error) {
        console.error('Error loading properties from storage:', error);
        addNotification("error", "Failed to load properties from storage");
      }
    }

    // Load persisted properties data on refresh
    const persistedProperties = localStorage.getItem('blendOptimizerLoadedProperties');
    if (persistedProperties && !propertiesToLoad) {
      try {
        const loadedProperties = JSON.parse(persistedProperties);
        console.log("Loaded persisted properties from storage:", loadedProperties);

        if (loadedProperties.properties && loadedProperties.properties.length > 0) {
          setPropertySpecs(loadedProperties.properties);

          const propertyColumns = loadedProperties.properties
            .filter(p => p.property && p.property.trim() !== '')
            .map(p => p.property.trim());

          setSelectedPropertyColumns(prev => {
            const newColumns = [...prev];
            propertyColumns.forEach(col => {
              if (!newColumns.includes(col)) {
                newColumns.push(col);
              }
            });
            return newColumns;
          });

          console.log(`✅ Loaded persisted ${loadedProperties.properties.length} properties from refresh`);
        }
      } catch (error) {
        console.error('Error loading persisted properties:', error);
        localStorage.removeItem('blendOptimizerLoadedProperties');
      }
    }

    const customNotes = localStorage.getItem('customNotesToLoad');
    if (customNotes) {
      setCustomTextField(customNotes);
      localStorage.removeItem('customNotesToLoad');
    }

    const editData = localStorage.getItem('blendOptimizerEditData');
    const persistentEditData = localStorage.getItem('blendOptimizerLoadedEditData');

    if (editData) {
      try {
        const parsedData = JSON.parse(editData);
        loadEditData(parsedData);

        setTimeout(() => {
          localStorage.removeItem('blendOptimizerEditData');
          localStorage.removeItem('componentsToEdit');
          localStorage.removeItem('propertiesToEdit');
        }, 1000);

      } catch (error) {
        console.error('Error loading edit data:', error);
        addNotification("error", "Failed to load edit data");
        localStorage.removeItem('blendOptimizerEditData');
        localStorage.removeItem('componentsToEdit');
        localStorage.removeItem('propertiesToEdit');
      }
    } else if (persistentEditData) {
      // Load persisted data on refresh
      try {
        const parsedData = JSON.parse(persistentEditData);
        loadEditData(parsedData);
        console.log("✅ Loaded persisted edit data from refresh");
      } catch (error) {
        console.error('Error loading persisted edit data:', error);
        localStorage.removeItem('blendOptimizerLoadedEditData');
      }
    }
    
    checkBackendConnectivity();
    testAPI();
    fetchComponentGroups();
    fetchPropertyGroups();
    fetchPropertySets();
    
    const storedUser = localStorage.getItem('blendOptimizerCurrentUser');
    if (storedUser) setCurrentUser(JSON.parse(storedUser));

    const storedUsers = localStorage.getItem('blendOptimizerUsers');
    if (storedUsers) {
      setUsers(JSON.parse(storedUsers));
    } else {
      const defaultUsers = [{ id: 1, username: 'admin', password: 'admin123', role: 'admin' }];
      localStorage.setItem('blendOptimizerUsers', JSON.stringify(defaultUsers));
      setUsers(defaultUsers);
    }

    // Load saved settings
    const savedSettings = [
      { key: 'blendOptimizerModelBasis', setter: setModelBasis },
      { key: 'blendOptimizerCurrency', setter: setCurrency },
      { key: 'blendOptimizerBlendingType', setter: setBlendingType },
      { key: 'blendOptimizerIndexFormula', setter: setIndexFormula },
      { key: 'blendOptimizerReverseFormula', setter: setReverseFormula },
      { key: 'blendOptimizerVolumeUnit', setter: setVolumeUnit },
      { key: 'blendOptimizerMassUnit', setter: setMassUnit },
      { key: 'blendOptimizerComponentBasis', setter: setComponentBasis },
      { key: 'blendOptimizerComponentBlendingType', setter: setComponentBlendingType }
    ];

    savedSettings.forEach(({ key, setter }) => {
      const saved = localStorage.getItem(key);
      if (saved) setter(saved);
    });
  }, []);

  useEffect(() => {
    if (components.length > 0) {
      setSelectedComponents(new Array(components.length).fill(true));
      setTimeout(() => {
        calculateAllPropertyResults();
      }, 100);
    }
  }, [components]);

  useEffect(() => {
    const propertyNames = propertySpecs
      .filter(prop => prop.property && prop.property.trim() !== '')
      .map(prop => prop.property.trim());

    const newPropertyColumns = selectedPropertyColumns.filter(prop =>
      propertyNames.includes(prop)
    );

    propertyNames.forEach(propertyName => {
      if (!newPropertyColumns.includes(propertyName)) {
        newPropertyColumns.push(propertyName);
      }
    });

    if (JSON.stringify(newPropertyColumns) !== JSON.stringify(selectedPropertyColumns)) {
      setSelectedPropertyColumns(newPropertyColumns);
    }
  }, [propertySpecs]);

  useEffect(() => {
    if (components.length > 0 && selectedPropertyColumns.length > 0) {
      const updatedComponents = components.map(comp => {
        const updatedComp = { ...comp };

        Object.keys(updatedComp).forEach(key => {
          const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'];
          if (!fixedFields.includes(key.toLowerCase()) &&
            !selectedPropertyColumns.includes(key)) {
            delete updatedComp[key];
          }
        });

        selectedPropertyColumns.forEach(prop => {
          if (!(prop in updatedComp)) {
            updatedComp[prop] = 0;
          }
        });

        return updatedComp;
      });

      if (JSON.stringify(updatedComponents) !== JSON.stringify(components)) {
        setComponents(updatedComponents);
      }

      setTimeout(() => {
        calculateAllPropertyResults();
      }, 100);
    }
  }, [selectedPropertyColumns]);

  useEffect(() => {
    if (showSaveAllPopup && saveAllNameInputRef.current) {
      setTimeout(() => {
        saveAllNameInputRef.current?.focus();
      }, 100);
    }
  }, [showSaveAllPopup]);

  useEffect(() => {
    if (Object.keys(blend).length > 0 && components.length > 0 && propertySpecs.length > 0) {
      setTimeout(() => {
        calculatePropertyResults();
      }, 100);
    }
  }, [blend, components, propertySpecs]);

  // API Functions
  const testAPI = async () => {
    try {
      const response = await api.testConnectivity();
      if (response.success) {
        setApiConnected(true);
      }
    } catch (error) {
      console.error('API Connectivity failed:', error);
      setApiConnected(false);
      addNotification("warning", "Backend connection failed. Using local storage only.");
    }
  };

  const checkBackendConnectivity = async () => {
    try {
      const response = await fetch(`${apiBasepy}/test-connectivity`);
      if (response.ok) {
        console.log("Backend connectivity: OK");
        return true;
      }
    } catch (error) {
      console.error("Backend connectivity failed:", error);
      setError(`Cannot connect to backend: ${error.message}`);
      addNotification("error", "Cannot connect to backend");
    }
    return false;
  };

  const fetchComponentGroups = async () => {
    setLoadingComponentGroups(true);
    try {
      const response = await fetch(`${apiBase}/group-reports`);
      const result = await response.json();

      if (result.success) {
        setComponentGroups(result.data || []);
      } else {
        throw new Error(result.message || "Failed to fetch component groups");
      }
    } catch (error) {
      console.error("Error fetching component groups:", error);
      addNotification("error", `Failed to load component groups: ${error.message}`);
    } finally {
      setLoadingComponentGroups(false);
    }
  };

  const fetchPropertyGroups = async () => {
    setLoadingPropertyGroups(true);
    try {
      const localGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
      if (localGroups.length > 0) {
        setPropertyGroups(localGroups);
        const formattedGroups = localGroups.map(group => ({
          id: group._id,
          name: group.name,
          properties: group.properties || [],
          description: group.description,
          createdAt: group.createdAt
        }));
        setSavedGroups(formattedGroups);
      }

      const response = await fetch(`${apiBase}/propertyGroups`);
      const result = await response.json();

      if (result.success) {
        setPropertyGroups(result.data || []);
        const formattedGroups = result.data.map(group => ({
          id: group._id,
          name: group.name,
          properties: group.properties || [],
          description: group.description,
          createdAt: group.createdAt
        }));
        setSavedGroups(formattedGroups);

        localStorage.setItem('propertyGroups', JSON.stringify(result.data || []));
      }
    } catch (error) {
      console.error("Error fetching property groups:", error);
      addNotification("error", `Failed to load property groups: ${error.message}`);
    } finally {
      setLoadingPropertyGroups(false);
    }
  };

  const fetchPropertySets = async () => {
    try {
      const localSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      if (localSets.length > 0) {
        setSavedPropertySets(localSets);
      }

      const response = await fetch(`${apiBase}/propertySets`);
      const result = await response.json();

      if (result.success) {
        setSavedPropertySets(result.data || []);
        localStorage.setItem('savedPropertySets', JSON.stringify(result.data || []));
      } else {
        const savedSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
        setSavedPropertySets(savedSets);
      }
    } catch (error) {
      console.error("Error fetching property sets:", error);
      const savedSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      setSavedPropertySets(savedSets);
    }
  };


const addComponent = () => {
  const newComponent = {
    name: `Component_${components.length + 1}`,
    cost: '',
    min: '',
    max: '',
    availability: 1000,
    density: '',
    gravity: '',
    ...Object.fromEntries(
      selectedPropertyColumns.map(prop => [prop, 0])
    )
  };

  setComponents(prev => [...prev, newComponent]);
  setSelectedComponents(prev => [...prev, true]);
  addNotification("success", "New component added");

  setTimeout(() => {
    calculateAllPropertyResults();
  }, 100);
};

  const removeComponent = (index) => {
    setComponents(prev => prev.filter((_, i) => i !== index));
    setSelectedComponents(prev => prev.filter((_, i) => i !== index));

    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

const editComp = (idx, field, value) => {
  if (field === "name") return;

  setComponents(prev => {
    const newComponents = [...prev];
    const component = { ...newComponents[idx] };
    
    if (field === "cost" || field === "min" || field === "max" || 
        field === "availability" || field === "density" || field === "gravity") {
      component[field] = value === "" ? "" : num(value);
    } else if (selectedPropertyColumns.includes(field)) {
      component[field] = value === "" ? 0 : num(value);
    } else {
      component[field] = value === "" ? "" : value;
    }
    
    newComponents[idx] = component;
    return newComponents;
  });

  if (field === 'cost') {
    const costValue = Number(value);
    if (isNaN(costValue) || costValue < 0) {
      addNotification("warning", `Please add valid cost (greater than 0) for component`);
    }
  }

  setTimeout(() => {
    calculateAllPropertyResults();
  }, 100);
};

  const toggleComponentSelection = (index) => {
    const newSelected = [...selectedComponents];
    newSelected[index] = !newSelected[index];
    setSelectedComponents(newSelected);
    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  const selectAllComponents = () => {
    setSelectedComponents(new Array(components.length).fill(true));
    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  const deselectAllComponents = () => {
    setSelectedComponents(new Array(components.length).fill(false));
    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  // Property Management
  const addProp = () => {
    setPropertySpecs(prev => [...prev, {
      property: "",
      uom: "",
      min: "",
      max: "",
      relax: false,
      penalty: "",
    }]);
    addNotification("info", "Added new property constraint");

    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  const removeProp = (idx) => {
    const propertyToRemove = propertySpecs[idx]?.property;

    setPropertySpecs(prev => prev.filter((_, i) => i !== idx));

    if (propertyToRemove && selectedPropertyColumns.includes(propertyToRemove)) {
      const newPropertyColumns = selectedPropertyColumns.filter(prop => prop !== propertyToRemove);
      setSelectedPropertyColumns(newPropertyColumns);
    }

    setPropertyResults(prev => {
      const newResults = { ...prev };
      delete newResults[propertyToRemove];
      return newResults;
    });

    setPropertyResultsForDisplay(prev => {
      const newResults = { ...prev };
      delete newResults[propertyToRemove];
      return newResults;
    });


    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  const editProp = (idx, field, value) => {
    setPropertySpecs(prev => prev.map((prop, i) =>
      i === idx ? { ...prop, [field]: value } : prop
    ));

    if (field === 'property' && value.trim() !== '') {
      const newPropertyColumns = [...selectedPropertyColumns];
      const oldPropertyName = propertySpecs[idx]?.property;

      if (oldPropertyName && newPropertyColumns.includes(oldPropertyName)) {
        const indexToRemove = newPropertyColumns.indexOf(oldPropertyName);
        newPropertyColumns.splice(indexToRemove, 1);
      }

      if (!newPropertyColumns.includes(value.trim())) {
        newPropertyColumns.push(value.trim());
      }

      setSelectedPropertyColumns(newPropertyColumns);
    }

    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  const handleEditProperty = (index) => {
    const property = propertySpecs[index];
    setNewProperty({
      property: property.property || '',
      uom: property.uom || '',
      min: property.min || '',
      max: property.max || ''
    });
    setEditingIndex(index);
    setShowAddPropertyPopup(true);
  };

  // File Handling
  const onChooseFile = (e) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    if (selectedFile) addNotification("info", `Selected file: ${selectedFile.name}`);
  };

 
  const loadAndEdit = async () => {
  resetResults();
  if (!file) {
    setError("Please choose an Excel file first.");
    addNotification("error", "Please select an Excel file first");
    return;
  }

  setLoading(true);
  try {
    const form = new FormData();
    form.append("file", file);

    console.log('Uploading file to:', `${apiBase}/parse/excel`);

    const res = await fetch(`${apiBase}/parse/excel`, {
      method: "POST",
      body: form
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData?.detail || `Parse failed (HTTP ${res.status})`);
    }

    const data = await res.json();
    console.log('Excel parse response:', data);

    const propertySpecsData = (data.property_specs || []).map(prop => ({
      property: String(prop.property || "").trim(),
      uom: prop.uom || "",
      min: prop.min || "",
      max: prop.max || "",
      basis: prop.basis || 'volume',
      linear: prop.linear !== undefined ? prop.linear : true,
      index_formula: prop.index_formula || '',
      reverse_formula: prop.reverse_formula || ''
    }));

    const baseComponent = (data.components || []).map(comp => {
      const normalizedComp = {
        name: comp.name || '',
        cost: comp.cost || '',
        min: comp.min || '',
        max: comp.max || '',
        availability: comp.availability || 1000,
        density: comp.density || 0,
        gravity: comp.gravity || '',
        basis: comp.basis || 'volume',
        blending_type: comp.blending_type || 'linear'
      };

      propertySpecsData.forEach(prop => {
        const propName = prop.property;
        if (propName && comp[propName] !== undefined) {
          normalizedComp[propName] = comp[propName] || 0;
        }
      });

      Object.keys(comp).forEach(key => {
        const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'];
        if (!fixedFields.includes(key.toLowerCase()) && 
            !propertySpecsData.some(p => p.property === key)) {
          normalizedComp[key] = comp[key] || 0;
        }
      });

      return normalizedComp;
    });

    setComponents(componentsWithProperties);
    setPropertySpecs(propertySpecsData);
    setLoadedFromExcel(true);

    const propertyNames = propertySpecsData
      .filter(prop => prop.property && prop.property.trim() !== '')
      .map(prop => prop.property.trim());

    const newPropertyColumns = [...new Set([...selectedPropertyColumns, ...propertyNames])];
    setSelectedPropertyColumns(newPropertyColumns);

    const updatedComponents = componentsWithProperties.map(comp => {
      const updatedComp = { ...comp };
      newPropertyColumns.forEach(prop => {
        if (!(prop in updatedComp)) {
          updatedComp[prop] = 0;
        }
      });
      return updatedComp;
    });

    setComponents(updatedComponents);
    setSelectedComponents(new Array(updatedComponents.length).fill(true));

    addNotification("success", "Excel file loaded successfully");

    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  } catch (err) {
    console.error('Error loading Excel:', err);
    setError(String(err));
    addNotification("error", `Failed to load Excel file: ${err.message}`);
  } finally {
    setLoading(false);
  }
};
  
  const calculateWeight = (blendData) => {
    if (!blendData || Object.keys(blendData).length === 0) {
      return 0;
    }

    let totalWeight = 0;
    const filteredComponents = components;

    Object.entries(blendData).forEach(([componentName, volume]) => {
      const component = filteredComponents.find(c => c.name === componentName);
      if (component) {
        const specificGravity = component.gravity ? Number(component.gravity) : 1.0;
        const componentVolume = Number(volume) || 0;
        
        const componentWeight = 0.158987 * componentVolume * specificGravity;
        totalWeight += componentWeight;
      }
    });

    return totalWeight;
  };

  const validateForm = () => {
    if (!volume || volume <= 0) {
      addNotification("error", "Please enter a valid Total Blend Quantity");
      return false;
    }

    const selectedCount = components.length;
    if (selectedCount === 0) {
      addNotification("error", "Please select at least one component for optimization");
      return false;
    }

    const filteredComponents = components;
    
    const componentsWithoutNames = filteredComponents.filter(comp => !comp.name || comp.name.trim() === '');
    if (componentsWithoutNames.length > 0) {
      addNotification("error", "Please add names for all components");
      return false;
    }

    const componentsWithInvalidCost = filteredComponents.filter(comp => {
      // Cost must be a valid positive number (not NaN, not <= 0, not empty)
      const cost = Number(comp.cost);
      return isNaN(cost) || cost <= 0 || comp.cost === '' || comp.cost === null || comp.cost === undefined; 
    });

    if (componentsWithInvalidCost.length > 0) {
      const componentNames = componentsWithInvalidCost.map(comp => comp.name || 'Unnamed Component').join(', ');
      addNotification("error", `Please add valid cost (greater than 0) for components: ${componentNames}`);
      return false;
    }

    return true;
  };


  const runManualOptimization = async () => {
  console.log("DEBUG: Starting optimization with:", {
    volume,
    componentsCount: components.length,
    propertiesCount: propertySpecs.length,
    selectedProperties: selectedPropertyColumns
  });

  if (!validateForm()) {
    return;
  }

  setLoadingOptimization(true);
  resetResults();

  try {
    const filteredComponents = components;

    if (filteredComponents.length === 0) {
      throw new Error("Please select at least one component for optimization");
    }

    const componentsForOptimization = filteredComponents.map(comp => {
      // Validate cost - must be a positive number before sending to backend
      const costValue = Number(comp.cost);
      if (isNaN(costValue) || costValue <= 0 || comp.cost === '' || comp.cost === null || comp.cost === undefined) {
        throw new Error(`Component "${comp.name || 'Unnamed'}" has invalid or missing cost. Please enter a valid cost greater than 0 before running optimization.`);
      }

      const componentData = {
        name: comp.name || '',
        cost: costValue, // Use validated cost
        min: Number(comp.min) || 0,
        max: Number(comp.max) || 1000,
        availability: Number(comp.availability) || 1000,
        density: (comp.density !== undefined && comp.density !== null && comp.density !== '')
                   ? Number(comp.density) : 0,
        // Explicitly include SG — 'gravity' is in fixedFields so the loop below skips it
        specific_gravity: Number(comp.gravity || comp['specific_gravity'] || comp['specific gravity'] || comp.sg || 0) || null,
      };

      Object.keys(comp).forEach(key => {
        const keyLower = key.toLowerCase();
        const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type', 'costunit', 'volumeunit', '_id', 'id', 'originalname', 'selectedproperties', 'selectedgroups', 'selectedproperty'];
        if (!fixedFields.includes(keyLower) && comp[key] !== undefined && comp[key] !== null && comp[key] !== '') {
          const numValue = Number(comp[key]);
          if (!isNaN(numValue)) {
            // Normalize SG field name so backend always receives `specific_gravity`
            // NOTE: "spg" is a SEPARATE property — do NOT alias it to SG
            let outKey = keyLower;
            if (outKey === "specific gravity" || outKey === "sg" || outKey === "gravity") outKey = "specific_gravity";
            componentData[outKey] = numValue;
          }
        }
      });

      return componentData;
    });

    const normalizePropRow = (r) => {
      let minValue = null;
      if (r.min !== undefined && r.min !== null && r.min !== '') {
        const minNum = Number(r.min);
        if (!isNaN(minNum)) {
          minValue = minNum;
        }
      }
      
      let maxValue = null;
      if (r.max !== undefined && r.max !== null && r.max !== '') {
        const maxNum = Number(r.max);
        if (!isNaN(maxNum)) {
          maxValue = maxNum;
        }
      }
      
      const propName = String(r.property || "").trim().toLowerCase();
      
      return {
        property: propName,
        min: minValue,
        max: maxValue,
        linear: normLinear(r.linear),
        basis: String(r.basis || "volume").toLowerCase(),
        index_formula: String(r.index_formula || "").trim(),
        reverse_formula: String(r.reverse_formula || "").trim(),
        // ── Soft-constraint fields ────────────────────────────────────
        relax:   Boolean(r.relax || false),
        penalty: r.penalty !== "" && r.penalty !== undefined && r.penalty !== null
                   ? Number(r.penalty) : null,
      };
    };
    
    const normLinear = (v) => {
      if (typeof v === "boolean") return v;
      if (v === undefined || v === null) return true; 
      const s = String(v).toLowerCase().trim();
      return !["false","0","no","none"].includes(s);
    };

    const propertySpecsForOptimization = propertySpecs
      .filter(prop => prop.property && prop.property.trim() !== '')
      .map(normalizePropRow)
      // Filter out SG from user-defined rows (it's handled separately below)
      .filter(p => !["specific gravity", "specific_gravity", "gravity"].includes(String(p.property).toLowerCase().trim()));

    // ── Inject Specific Gravity as a constraint if user set min or max ──
    const sgMinVal = sgMin !== "" && sgMin !== null && sgMin !== undefined ? Number(sgMin) : null;
    const sgMaxVal = sgMax !== "" && sgMax !== null && sgMax !== undefined ? Number(sgMax) : null;
    if (sgMinVal !== null || sgMaxVal !== null) {
      propertySpecsForOptimization.push({
        property: "specific gravity",
        min: sgMinVal,
        max: sgMaxVal,
        linear: true,
        basis: "volume",
        index_formula: "",
        reverse_formula: "",
        relax: Boolean(sgRelax),
        penalty: sgPenalty !== "" && sgPenalty !== null && sgPenalty !== undefined
                   ? Number(sgPenalty) : null,
      });
    }

    console.log('Components for optimization:', componentsForOptimization);
    console.log('Property specs for optimization:', propertySpecsForOptimization);

    const payload = {
      components: componentsForOptimization,
      property_specs: propertySpecsForOptimization,
specs: {
  // Backward compatibility: keep "volume" for older backend
  volume: Number(volume) || 0,

  // NEW: model-basis target
  blend_basis: String(modelBasis || "volume").toLowerCase(),
  quantity: Number(volume) || 0, // reuse same UI input, but now interpreted by blend_basis

  // Unit selections – MUST be sent so backend converts bounds/costs correctly
  volume_unit: String(volumeUnit || "bbl").toLowerCase(),
  mass_unit: String(massUnit || "mt").toLowerCase(),

  time_limit: 300,
  gap: Number(gap) || 0.0,
  threads: Number(threads) || 1,

  // ── Soft-constraint flags ─────────────────────────────────────
  auto_infeasibility_break: autoRelax,
  diagnostic_relax: true,          // always run diagnostic pass for driver info
}

    };

    console.log('Optimization payload:', payload);
    console.log('Sending request to:', `${apiBasepy}/optimize/json`);

    const res = await fetch(`${apiBasepy}/optimize/json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error('Server error response:', errorText);
      console.error('Response status:', res.status);
      console.error('Response URL:', res.url);
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { detail: `Optimization failed (HTTP ${res.status})` };
      }
      throw new Error(errorData?.detail || `Optimization failed (HTTP ${res.status})`);
    }

    const data = await res.json();
    console.log('Optimization response received:', data);
    console.log('Response properties:', data.properties);
    console.log('Response status:', data.status);

      const status = data.status || "";
      const statusUpper = status.toUpperCase();
      
      if (statusUpper === "INFEASIBLE" || statusUpper === "UNBOUNDED" || statusUpper === "NOT SOLVED" || statusUpper === "NOT_SOLVED") {
        console.log(`Optimization failed with status: ${status} - stopping execution`);
        
        resetResults();

        // ── Even on infeasible, store violation/driver info from diagnostic pass ──
        if (data.violations && Object.keys(data.violations).length > 0) {
          setViolations(data.violations);
          setInfeasibilityDrivers(data.infeasibility_drivers || []);
        }
        
        let errorMessage = data.message;
        if (!errorMessage) {
          if (statusUpper === "INFEASIBLE") {
            errorMessage = "Optimization failed: The given constraints cannot be satisfied. Please check your component properties, constraints, and volume requirements.";
          } else if (statusUpper === "UNBOUNDED") {
            errorMessage = "Optimization failed: The problem is unbounded. The solution can be improved indefinitely. Please check your constraints and objective function.";
          } else if (statusUpper === "NOT SOLVED" || statusUpper === "NOT_SOLVED") {
            errorMessage = "Optimization failed: The problem could not be solved. Please check your inputs and try again.";
          } else {
            errorMessage = `Optimization failed with status: ${status}. Please check your inputs and constraints.`;
          }
        }
        
        setError(errorMessage);
        addNotification("error", errorMessage);
        return;
      } else {
        const filteredBlend = Object.fromEntries(
          Object.entries(data.blend || {}).filter(([_, volume]) => volume > 0)
        );

        setStatus(data.status || "");
        setMessage(data.message || "");
        setBlend(filteredBlend);
        setTotalCost(data.total_cost || 0);

        // Backend always returns both totals in user-selected units
        const totalMass = Number(data.total_mass) || 0;
        const totalVol = Number(data.total_volume) || 0;

        // weight = total mass in massUnit; totalVolume = total volume in volumeUnit
        setWeight(totalMass);
        setTotalVolume(totalVol);

        setReportB64(data.report_xlsx_b64 || "");

        // Per-component volume and mass from backend (correctly unit-converted)
        const blendMass = data.blend_mass || {};
        const blendVol  = data.blend_volume || {};

        const breakdown = [];
        for (const [name, blendQty] of Object.entries(filteredBlend)) {
          const component = filteredComponents.find(c => c.name === name);
          if (component && component.cost && blendQty > 0) {
            const unitCostPerBbl = convertCostToPerBbl(component.cost, component.density);
            const totalCostValue = Number(blendQty) * unitCostPerBbl;
            // Use backend per-component volume/mass if available
            const compVol  = blendVol[name]  !== undefined ? Number(blendVol[name])
                             : (modelBasis === 'volume' ? Number(blendQty) : null);
            const compMass = blendMass[name] !== undefined ? Number(blendMass[name])
                             : (modelBasis === 'mass'   ? Number(blendQty) : null);
            breakdown.push({
              name,
              volume:   compVol,
              mass:     compMass,
              blendQty: Number(blendQty) || 0,
              unitCost: Number(component.cost) || 0,
              totalCost: totalCostValue
            });
          }
        }
        setCostBreakdown(breakdown);
        setChartData(prepareChartData(filteredBlend));

        const backendProperties = data.properties || data.property_results || {};
        console.log('Backend properties received:', backendProperties);
        console.log('Property specs:', propertySpecs);
        
        const newPropertyResults = {};
        const newPropertyResultsForDisplay = {};
        
        if (backendProperties && typeof backendProperties === 'object' && Object.keys(backendProperties).length > 0) {
          propertySpecs.forEach(prop => {
            if (prop.property && prop.property.trim() !== '') {
              const propName = prop.property.trim().toLowerCase();
              
              let backendValue = null;
              const matchingKey = Object.keys(backendProperties).find(key => 
                key.toLowerCase() === propName
              );
              
              if (matchingKey !== undefined && backendProperties[matchingKey] !== null && backendProperties[matchingKey] !== undefined) {
                backendValue = backendProperties[matchingKey];
                const numValue = Number(backendValue);
                if (!isNaN(numValue)) {
                  newPropertyResults[propName] = numValue;
                  newPropertyResultsForDisplay[propName] = numValue;
                  console.log(`Property ${propName} (matched from ${matchingKey}): ${numValue}`);
                }
              } else {
                console.log(`Property ${propName} not found in backend response`);
              }
            }
          });
          
          Object.entries(backendProperties).forEach(([propKey, value]) => {
            if (value !== null && value !== undefined) {
              const propKeyLower = propKey.toLowerCase();
              const alreadyHandled = propertySpecs.some(p => 
                p.property && p.property.trim().toLowerCase() === propKeyLower
              );
              
              if (!alreadyHandled) {
                const numValue = Number(value);
                if (!isNaN(numValue)) {
                  newPropertyResults[propKeyLower] = numValue;
                  newPropertyResultsForDisplay[propKeyLower] = numValue;
                  console.log(`Additional property ${propKeyLower} (from backend): ${numValue}`);
                }
              }
            }
          });
        } else {
          console.warn('No properties found in backend response:', data);
        }

        console.log('Final property results:', newPropertyResults);
        setPropertyResults(newPropertyResults);
        setPropertyResultsForDisplay(newPropertyResultsForDisplay);

        addNotification("success", `Optimization completed: ${data.status || 'SUCCESS'}`);

        // ── Soft-constraint result fields ─────────────────────────────────
        setViolations(data.violations || {});
        setPenaltyCost(data.penalty_cost || 0);
        setBlendCost(data.blend_cost || data.total_cost || 0);
        setInfeasibilityDrivers(data.infeasibility_drivers || []);
      }

    } catch (err) {
      console.error('Optimization error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      let errorMessage = err.message || 'Optimization failed';
      
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || err.message.includes('fetch')) {
        errorMessage = `Cannot connect to Python backend at ${apiBasepy}. Please ensure the Python backend is running on port 8000.`;
      }
      
      setError(errorMessage);
      addNotification("error", errorMessage);
    } finally {
      setLoadingOptimization(false);
    }
  };

  const resetResults = () => {
    setError(""); setMessage(""); setStatus(""); setBlend({}); setTotalCost(0);
    setReportB64(""); setCostBreakdown([]); setChartData([]); setWeight(0); setTotalVolume(0);
    setPropertyResults({}); setPropertyResultsForDisplay({});
    setViolations({}); setPenaltyCost(0); setBlendCost(0); setInfeasibilityDrivers([]);
  };

  // Compute volume-weighted blended Specific Gravity from current blend result
  const getBlendedSG = () => {
    if (!blend || Object.keys(blend).length === 0) return null;
    const filteredComponents = components;
    let totalSGxVol = 0;
    let totalVol = 0;
    Object.entries(blend).forEach(([name, qty]) => {
      const comp = filteredComponents.find(c => c.name === name);
      if (!comp) return;
      // get volume: if model is mass-basis, convert mass->vol using SG
      const sg = Number(comp.gravity || comp['specific_gravity'] || comp['specific gravity'] || comp.sg || 0);
      if (sg <= 0) return;
      let vol;
      if (modelBasis === 'mass') {
        vol = Number(qty) / sg;  // approx vol (relative units, SG ratio)
      } else {
        vol = Number(qty);
      }
      totalSGxVol += sg * vol;
      totalVol += vol;
    });
    return totalVol > 0 ? totalSGxVol / totalVol : null;
  };

  const prepareChartData = (blendData) => {
    return Object.entries(blendData)
      .filter(([_, volume]) => volume > 0)
      .map(([name, volume]) => ({ name, volume, value: volume }));
  };

  // Utility Functions
  const getCurrencySymbol = (currencyCode = null) => {
    const currencies = [
      { code: 'INR', symbol: '₹' },
      { code: 'USD', symbol: '$' },
      { code: 'EUR', symbol: '€' },
      { code: 'GBP', symbol: '£' },
      { code: 'JPY', symbol: '¥' },
      { code: 'CAD', symbol: 'C$' },
      { code: 'AUD', symbol: 'A$' },
      { code: 'AED', symbol: 'د.إ' },
      { code: 'SAR', symbol: '﷼' },
      { code: 'QAR', symbol: '﷼' },
      { code: 'KWD', symbol: 'د.ك' },
      { code: 'BHD', symbol: '.د.ب' },
      { code: 'OMR', symbol: '﷼' }
    ];
    return currencies.find(c => c.code === (currencyCode || currency))?.symbol || '$';
  };

  const getCurrentUnit = () => {
    if (modelBasis === 'volume') {
      return volumeUnit;
    } else {
      return massUnit;
    }
  };

  const getCurrentUnitWithBrackets = () => {
    return `(${getCurrentUnit()})`;
  };

  const getCostUnit = () => {
    if (modelBasis === 'volume') {
      return `${getCurrencySymbol()}/${volumeUnit}`;
    } else {
      return `${getCurrencySymbol()}/${massUnit}`;
    }
  };

  const convertCostToPerBbl = (cost, density) => {
    const numCost = Number(cost);
    const numDensity = Number(density);

    if (!Number.isFinite(numCost) || numCost < 0) return 0;
    if (!Number.isFinite(numDensity) || numDensity <= 0) return numCost;

    if (modelBasis === 'mass') {
      return numCost * numDensity * 158.987;
    }
    return numCost;
  };

  const clearPersistedModelData = () => {
    // Clear all persisted model data after successful save
    const keysToRemove = [
      'blendOptimizerLoadedEditData',
      'blendOptimizerLoadedComponents',
      'blendOptimizerLoadedProperties'
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  };

  const addNotification = (type, message) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, type, message }]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // ── Model Name persistence ──────────────────────────────────────────
  useEffect(() => {
    if (modelName) localStorage.setItem('blendOptimizerModelName', modelName);
  }, [modelName]);

  // ── Validation Engine ──────────────────────────────────────────────
  const runValidation = () => {
    const errors = [];
    // Check Min > Max in components
    components.forEach((comp, i) => {
      if (comp.min !== '' && comp.max !== '' && Number(comp.min) > Number(comp.max)) {
        errors.push({ type: 'component', index: i, field: 'bounds', message: `${comp.name}: Min (${comp.min}) > Max (${comp.max})` });
      }
    });
    // Check Min > Max in property specs
    propertySpecs.forEach((prop, i) => {
      if (prop.min !== '' && prop.max !== '' && Number(prop.min) > Number(prop.max)) {
        errors.push({ type: 'property', index: i, field: 'bounds', message: `${prop.property}: Min (${prop.min}) > Max (${prop.max})` });
      }
      // Check penalty given but relax off
      if (prop.penalty && !prop.relax) {
        errors.push({ type: 'property', index: i, field: 'penalty', message: `${prop.property}: Penalty set but Relax is off`, severity: 'warning' });
      }
    });
    // Check SG constraint
    if (sgMin !== '' && sgMax !== '' && Number(sgMin) > Number(sgMax)) {
      errors.push({ type: 'property', field: 'sg-bounds', message: `Specific Gravity: Min (${sgMin}) > Max (${sgMax})` });
    }
    // Check missing SG for basis conversions when needed
    if (modelBasis === 'mass' || propertySpecs.some(p => (p.basis || 'volume') !== modelBasis)) {
      const missingGravity = components.filter((c) => (!c.gravity || Number(c.gravity) === 0));
      if (missingGravity.length > 0) {
        errors.push({ type: 'component', field: 'gravity', message: `Missing SG for basis conversion: ${missingGravity.map(c => c.name).join(', ')}`, severity: 'error' });
      }
    }
    setValidationErrors(errors);
    return errors;
  };

  useEffect(() => {
    if (components.length > 0 || propertySpecs.length > 0) {
      runValidation();
    }
  }, [components, selectedComponents, propertySpecs, sgMin, sgMax, modelBasis]);

  const hasBlockingErrors = validationErrors.filter(e => !e.severity || e.severity === 'error').filter(e => e.field === 'bounds' || e.field === 'sg-bounds' || e.field === 'gravity').length > 0;

  // ── New Model Handler ──────────────────────────────────────────────
  const handleNewModel = () => {
    if (components.length > 0 || propertySpecs.length > 0) {
      if (!window.confirm('Start a new model? All unsaved changes will be lost.')) return;
    }
    setComponents([]);
    setSelectedComponents([]);
    setPropertySpecs([]);
    setSelectedPropertyColumns([]);
    setCustomColumns([]);
    setBlend({});
    setTotalCost(0);
    setWeight(0);
    setTotalVolume(0);
    setStatus('');
    setMessage('');
    setReportB64('');
    setCostBreakdown([]);
    setChartData([]);
    setPropertyResults({});
    setPropertyResultsForDisplay({});
    setViolations({});
    setPenaltyCost(0);
    setBlendCost(0);
    setInfeasibilityDrivers([]);
    setAutoRelax(false);
    setSgMin('');
    setSgMax('');
    setSgRelax(false);
    setSgPenalty('');
    setEditingResult(null);
    setEditingComponent(null);
    setEditingProperty(null);
    setModelName('');
    setLastSavedTimestamp('');
    setCurrentModelId(null); // Reset model ID for new model
    setVolume(5000);
    setRightPanelTab('components');
    localStorage.removeItem('blendOptimizerModelName');
    localStorage.removeItem('blendOptimizerLastSaved');
    localStorage.removeItem('blendOptimizerCurrentModelId'); // Clear model ID
    localStorage.removeItem('blendOptimizerLoadedEditData');
    addNotification('success', 'New model created. Add components and properties to begin.');
  };

  // ── Open Model Handler (navigate to saved results) ──────────────────
  const handleOpenModel = () => {
    handleTabChange('optimization-results');
  };

  // ── Bulk Actions for Components ─────────────────────────────────────
  const applyBulkMin = () => {
    if (bulkMinValue === '') return;
    setComponents(prev => prev.map((comp, i) => selectedComponents[i] ? { ...comp, min: Number(bulkMinValue) } : comp));
    addNotification('success', `Min bound set to ${bulkMinValue} for ${selectedComponents.filter(Boolean).length} selected components`);
    setBulkMinValue('');
  };
  const applyBulkMax = () => {
    if (bulkMaxValue === '') return;
    setComponents(prev => prev.map((comp, i) => selectedComponents[i] ? { ...comp, max: Number(bulkMaxValue) } : comp));
    addNotification('success', `Max bound set to ${bulkMaxValue} for ${selectedComponents.filter(Boolean).length} selected components`);
    setBulkMaxValue('');
  };
  const applyBulkCost = () => {
    if (bulkCostValue === '') return;
    setComponents(prev => prev.map((comp, i) => selectedComponents[i] ? { ...comp, cost: Number(bulkCostValue) } : comp));
    addNotification('success', `Cost set to ${bulkCostValue} for ${selectedComponents.filter(Boolean).length} selected components`);
    setBulkCostValue('');
  };

  // ── CSV Download Pack ───────────────────────────────────────────────
  const downloadCSVPack = () => {
    try {
      const escCSV = (v) => {
        const s = String(v ?? '');
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      let csv = '\uFEFF'; // BOM for Excel
      const ts = new Date().toISOString();
      // Header section
      csv += `Blend Optimization Report\r\n`;
      csv += `Model Name,${escCSV(modelName || 'Untitled')}\r\n`;
      csv += `Generated,${ts}\r\n`;
      csv += `Status,${escCSV(status)}\r\n`;
      csv += `Total Volume,${totalVolume || Object.values(blend).reduce((a, b) => a + Number(b || 0), 0)},${volumeUnit}\r\n`;
      csv += `Total Cost,${totalCost},${currency}\r\n`;
      csv += `Total Mass,${weight},${massUnit}\r\n`;
      csv += `Model Basis,${modelBasis}\r\n\r\n`;

      // Blend Slate
      csv += `--- BLEND SLATE ---\r\n`;
      csv += `Component,Volume (${volumeUnit}),Vol %,Cost (${currency})\r\n`;
      const totalBlendVol = Object.values(blend).reduce((a, b) => a + Number(b || 0), 0);
      Object.entries(blend).forEach(([name, vol]) => {
        const pct = totalBlendVol > 0 ? ((Number(vol) / totalBlendVol) * 100).toFixed(2) : '0';
        const cb = costBreakdown.find(c => c.name === name);
        csv += `${escCSV(name)},${Number(vol).toFixed(4)},${pct}%,${cb ? cb.totalCost?.toFixed(2) : ''}\r\n`;
      });
      csv += `\r\n`;

      // Property Results
      if (Object.keys(propertyResultsForDisplay).length > 0) {
        csv += `--- PROPERTY RESULTS ---\r\n`;
        csv += `Property,Result,Min,Max,Status\r\n`;
        Object.entries(propertyResultsForDisplay).forEach(([name, val]) => {
          const spec = propertySpecs.find(p => (p.property || '').toLowerCase() === name.toLowerCase() || p.property === name);
          const min = spec ? spec.min : '';
          const max = spec ? spec.max : '';
          const inRange = spec && val >= Number(spec.min) && val <= Number(spec.max) ? 'PASS' : spec ? 'FAIL' : '';
          csv += `${escCSV(name)},${Number(val).toFixed(4)},${min},${max},${inRange}\r\n`;
        });
        csv += `\r\n`;
      }

      // Solver metadata
      csv += `--- SOLVER METADATA ---\r\n`;
      csv += `Auto Infeasibility Breaker,${autoRelax ? 'ON' : 'OFF'}\r\n`;
      csv += `Gap,${gap}\r\n`;
      csv += `Threads,${threads}\r\n`;

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blend_report_${(modelName || 'optimization').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      addNotification('success', 'CSV report downloaded');
    } catch (err) {
      console.error('CSV download error:', err);
      addNotification('error', 'Failed to generate CSV report');
    }
  };

  // ── Keyboard Handler for Table Cells ─────────────────────────────────
  const handleCellKeyDown = (e, rowIndex, fieldName, tableType) => {
    if (e.key === 'Enter') {
      e.target.blur();
      // Move to next row
      const nextRow = document.querySelector(`[data-row="${tableType}-${rowIndex + 1}"][data-field="${fieldName}"]`);
      if (nextRow) nextRow.focus();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextRow = document.querySelector(`[data-row="${tableType}-${rowIndex + 1}"][data-field="${fieldName}"]`);
      if (nextRow) nextRow.focus();
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevRow = document.querySelector(`[data-row="${tableType}-${rowIndex - 1}"][data-field="${fieldName}"]`);
      if (prevRow) prevRow.focus();
    }
  };

 

  const handleComponentGroupChange = async (e) => {
  const groupId = e.target.value;
  setSelectedComponentGroup(groupId);
  setLoadingComponentGroup(true);

  if (groupId) {
    try {
      const selectedGroup = componentGroups.find(group => group._id === groupId);

      if (selectedGroup && selectedGroup.components) {
        const allGroupProperties = new Map();

        const mappedComponents = selectedGroup.components.map(comp => {
          const componentData = {
            name: comp.name || '',
            cost: comp.cost || '',
            min: comp.min || '',
            max: comp.max || '',
            availability: comp.volume || 1000,
            density: comp.density || '', 
            gravity: comp.gravity || '',
          };

          if (comp.selectedProperties && Array.isArray(comp.selectedProperties)) {
            comp.selectedProperties.forEach(prop => {
              if (prop.name && prop.name.toLowerCase() !== 'density') {
                componentData[prop.name] = prop.defaultValue || prop.editableDefaultValue || 0;

                if (!allGroupProperties.has(prop.name)) {
                  allGroupProperties.set(prop.name, {
                    property: prop.name,
                    uom: prop.uom || '',
                    min: prop.minValue || '',
                    max: prop.maxValue || '',
                    defaultValue: prop.defaultValue || prop.editableDefaultValue || '',
                    description: prop.description || '',
                    basis: prop.basis || 'volume',
                    linear: prop.linear !== undefined ? prop.linear : true
                  });
                }
              }
            });
          }

          if (comp.selectedGroups && Array.isArray(comp.selectedGroups)) {
            comp.selectedGroups.forEach(group => {
              if (group.name && group.name.toLowerCase() !== 'density') {
                componentData[group.name] = group.defaultValue || group.editableDefaultValue || 0;

                if (!allGroupProperties.has(group.name)) {
                  allGroupProperties.set(group.name, {
                    property: group.name,
                    uom: group.uom || '',
                    min: group.minValue || '',
                    max: group.maxValue || '',
                    defaultValue: group.defaultValue || group.editableDefaultValue || '',
                    description: group.description || '',
                    basis: group.basis || 'volume',
                    linear: prop.linear !== undefined ? prop.linear : true
                  });
                }
              }
            });
          }

          return componentData;
        });

        setComponents(mappedComponents);
        setSelectedComponents(new Array(mappedComponents.length).fill(true));

        const newPropertyColumns = Array.from(allGroupProperties.keys());
        setSelectedPropertyColumns(newPropertyColumns);

        const propertyConstraints = Array.from(allGroupProperties.values()).map(propData => ({
          property: propData.property,
          uom: propData.uom,
          min: propData.min,
          max: propData.max,
          defaultValue: propData.defaultValue,
          basis: propData.basis,
          linear: propData.linear,
          description: propData.description
        }));

        setPropertySpecs(propertyConstraints);
        setComponentGroupProperties(propertyConstraints);

        addNotification("success", `Loaded ${mappedComponents.length} components and ${allGroupProperties.size} properties from "${selectedGroup.name}" group`);

        setTimeout(() => {
          calculateAllPropertyResults();
        }, 100);
      }
    } catch (error) {
      console.error("Error loading component group:", error);
      addNotification("error", "Failed to load component group");
    } finally {
      setLoadingComponentGroup(false);
    }
  } else {
    setComponents([]);
    setSelectedComponents([]);
    setPropertyResults({});
    setComponentGroupProperties([]);
    setLoadingComponentGroup(false);
  }
};

  const handleGroupChange = async (e) => {
    const groupId = e.target.value;
    setSelectedGroup(groupId);
    setLoadingPropertyGroup(true);

    if (groupId) {
      try {
        const selectedGroupData = propertyGroups.find(group => group._id === groupId);

        if (selectedGroupData && selectedGroupData.properties) {
          const mappedProperties = selectedGroupData.properties.map(prop => ({
            property: prop.property || "",
            uom: prop.uom || "",
            min: prop.min || prop.defaultValue || "",
            max: prop.max || prop.defaultValue || "",
            basis: prop.basis || "volume",
            linear: prop.linear !== undefined ? prop.linear : true,
            description: prop.description || "",
            index_formula: prop.index_formula || "",
            reverse_formula: prop.reverse_formula || "",
            defaultValue: prop.defaultValue || ""
          }));

          setPropertySpecs(mappedProperties);

          const propertyNames = mappedProperties
            .filter(prop => prop.property && prop.property.trim() !== '')
            .map(prop => prop.property.trim());

          setSelectedPropertyColumns(propertyNames);

          addNotification("success", `Loaded ${mappedProperties.length} properties from "${selectedGroupData.name}"`);

          setTimeout(() => {
            calculateAllPropertyResults();
          }, 100);
        }
      } catch (error) {
        console.error("Error loading property group:", error);
        addNotification("error", "Failed to load property group");
      } finally {
        setLoadingPropertyGroup(false);
      }
    } else {
      setPropertySpecs([]);
      setPropertyResults({});
      setSelectedPropertyColumns([]);
      setLoadingPropertyGroup(false);
    }
  };

  const handlePropertySetChange = async (e) => {
    const setId = e.target.value;
    setSelectedPropertySet(setId);
    setLoadingPropertySet(true);

    if (setId) {
      try {
        const selectedSet = savedPropertySets.find(set => set._id === setId || set.id === setId);

        if (selectedSet && selectedSet.properties) {
          const mappedProperties = selectedSet.properties.map(prop => ({
            property: prop.property || "",
            uom: prop.uom || "",
            min: prop.min || prop.defaultValue || "",
            max: prop.max || prop.defaultValue || "",
            basis: prop.basis || "volume",
            linear: prop.linear !== undefined ? prop.linear : true,
            description: prop.description || "",
            index_formula: prop.index_formula || "",
            reverse_formula: prop.reverse_formula || "",
            defaultValue: prop.defaultValue || ""
          }));

          setPropertySpecs(mappedProperties);

          const propertyNames = mappedProperties
            .filter(prop => prop.property && prop.property.trim() !== '')
            .map(prop => prop.property.trim());

          setSelectedPropertyColumns(propertyNames);

          addNotification("success", `Loaded ${mappedProperties.length} properties from "${selectedSet.name}" set`);

          setTimeout(() => {
            calculateAllPropertyResults();
          }, 100);
        }
      } catch (error) {
        console.error("Error loading property set:", error);
        addNotification("error", "Failed to load property set");
      } finally {
        setLoadingPropertySet(false);
      }
    } else {
      setPropertySpecs([]);
      setPropertyResults({});
      setSelectedPropertyColumns([]);
      setLoadingPropertySet(false);
    }
  };

  // Modal Handlers
  const handleAddPropertyClick = () => {
    setShowAddPropertyPopup(true);
    setEditingIndex(null);
    setNewProperty({
      property: '',
      uom: '',
      min: '',
      max: ''
    });
  };

  const handlePropertyChange = (field, value) => {
    setNewProperty(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProperty = () => {
    if (!newProperty.property.trim()) {
      addNotification("error", "Property name is required");
      return;
    }

    if (!newProperty.uom.trim()) {
      addNotification("error", "UOM name is required");
      return;
    }

    const propertyToSave = {
      ...newProperty,
      property: newProperty.property.trim(),
      uom: newProperty.uom.trim(),
      min: newProperty.min,
      max: newProperty.max
    };

    if (editingIndex !== null) {
      const oldPropertyName = propertySpecs[editingIndex]?.property;

      setPropertySpecs(prev => prev.map((prop, i) =>
        i === editingIndex ? propertyToSave : prop
      ));

      if (oldPropertyName !== propertyToSave.property) {
        const newPropertyColumns = [...selectedPropertyColumns];

        if (oldPropertyName && newPropertyColumns.includes(oldPropertyName)) {
          const indexToRemove = newPropertyColumns.indexOf(oldPropertyName);
          newPropertyColumns.splice(indexToRemove, 1);
        }

        if (!newPropertyColumns.includes(propertyToSave.property)) {
          newPropertyColumns.push(propertyToSave.property);
        }

        setSelectedPropertyColumns(newPropertyColumns);
      }

      addNotification("success", "Property updated successfully");
    } else {
      setPropertySpecs(prev => [...prev, propertyToSave]);

      if (!selectedPropertyColumns.includes(propertyToSave.property)) {
        const newPropertyColumns = [...selectedPropertyColumns, propertyToSave.property];
        setSelectedPropertyColumns(newPropertyColumns);
      }

      addNotification("success", "Property added successfully");
    }

    setShowAddPropertyPopup(false);
    setEditingIndex(null);

    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  const handleClosePopup = () => {
    setShowAddPropertyPopup(false);
    setEditingIndex(null);
  };

  // Global Properties Modal
  const openGlobalPropertiesModal = async () => {
    setLoadingGlobalProperties(true);
    try {
      const response = await fetch(`${apiBase}/allPropertiesReport`);
      const result = await response.json();

      if (result.success && result.data) {
        const allProperties = result.data.map(property => ({
          property: property.property || "",
          uom: property.uom || "",
          min: property.min || property.defaultValue || "",
          max: property.max || property.defaultValue || "",
          basis: property.basis || "volume",
          linear: property.linear !== undefined ? property.linear : true,
          description: property.description || "",
          index_formula: property.index_formula || "",
          reverse_formula: property.reverse_formula || "",
          defaultValue: property.defaultValue || "",
          sources: property.sources || []
        }));

        setGlobalProperties(allProperties);
        setSelectedGlobalProperties(new Array(allProperties.length).fill(false));
        setShowGlobalPropertiesModal(true);

        addNotification("success", `Loaded ${allProperties.length} properties from global report`);
      } else {
        throw new Error(result.message || "Failed to fetch global properties");
      }
    } catch (error) {
      console.error("Error fetching global properties:", error);
      addNotification("error", "Failed to load global properties");
    } finally {
      setLoadingGlobalProperties(false);
    }
  };

  const closeGlobalPropertiesModal = () => {
    setShowGlobalPropertiesModal(false);
    setGlobalProperties([]);
    setSelectedGlobalProperties([]);
  };

  const toggleGlobalPropertySelection = (index) => {
    const newSelected = [...selectedGlobalProperties];
    newSelected[index] = !newSelected[index];
    setSelectedGlobalProperties(newSelected);
  };

  const selectAllGlobalProperties = () => setSelectedGlobalProperties(new Array(globalProperties.length).fill(true));
  const deselectAllGlobalProperties = () => setSelectedGlobalProperties(new Array(globalProperties.length).fill(false));

  const handleSaveSelectedGlobalProperties = () => {
    const selectedPropertiesData = globalProperties.filter((_, index) => selectedGlobalProperties[index]);

    if (selectedPropertiesData.length === 0) {
      addNotification("error", "Please select at least one property");
      return;
    }

    const componentFixedFields = [
      'name', 'cost', 'min', 'max', 'availability', 'density',
      'gravity', 'specificgravity', 'specific gravity', 'basis', 'blending_type'
    ];

    const filteredProperties = selectedPropertiesData.filter(newProp => {
      const propNameLower = newProp.property.toLowerCase().trim();

      const isFixedField = componentFixedFields.includes(propNameLower);

      if (isFixedField) {
        return false; 
      }

      const existsInSpecs = propertySpecs.some(prop =>
        prop.property && prop.property.toLowerCase() === propNameLower
      );

      return !existsInSpecs;
    });

    // Handle fixed fields by populating component columns
    const fixedFieldProperties = selectedPropertiesData.filter(newProp =>
      componentFixedFields.includes(newProp.property.toLowerCase().trim())
    );

    // Populate fixed field values in components
    if (fixedFieldProperties.length > 0 && components.length > 0) {
      const updatedComponents = components.map(comp => {
        const updatedComp = { ...comp };

      fixedFieldProperties.forEach(fixedProp => {
        const propName = fixedProp.property.toLowerCase().trim();

        if (propName === 'density' && !updatedComp.density) {
          // Set to 0 when property is selected
          updatedComp.density = '0';
        }
        if ((propName === 'gravity' || propName === 'specific gravity') && !updatedComp.gravity) {
          // Set to 0 when property is selected
          updatedComp.gravity = '0';
        }
        // Add other fixed fields as needed
      });

        return updatedComp;
      });

      setComponents(updatedComponents);
    }

    if (filteredProperties.length === 0) {
      if (fixedFieldProperties.length > 0) {
        addNotification("info", "Fixed fields like Density are already available in component columns - values populated");
      } else {
        addNotification("info", "All selected properties already exist in property constraints");
      }
      closeGlobalPropertiesModal();
      return;
    }

    const updatedPropertySpecs = [...propertySpecs];
    let addedCount = 0;

    filteredProperties.forEach(newProp => {
      const propName = newProp.property.trim();
      const propNameLower = propName.toLowerCase();

      const existingIndex = updatedPropertySpecs.findIndex(prop =>
        prop.property && prop.property.toLowerCase() === propNameLower
      );

      let minValue = newProp.min || newProp.defaultValue || '';
      let maxValue = newProp.max || newProp.defaultValue || '';

      if (propNameLower === 'density' && components.length > 0) {
        const densityValues = components
          .map(comp => {
            const densityVal = Number(comp.density) || 0;
            return densityVal > 0 ? densityVal : null;
          })
          .filter(val => val !== null && val > 0);

        if (densityValues.length > 0) {
          const minDensity = Math.min(...densityValues);
          const maxDensity = Math.max(...densityValues);

          minValue = (minDensity * 0.95).toFixed(3); 
          maxValue = (maxDensity * 1.05).toFixed(3); 
        }
      }

      const propertyData = {
        property: propName,
        uom: newProp.uom || '',
        min: minValue,
        max: maxValue,
        basis: newProp.basis || 'volume',
        linear: newProp.linear !== undefined ? newProp.linear : true,
        index_formula: newProp.index_formula || '',
        reverse_formula: newProp.reverse_formula || '',
        description: newProp.description || '',
        defaultValue: newProp.defaultValue || ''
      };

      if (existingIndex >= 0) {
        updatedPropertySpecs[existingIndex] = propertyData;
      } else {
        updatedPropertySpecs.push(propertyData);
      }
      addedCount++;
    });

    setPropertySpecs(updatedPropertySpecs);

    const propertyNamesToAdd = filteredProperties
      .filter(prop => !componentFixedFields.includes(prop.property.toLowerCase().trim()))
      .map(prop => prop.property.trim());

    if (propertyNamesToAdd.length > 0) {
      const newPropertyColumns = [...selectedPropertyColumns];
      let hasChanges = false;

      propertyNamesToAdd.forEach(propertyName => {
        if (!newPropertyColumns.includes(propertyName)) {
          newPropertyColumns.push(propertyName);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setSelectedPropertyColumns(newPropertyColumns);
      }
    }

    if (components.length > 0 && propertyNamesToAdd.length > 0) {
      const updatedComponents = components.map(comp => {
        const updatedComp = { ...comp };

        propertyNamesToAdd.forEach(propName => {
          const propNameLower = propName.toLowerCase();

          if (!componentFixedFields.includes(propNameLower)) {
            if (!(propName in updatedComp)) {
              const propData = filteredProperties.find(p =>
                p.property.toLowerCase() === propNameLower
              );
              updatedComp[propName] = propData?.defaultValue || 0;
            }
          }
        });

        return updatedComp;
      });

      setComponents(updatedComponents);
    }

    addNotification("success", `Added ${addedCount} property columns from global report`);
    closeGlobalPropertiesModal();

    setTimeout(() => {
      calculateAllPropertyResults();
    }, 100);
  };

  // Property Column Modal
  const openPropertyColumnModal = async () => {
    setLoadingPropertyColumn(true);
    try {
      const response = await fetch(`${apiBase}/allPropertiesReport`);
      const result = await response.json();

      if (result.success && result.data) {
        const allProperties = result.data.map(property => ({
          property: property.property || "",
          uom: property.uom || "",
          min: property.min || property.defaultValue || "",
          max: property.max || property.defaultValue || "",
          basis: property.basis || "volume",
          linear: property.linear !== undefined ? property.linear : true,
          description: property.description || "",
          index_formula: property.index_formula || "",
          reverse_formula: property.reverse_formula || "",
          defaultValue: property.defaultValue || "",
          sources: property.sources || []
        }));

        setGlobalProperties(allProperties);
        setSelectedGlobalProperties(new Array(allProperties.length).fill(false));
        setShowPropertyColumnModal(true);

        addNotification("success", `Loaded ${allProperties.length} properties from global report`);
      } else {
        throw new Error(result.message || "Failed to fetch global properties");
      }
    } catch (error) {
      console.error("Error fetching global properties:", error);
      addNotification("error", "Failed to load properties");
    } finally {
      setLoadingPropertyColumn(false);
    }
  };

  const closePropertyColumnModal = () => {
    setShowPropertyColumnModal(false);
    setGlobalProperties([]);
    setSelectedGlobalProperties([]);
  };

  const toggleGlobalPropertySelectionFixed = (index) => {
    const newSelected = [...selectedGlobalProperties];
    newSelected[index] = !newSelected[index];
    setSelectedGlobalProperties(newSelected);
  };




const handleSaveSelectedPropertyColumns = () => {
  const selectedPropertiesData = globalProperties.filter((_, index) => selectedGlobalProperties[index]);

  if (selectedPropertiesData.length === 0) {
    addNotification("error", "Please select at least one property");
    return;
  }

  // Existing properties in property specs
  const existingProperties = selectedPropertyColumns.map(p => p.toLowerCase());
  
  const componentFixedFields = [
    'name', 'cost', 'min', 'max', 'availability',
    'density', 'gravity', 'specificgravity', 'specific gravity', 'basis', 'blending_type',
    'selected', 'actions'
  ];

  const commonComponentFields = [
    'component', 'costunit', 'volumeunit', 'minvolume', 'maxvolume',
    'availablevolume', 'solution', 'volume'
  ];

  const filteredProperties = selectedPropertiesData.filter(newProp => {
    const propNameLower = newProp.property.toLowerCase().trim();
    
    if (existingProperties.includes(propNameLower)) {
      return false;
    }
    
    if (componentFixedFields.includes(propNameLower)) {
      return false;
    }
    
    if (commonComponentFields.includes(propNameLower)) {
      return false;
    }
    
    return true;
  });

  // Handle fixed fields by populating component columns
  const fixedFieldProperties = selectedPropertiesData.filter(newProp =>
    componentFixedFields.includes(newProp.property.toLowerCase().trim()) ||
    commonComponentFields.includes(newProp.property.toLowerCase().trim())
  );

  // Populate fixed field values in components
  if (fixedFieldProperties.length > 0 && components.length > 0) {
    const updatedComponents = components.map(comp => {
      const updatedComp = { ...comp };

      fixedFieldProperties.forEach(fixedProp => {
        const propName = fixedProp.property.toLowerCase().trim();

        if (propName === 'density' && !updatedComp.density) {
          // Set to 0 when property is selected
          updatedComp.density = '0';
        }
        if ((propName === 'gravity' || propName === 'specific gravity') && !updatedComp.gravity) {
          // Set to 0 when property is selected
          updatedComp.gravity = '0';
        }
        // Add other fixed fields as needed
      });

      return updatedComp;
    });

    setComponents(updatedComponents);
  }

  if (filteredProperties.length === 0) {
    if (fixedFieldProperties.length > 0) {
      addNotification("info", "Fixed fields like Density are already available in component columns - values populated");
    } else {
      addNotification("info", "All selected properties already exist");
    }
    closePropertyColumnModal();
    return;
  }

  const newPropertyColumns = filteredProperties.map(prop => prop.property.trim());
  const mergedPropertyColumns = [...new Set([...selectedPropertyColumns, ...newPropertyColumns])];
  setSelectedPropertyColumns(mergedPropertyColumns);

  const newPropertySpecs = [...propertySpecs];
  filteredProperties.forEach(newProp => {
    const propName = newProp.property.trim();
    const propNameLower = propName.toLowerCase();
    
    const propertyExists = newPropertySpecs.some(prop => 
      prop.property && prop.property.toLowerCase() === propNameLower
    );
    
    if (!propertyExists) {
      let minValue = newProp.min || newProp.defaultValue || '';
      let maxValue = newProp.max || newProp.defaultValue || '';
      
      if (propNameLower === 'density') {
        if (components.length > 0) {
          const densityValues = components
            .map(comp => {
              const densityVal = Number(comp.density) || 0;
              return densityVal > 0 ? densityVal : null;
            })
            .filter(val => val !== null && val > 0);

          if (densityValues.length > 0) {
            const minDensity = Math.min(...densityValues);
            const maxDensity = Math.max(...densityValues);
            minValue = (minDensity * 0.95).toFixed(3); 
            maxValue = (maxDensity * 1.05).toFixed(3); 
          }
        }
      }

      newPropertySpecs.push({
        property: propName,
        uom: newProp.uom || '',
        min: minValue,
        max: maxValue,
        basis: newProp.basis || 'volume',
        linear: newProp.linear !== undefined ? newProp.linear : true,
        index_formula: newProp.index_formula || '',
        reverse_formula: newProp.reverse_formula || '',
        description: newProp.description || '',
        defaultValue: newProp.defaultValue || ''
      });
    }
  });

  setPropertySpecs(newPropertySpecs);

  // Update components with new properties
  if (components.length > 0) {
    const updatedComponents = components.map(comp => {
      const updatedComp = { ...comp };
      
      filteredProperties.forEach(newProp => {
        const propName = newProp.property.trim();
        const propNameLower = propName.toLowerCase();
        
        if (!(propName in updatedComp)) {
          if (propNameLower === 'density') {
            if (updatedComp.density === '' || updatedComp.density === undefined) {
              const allDensities = components
                .filter(c => c.density && Number(c.density) > 0)
                .map(c => Number(c.density));
              
              if (allDensities.length > 0) {
                const avgDensity = allDensities.reduce((a, b) => a + b, 0) / allDensities.length;
                updatedComp.density = avgDensity.toFixed(3);
              } else {
                updatedComp.density = '0.850'; 
              }
            }
          } else if (propNameLower === 'gravity') {
            if (updatedComp.gravity === '' || updatedComp.gravity === undefined) {
              updatedComp.gravity = '35.0'; 
            }
          } else {
            updatedComp[propName] = newProp.defaultValue || 0;
          }
        }
      });
      
      return updatedComp;
    });
    setComponents(updatedComponents);
  }

  addNotification("success", `Added ${filteredProperties.length} property columns from global report`);
  closePropertyColumnModal();

  setTimeout(() => {
    calculateAllPropertyResults();
  }, 100);
};
  
  const openBlendComponentsModal = async () => {
    setLoadingAddComponent(true);
    try {
      const [componentsResponse, addedComponentsResponse] = await Promise.all([
        fetch(`${apiBase}/components`),
        fetch(`${apiBase}/added-components`)
      ]);

      const componentsResult = await componentsResponse.json();
      const addedComponentsResult = await addedComponentsResponse.json();

      if (!componentsResult.success || !addedComponentsResult.success) {
        throw new Error('Failed to fetch components from global report');
      }

      const apiComponents = componentsResult.data || [];
      const addedComponents = addedComponentsResult.data || [];

      const allComponents = [...apiComponents, ...addedComponents];

      if (allComponents.length === 0) {
        addNotification("error", "No components found in global report");
        return;
      }

      const enrichedComponents = await Promise.all(allComponents.map(async (component) => {
        try {
          // const detailsResponse = await fetch(`${apiBase}/components/${component._id || component.id}/details`);
          const detailsResponse = await fetch(`${apiBase}/components/${component._id || component.id}`);
          if (detailsResponse.ok) {
            const details = await detailsResponse.json();
            return {
              ...component,
              selectedProperties: details.data?.selectedProperties || [],
              selectedGroups: details.data?.selectedGroups || [],
              otherProductList: details.data?.otherProductList || []
            };
          }
        } catch (error) {
          console.error(`Error fetching details for component ${component.name}:`, error);
        }
        return component;
      }));

      const formattedComponents = enrichedComponents.map(component => ({
        id: component._id || component.id,
        name: component.name || '',
        cost: component.cost || '',
        costUnit: component.costUnit || currency,
        minVolume: component.minVolume || '',
        maxVolume: component.maxVolume || '',
        availableVolume: component.availableVolume || '',
        volumeUnit: component.volumeUnit || getCurrentUnit(),
        density: component.density || '',
        gravity: component.gravity || '',
        propertyType: component.propertyType || 'global',
        selectedProperty: component.selectedProperty || '',
        source: component.source || 'api',
        selectedProperties: component.selectedProperties || [],
        selectedGroups: component.selectedGroups || [],
        otherProductList: component.otherProductList || []
      }));

      setBlendComponentsModalData(formattedComponents);
      setSelectedBlendComponents(new Array(formattedComponents.length).fill(false));
      setShowBlendComponentsModal(true);

      addNotification("success", `Loaded ${formattedComponents.length} components from global report`);

    } catch (error) {
      console.error("Error loading components from global report:", error);
      addNotification("error", `Failed to load components: ${error.message}`);
    } finally {
      setLoadingAddComponent(false);
    }
  };

  const closeBlendComponentsModal = () => {
    setShowBlendComponentsModal(false);
    setBlendComponentsModalData([]);
    setSelectedBlendComponents([]);
  };

  const toggleBlendComponentSelection = (index) => {
    const newSelected = [...selectedBlendComponents];
    newSelected[index] = !newSelected[index];
    setSelectedBlendComponents(newSelected);
  };

  const selectAllBlendComponents = () => setSelectedBlendComponents(new Array(blendComponentsModalData.length).fill(true));
  const deselectAllBlendComponents = () => setSelectedBlendComponents(new Array(blendComponentsModalData.length).fill(false));

 



const handleSaveSelectedBlendComponents = () => {
  const selectedComponentsData = blendComponentsModalData.filter((_, index) => selectedBlendComponents[index]);

  if (selectedComponentsData.length === 0) {
    addNotification("error", "Please select at least one component");
    return;
  }

  const allPropertiesFromComponents = new Map();
  const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'];

  selectedComponentsData.forEach(component => {
    if (component.selectedProperties && Array.isArray(component.selectedProperties)) {
      component.selectedProperties.forEach(prop => {
        const propName = prop.name;
        const propNameLower = propName.toLowerCase();
        
        // Skip fixed fields
        if (!fixedFields.includes(propNameLower)) {
          if (!allPropertiesFromComponents.has(propName)) {
            allPropertiesFromComponents.set(propName, {
              property: propName,
              uom: prop.uom || '',
              defaultValue: prop.defaultValue || prop.editableDefaultValue || '',
              min: prop.minValue || '',
              max: prop.maxValue || '',
              basis: prop.basis || 'volume',
              linear: prop.linear !== undefined ? prop.linear : true,
              description: prop.description || ''
            });
          }
        }
      });
    }

    if (component.selectedGroups && Array.isArray(component.selectedGroups)) {
      component.selectedGroups.forEach(group => {
        const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'];
        if (group.name && !fixedFields.includes(group.name.toLowerCase())) {
          if (!allPropertiesFromComponents.has(group.name)) {
            allPropertiesFromComponents.set(group.name, {
              property: group.name,
              uom: group.uom || '',
              defaultValue: group.defaultValue || group.editableDefaultValue || '',
              min: group.minValue || '',
              max: group.maxValue || '',
              basis: group.basis || 'volume',
              linear: group.linear !== undefined ? group.linear : true,
              description: group.description || ''
            });
          }
        }
      });
    }

    if (component.otherProductList && Array.isArray(component.otherProductList)) {
      component.otherProductList.forEach(item => {
        const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'];
        if (item.name && !fixedFields.includes(item.name.toLowerCase())) {
          if (!allPropertiesFromComponents.has(item.name)) {
            allPropertiesFromComponents.set(item.name, {
              property: item.name,
              uom: getPropertyUOM(item.name) || '',
              defaultValue: item.value || 0,
              min: '',
              max: '',
              basis: 'volume',
              linear: true,
              description: ''
            });
          }
        }
      });
    }
  });

  const formattedComponents = selectedComponentsData.map(component => {
    const formattedComponent = {
      name: component.name,
      cost: component.cost || '',
      min: component.minVolume || '',
      max: component.maxVolume || '',
      availability: component.availableVolume || 1000,
      density: component.density !== undefined && component.density !== null && component.density !== '' ? component.density : '',
      gravity: component.gravity || ''
    };

    allPropertiesFromComponents.forEach((propData, propName) => {
      let propValue = 0;
      
      if (component.selectedProperties && Array.isArray(component.selectedProperties)) {
        const prop = component.selectedProperties.find(p => p.name === propName);
        if (prop) {
          propValue = prop.value || prop.defaultValue || prop.editableDefaultValue || 0;
        }
      }
      
      if (component.selectedGroups && Array.isArray(component.selectedGroups)) {
        const group = component.selectedGroups.find(g => g.name === propName);
        if (group) {
          propValue = group.value || group.defaultValue || group.editableDefaultValue || 0;
        }
      }
      
      if (component.otherProductList && Array.isArray(component.otherProductList)) {
        const item = component.otherProductList.find(i => i.name === propName);
        if (item) {
          propValue = item.value || 0;
        }
      }

      formattedComponent[propName] = propValue;
    });

    return formattedComponent;
  });

  setComponents(prev => [...prev, ...formattedComponents]);
  setSelectedComponents(prev => [...prev, ...new Array(formattedComponents.length).fill(true)]);

  const newPropertySpecs = [];
  const newPropertyColumns = [...selectedPropertyColumns];
  
  allPropertiesFromComponents.forEach((propData) => {
    const propertyExists = propertySpecs.some(prop => 
      prop.property && prop.property.toLowerCase() === propData.property.toLowerCase()
    );
    
    if (!propertyExists) {
      newPropertySpecs.push({
        property: propData.property,
        uom: propData.uom,
        min: propData.min,
        max: propData.max,
        defaultValue: propData.defaultValue,
        basis: propData.basis,
        linear: propData.linear,
        description: propData.description
      });
    }
    
    // Skip adding fixed fields to property columns
    const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'];
    if (!fixedFields.includes(propData.property.toLowerCase())) {
      if (!newPropertyColumns.includes(propData.property)) {
        newPropertyColumns.push(propData.property);
      }
    }
  });

  if (newPropertySpecs.length > 0) {
    setPropertySpecs(prev => [...prev, ...newPropertySpecs]);
  }
  
  setSelectedPropertyColumns(newPropertyColumns);

  addNotification("success", `Added ${formattedComponents.length} components with ${allPropertiesFromComponents.size} properties from global report`);
  closeBlendComponentsModal();

  setTimeout(() => {
    calculateAllPropertyResults();
  }, 100);
};


const getPropertyUOM = (propertyName) => {
  // Fixed UOMs for component fields
  if (!propertyName) return '';
  
  const propNameLower = propertyName.toLowerCase();
  
  if (propNameLower === 'density') {
    return 'g/cm³';
  }
  if (propNameLower === 'gravity' || propNameLower === 'specificgravity') {
    return 'API';
  }
  
  // Check in property specs
  const propertySpec = propertySpecs.find(prop =>
    prop.property && prop.property.toLowerCase() === propNameLower
  );

  if (propertySpec && propertySpec.uom) {
    return propertySpec.uom;
  }

  // Default UOMs for common properties
  const defaultUOMs = {
    'ron': '',
    'mon': '',
    'viscosity': 'cSt',
    'sulfur': 'ppm',
    'flashpoint': '°C',
    'pourpoint': '°C',
    'temperature': '°C',
    'pressure': 'psi',
    'volume': getCurrentUnit(),
    'weight': 'kg',
    'mass': 'kg'
  };

  return defaultUOMs[propNameLower] || '';
};


  const getComponentGroups = () => {
    return componentGroups.map(group => ({
      id: group._id,
      name: group.name,
      components: group.components || [],
      description: group.description,
      totalCost: group.totalCost,
      totalVolume: group.totalVolume,
      currency: group.currency,
      volumeUnit: group.volumeUnit,
      createdAt: group.createdAt
    }));
  };

  const getPropertyGroups = () => {
    return propertyGroups.map(group => ({
      id: group._id,
      name: group.name,
      properties: group.properties || [],
      description: group.description,
      createdAt: group.createdAt
    }));
  };

  const getPropertySets = () => {
    return savedPropertySets.map(set => ({
      id: set._id || set.id,
      name: set.name,
      properties: set.properties || [],
      description: set.description,
      createdAt: set.createdAt,
      setMin: set.setMin,
      setMax: set.setMax
    }));
  };

  // Save Functions
  const handleSaveAllClick = () => {
    if (!volume && !editingComponent && !editingProperty) {
      addNotification("error", "Volume is required to save optimization");
      return;
    }
    setShowSaveAllPopup(true);
  };


  // Save - Updates existing data, or creates new if not editing
  const handleSaveClick = async () => {
    if (!volume) {
      addNotification("error", "Volume is required to save");
      return;
    }

    // If we have a currentModelId, we're saving an existing model - update it
    if (currentModelId) {
      setLoadingSaveAll(true);
      
      const optimizationData = {
        _id: currentModelId,
        blend: blend || {},
        totalCost: totalCost || 0,
        status: status || "Success",
        message: message || "Optimization saved successfully",
        costBreakdown: costBreakdown || [],
        chartData: chartData || [],
        volume: Number(volume) || 0,
        gap: Number(gap) || 0.01,
        threads: Number(threads) || 1,
        timestamp: Date.now(),
        updatedAt: new Date().toISOString(),
        customTextField: modelName.trim() || `Optimization_${formatDate(Date.now())}`,
        customName: modelName.trim() || `Optimization_${formatDate(Date.now())}`,
        customColumns: Array.isArray(customColumns) ? customColumns : [],
        modelBasis: modelBasis || 'volume',
        currency: currency || 'USD',
        blendingType: blendingType || 'linear',
        indexFormula: indexFormula || '',
        reverseFormula: reverseFormula || '',
        componentBasis: componentBasis || 'volume',
        componentBlendingType: componentBlendingType || 'linear',
        weight: Number(weight) || 0,
        components: components
          .filter((_, index) => selectedComponents[index])
          .map(comp => {
            const cleanComp = {
              name: String(comp.name || 'Unknown Component'),
              originalName: String(comp.name || 'Unknown Component'),
              cost: Number(comp.cost) || 0,
              costUnit: comp.costUnit || currency || 'USD',
              min: Number(comp.min) || 0,
              max: Number(comp.max) || 1000,
              availability: Number(comp.availability) || 1000,
              volumeUnit: comp.volumeUnit || getCurrentUnit(),
              density: Number(comp.density) || 0,
              gravity: Number(comp.gravity) || 0,
              basis: comp.basis || 'volume',
              blending_type: comp.blending_type || comp.blendingType || 'linear'
            };
            selectedPropertyColumns.forEach(col => {
              if (col && !['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'availability', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype'].includes(col.toLowerCase())) {
                cleanComp[col] = Number(comp[col]) || 0;
              }
            });
            return cleanComp;
          }),
        propertySpecs: propertySpecs.map(prop => ({
          property: String(prop.property || 'Unknown Property'),
          uom: String(prop.uom || 'units'),
          min: Number(prop.min) || 0,
          max: Number(prop.max) || 100,
          basis: prop.basis || 'volume',
          linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
          index_formula: String(prop.index_formula || ''),
          reverse_formula: String(prop.reverse_formula || ''),
          actualResult: Number(propertyResultsForDisplay[prop.property]) || 0
        })),
        selectedComponents: selectedComponents.filter(Boolean),
        propertyResults: propertyResultsForDisplay || {},
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : []
      };

      try {
        // Update in localStorage
        const existingResults = JSON.parse(localStorage.getItem('savedOptimizationResults') || '[]');
        const updatedResults = existingResults.map(result => 
          result._id === currentModelId ? optimizationData : result
        );
        localStorage.setItem('savedOptimizationResults', JSON.stringify(updatedResults));
        
        // Update timestamp
        const now = new Date().toISOString();
        setLastSavedTimestamp(now);
        localStorage.setItem('blendOptimizerLastSaved', now);
        
        // Update in API if connected
        if (apiConnected) {
          try {
            const response = await fetch(`${apiBase}/optimizedResults/updateOptimizedResult/${currentModelId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(optimizationData)
            });
            const result = await response.json();
            if (result.success) {
              addNotification("success", `Model "${modelName}" saved successfully`);
            } else {
              addNotification("success", `Model "${modelName}" saved locally`);
            }
          } catch (apiError) {
            addNotification("success", `Model "${modelName}" saved locally`);
          }
        } else {
          addNotification("success", `Model "${modelName}" saved successfully`);
        }
        
        window.dispatchEvent(new Event('refreshResults'));
      } catch (error) {
        console.error('Error saving model:', error);
        addNotification("error", "Failed to save model");
      }
      
      setLoadingSaveAll(false);
      return;
    }

    // No currentModelId means this is a new model - open Save As popup
    const nameToSave = modelName || `Optimization_${formatDate(Date.now())}`;
    setSaveAsName(nameToSave);
    setShowSaveAsPopup(true);
  };

const handleSaveAsClick = async () => {
  if (!volume && !editingComponent && !editingProperty) {
    addNotification("error", "Volume is required to save optimization");
    return;
  }
  
  let nameToSave = '';
  if (editingResult) {
    nameToSave = editingResult.customName || editingResult.customTextField || editingResult.optimizeName || `Edit - ${formatDate(editingResult.timestamp)}`;
  } else if (editingComponent) {
    nameToSave = editingComponent.customName || `Edit Component - ${new Date().toLocaleDateString()}`;
  } else if (editingProperty) {
    nameToSave = editingProperty.customName || `Edit Property - ${new Date().toLocaleDateString()}`;
  } else {
    nameToSave = saveAllName || `Optimization_${formatDate(Date.now())}`;
  }
  
  setSaveAsName(nameToSave);
  
  if (editingResult || editingComponent || editingProperty) {
    await handleSaveAsConfirmDirect(nameToSave);
  } else {
    setShowSaveAsPopup(true);
  }
};
  

  // Save As - Always creates new data (doesn't edit)
  const handleSaveAsConfirmDirect = async (nameToSave) => {
  if (!nameToSave || !nameToSave.trim()) {
    addNotification("error", "Please enter a name for saving");
    return;
  }

  setLoadingSaveAll(true);

  const isEditingResult = editingResult !== null;
  const isEditingComponent = editingComponent !== null;
  const isEditingProperty = editingProperty !== null;
  
  let itemType = 'optimization';
  
  if (isEditingResult) {
    itemType = 'optimization';
  } else if (isEditingComponent) {
    itemType = 'component';
  } else if (isEditingProperty) {
    itemType = 'property';
  }

  try {
    if (itemType === 'optimization') {
      const optimizationData = {
        _id: undefined, // Always create new - don't use originalId
        blend: blend || {},
        totalCost: totalCost || 0,
        status: isEditingResult ? "Edited" : "Success",
        message: message || (isEditingResult ? "Optimization edited successfully" : "Optimization completed successfully"),
        costBreakdown: costBreakdown || [],
        chartData: chartData || [],
        volume: Number(volume) || 0,
        gap: Number(gap) || 0.01,
        threads: Number(threads) || 1,
        timestamp: isEditingResult ? editingResult?.timestamp : Date.now(),
        createdAt: isEditingResult ? editingResult?.createdAt : new Date().toISOString(),
        customTextField: nameToSave.trim(),
        customName: nameToSave.trim(),
        customColumns: Array.isArray(customColumns) ? customColumns : [],
        modelBasis: modelBasis || 'volume',
        currency: currency || 'USD',
        blendingType: blendingType || 'linear',
        indexFormula: indexFormula || '',
        reverseFormula: reverseFormula || '',
        componentBasis: componentBasis || 'volume',
        componentBlendingType: componentBlendingType || 'linear',
        components: components
          .filter((_, index) => selectedComponents[index])
          .map(comp => {
            const cleanComp = {
              name: String(comp.name || 'Unknown Component'),
              originalName: String(comp.name || 'Unknown Component'),
              cost: Number(comp.cost) || 0,
              costUnit: comp.costUnit || currency || 'USD',
              min: Number(comp.min) || 0,
              max: Number(comp.max) || 1000,
              availability: Number(comp.availability) || 1000,
              volumeUnit: comp.volumeUnit || getCurrentUnit(),
              density: Number(comp.density) || 0,
              gravity: Number(comp.gravity) || 0,
              basis: comp.basis || 'volume',
              blending_type: comp.blending_type || comp.blendingType || 'linear'
            };

            selectedPropertyColumns.forEach(col => {
              if (col && !['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'availability', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype'].includes(col.toLowerCase())) {
                cleanComp[col] = Number(comp[col]) || 0;
              }
            });

            return cleanComp;
          }),
        propertySpecs: propertySpecs.map(prop => ({
          property: String(prop.property || 'Unknown Property'),
          uom: String(prop.uom || 'units'),
          min: Number(prop.min) || 0,
          max: Number(prop.max) || 100,
          basis: prop.basis || 'volume',
          linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
          index_formula: String(prop.index_formula || ''),
          reverse_formula: String(prop.reverse_formula || ''),
          actualResult: Number(propertyResultsForDisplay[prop.property]) || 0
        })),
        selectedComponents: selectedComponents.filter(Boolean),
        weight: Number(weight) || 0,
        propertyResults: propertyResultsForDisplay || {},
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : [],
        componentPropertyValues: components
          .filter((_, index) => selectedComponents[index])
          .map(comp => {
            const propertyValues = {};
            const fixedFields = ['name', 'originalName', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type', '_id', 'id'];
            
            if (selectedPropertyColumns && Array.isArray(selectedPropertyColumns)) {
              selectedPropertyColumns.forEach(propName => {
                if (propName && !fixedFields.includes(propName.toLowerCase()) && comp[propName] !== undefined && comp[propName] !== null) {
                  propertyValues[propName] = Number(comp[propName]) || 0;
                }
              });
            }
            
            Object.keys(comp).forEach(key => {
              if (!fixedFields.includes(key.toLowerCase()) && comp[key] !== undefined && comp[key] !== null && !propertyValues.hasOwnProperty(key)) {
                propertyValues[key] = Number(comp[key]) || 0;
              }
            });
            
            return {
              componentName: comp.name || 'Unknown Component',
              propertyValues: propertyValues
            };
          }).filter(cpv => Object.keys(cpv.propertyValues).length > 0)
      };

      let apiSaveSuccess = false;

      if (apiConnected) {
        try {
          // Always create new - don't update existing
          apiSaveSuccess = await saveResultToAPI(optimizationData);
        } catch (error) {
          console.error('Error saving to API:', error);
          addNotification("warning", "Failed to save to database, saving locally only");
        }
      }

      try {
        const existingResults = JSON.parse(localStorage.getItem('savedOptimizationResults') || '[]');
        // Always create new entry
        const updatedResults = [optimizationData, ...existingResults.slice(0, 49)];
        localStorage.setItem('savedOptimizationResults', JSON.stringify(updatedResults));
        addNotification("success", `New optimization result saved successfully as "${nameToSave.trim()}"`);
        clearPersistedModelData();

      } catch (localError) {
        console.error('Error saving to localStorage:', localError);
        addNotification("error", "Failed to save optimization data locally");
      }
      
    } else if (itemType === 'component') {
      const componentData = {
        _id: isEditingComponent ? originalId : undefined,
        name: nameToSave.trim(),
        customName: nameToSave.trim(),
        timestamp: isEditingComponent ? editingComponent?.timestamp : Date.now(),
        createdAt: isEditingComponent ? editingComponent?.createdAt : new Date().toISOString(),
        components: components.map(comp => {
          const cleanComp = {
            name: String(comp.name || 'Unknown Component'),
            cost: Number(comp.cost) || 0,
            min: Number(comp.min) || 0,
            max: Number(comp.max) || 1000,
            availability: Number(comp.availability) || 1000,
            density: Number(comp.density) || 0,
            gravity: Number(comp.gravity) || 0,
            basis: comp.basis || 'volume'
          };

          selectedPropertyColumns.forEach(col => {
            if (col && !['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'].includes(col.toLowerCase())) {
              cleanComp[col] = Number(comp[col]) || 0;
            }
          });

          return cleanComp;
        }),
        modelBasis: modelBasis || 'volume',
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : []
      };

      if (apiConnected) {
        try {
          // Always create new - don't update existing
          const response = await api.components.create({
            name: nameToSave.trim(),
            components: componentData.components,
            basis: modelBasis || 'volume'
          });
          
          if (response.success) {
            addNotification("success", "Component set saved to database");
          } else {
            throw new Error(response.message || 'Create failed');
          }
        } catch (error) {
          console.error('Error saving component to API:', error);
          addNotification("warning", "Failed to save component to database, saving locally only");
        }
      }

      try {
        const existingComponents = JSON.parse(localStorage.getItem('savedComponents') || '[]');
        
        // Always create new entry
        const updatedComponents = [componentData, ...existingComponents.slice(0, 49)];
        localStorage.setItem('savedComponents', JSON.stringify(updatedComponents));
        addNotification("success", `New component set saved successfully as "${nameToSave.trim()}"`);
        clearPersistedModelData();
      } catch (localError) {
        console.error('Error saving component to localStorage:', localError);
        addNotification("error", "Failed to save component data locally");
      }
      
    } else if (itemType === 'property') {
      const propertyData = {
        _id: undefined, // Always create new - don't use originalId
        gradeName: nameToSave.trim(),
        customName: nameToSave.trim(),
        timestamp: Date.now(),
        createdAt: new Date().toISOString(),
        constraintsProperties: propertySpecs.map(prop => ({
          name: prop.property || 'Unknown Property',
          property: prop.property || 'Unknown Property',
          uom: prop.uom || 'units',
          minVolume: Number(prop.min) || 0,
          maxVolume: Number(prop.max) || 100,
          basis: prop.basis || 'volume',
          linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
          indexFormula: prop.index_formula || '',
          reverseFormula: prop.reverse_formula || ''
        })),
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : []
      };

      if (apiConnected) {
        try {
          if (isEditingProperty && originalId) {
            const response = await api.gradeProperties.edit(originalId, {
              gradeName: nameToSave.trim(),
              constraintsProperties: propertyData.constraintsProperties,
              updatedAt: new Date().toISOString()
            });
            
            if (response.success) {
              addNotification("success", "Property set updated in database");
            } else {
              throw new Error(response.message || 'Update failed');
            }
          } else {
            const response = await api.gradeProperties.add({
              gradeName: nameToSave.trim(),
              constraintsProperties: propertyData.constraintsProperties,
              createdAt: new Date().toISOString()
            });
            
            if (response.success) {
              addNotification("success", "Property set saved to database");
            } else {
              throw new Error(response.message || 'Create failed');
            }
          }
        } catch (error) {
          console.error('Error saving property to API:', error);
          addNotification("warning", "Failed to save property to database, saving locally only");
        }
      }

      try {
        const existingProperties = JSON.parse(localStorage.getItem('savedProperties') || '[]');
        
        if (isEditingProperty && originalId) {
          const updatedProperties = existingProperties.map(prop => 
            prop._id === originalId ? propertyData : prop
          );
          localStorage.setItem('savedProperties', JSON.stringify(updatedProperties));
          addNotification("success", `Property set updated successfully as "${nameToSave.trim()}"`);
          clearPersistedModelData();
        } else {
          const updatedProperties = [propertyData, ...existingProperties.slice(0, 49)];
          localStorage.setItem('savedProperties', JSON.stringify(updatedProperties));
          addNotification("success", "New property set saved successfully");
          clearPersistedModelData();
        }
      } catch (localError) {
        console.error('Error saving property to localStorage:', localError);
        addNotification("error", "Failed to save property data locally");
      }
    }
    
    setEditingResult(null);
    setEditingComponent(null);
    setEditingProperty(null);
    setSaveAsName('');
    
    localStorage.removeItem('blendOptimizerEditData');
    localStorage.removeItem('componentsToEdit');
    localStorage.removeItem('propertiesToEdit');
    
  } catch (error) {
    console.error('Error in handleSaveAsConfirmDirect:', error);
    addNotification("error", "Failed to save data");
  } finally {
    setLoadingSaveAll(false);
  }
};

  const handleSaveAsCancel = () => {
    setShowSaveAsPopup(false);
    setSaveAsName('');
  };

  const handleSaveAsConfirm = async () => {
    if (!saveAsName.trim()) {
      addNotification("error", "Please enter a name for saving");
      return;
    }

    setLoadingSaveAll(true);

    const isEditingResult = editingResult !== null;
    const isEditingComponent = editingComponent !== null;
    const isEditingProperty = editingProperty !== null;
    
    let originalId = null;
    let itemType = 'optimization';
    
    if (isEditingResult) {
      originalId = editingResult._id;
      itemType = 'optimization';
    } else if (isEditingComponent) {
      originalId = editingComponent._id;
      itemType = 'component';
    } else if (isEditingProperty) {
      originalId = editingProperty._id;
      itemType = 'property';
    }

    if (itemType === 'optimization') {
      const optimizationData = {
        _id: isEditingResult ? originalId : undefined,
        blend: blend || {},
        totalCost: totalCost || 0,
        status: isEditingResult ? "Edited" : "Success",
        message: message || (isEditingResult ? "Optimization edited successfully" : "Optimization completed successfully"),
        costBreakdown: costBreakdown || [],
        chartData: chartData || [],
        volume: Number(volume) || 0,
        gap: Number(gap) || 0.01,
        threads: Number(threads) || 1,
        timestamp: isEditingResult ? editingResult?.timestamp : Date.now(),
        createdAt: isEditingResult ? editingResult?.createdAt : new Date().toISOString(),
        customTextField: saveAsName.trim(),
        customName: saveAsName.trim(),
        customColumns: Array.isArray(customColumns) ? customColumns : [],
        modelBasis: modelBasis || 'volume',
        currency: currency || 'USD',
        blendingType: blendingType || 'linear',
        indexFormula: indexFormula || '',
        reverseFormula: reverseFormula || '',
        componentBasis: componentBasis || 'volume',
        componentBlendingType: componentBlendingType || 'linear',
        components: components
          .filter((_, index) => selectedComponents[index])
          .map(comp => {
            const cleanComp = {
              name: String(comp.name || 'Unknown Component'),
              originalName: String(comp.name || 'Unknown Component'),
              cost: Number(comp.cost) || 0,
              costUnit: comp.costUnit || currency || 'USD',
              min: Number(comp.min) || 0,
              max: Number(comp.max) || 1000,
              availability: Number(comp.availability) || 1000,
              volumeUnit: comp.volumeUnit || getCurrentUnit(),
              density: Number(comp.density) || 0,
              gravity: Number(comp.gravity) || 0,
              basis: comp.basis || 'volume',
              blending_type: comp.blending_type || comp.blendingType || 'linear'
            };

            selectedPropertyColumns.forEach(col => {
              if (col && !['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'availability', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype'].includes(col.toLowerCase())) {
                cleanComp[col] = Number(comp[col]) || 0;
              }
            });

            return cleanComp;
          }),
        propertySpecs: propertySpecs.map(prop => ({
          property: String(prop.property || 'Unknown Property'),
          uom: String(prop.uom || 'units'),
          min: Number(prop.min) || 0,
          max: Number(prop.max) || 100,
          basis: prop.basis || 'volume',
          linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
          index_formula: String(prop.index_formula || ''),
          reverse_formula: String(prop.reverse_formula || ''),
          actualResult: Number(propertyResultsForDisplay[prop.property]) || 0
        })),
        selectedComponents: selectedComponents.filter(Boolean),
        weight: Number(weight) || 0,
        propertyResults: propertyResultsForDisplay || {},
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : [],
        componentPropertyValues: components
          .filter((_, index) => selectedComponents[index])
          .map(comp => {
            const propertyValues = {};
            const fixedFields = ['name', 'originalName', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type', '_id'];
            
            Object.keys(comp).forEach(key => {
              if (!fixedFields.includes(key.toLowerCase()) && comp[key] !== undefined && comp[key] !== null) {
                propertyValues[key] = Number(comp[key]) || 0;
              }
            });
            
            return {
              componentName: comp.name || 'Unknown Component',
              propertyValues: propertyValues
            };
          }).filter(cpv => Object.keys(cpv.propertyValues).length > 0)
      };

      let apiSaveSuccess = false;

      if (apiConnected) {
        try {
          if (isEditingResult && originalId) {
            console.log("Updating existing result:", originalId);
            try {
              const response = await fetch(`${apiBase}/optimizedResults/updateOptimizedResult/${originalId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(optimizationData)
              });
              const result = await response.json();
              if (result.success) {
                apiSaveSuccess = true;
                addNotification("success", "Optimization result updated in database");
              } else {
                throw new Error(result.message || 'Update failed');
              }
            } catch (updateError) {
              console.error('Error updating result:', updateError);
              addNotification("warning", "Failed to update in database, saving locally only");
            }
          } else {
            apiSaveSuccess = await saveResultToAPI(optimizationData);
          }
        } catch (error) {
          console.error('Error saving to API:', error);
          addNotification("warning", "Failed to save to database, saving locally only");
        }
      }

      try {
        const existingResults = JSON.parse(localStorage.getItem('savedOptimizationResults') || '[]');
        
        if (isEditingResult && originalId) {
          const updatedResults = existingResults.map(result => 
            result._id === originalId ? optimizationData : result
          );
          localStorage.setItem('savedOptimizationResults', JSON.stringify(updatedResults));
          addNotification("success", "Optimization result updated successfully");
          clearPersistedModelData();
        } else {
          const updatedResults = [optimizationData, ...existingResults.slice(0, 49)];
          localStorage.setItem('savedOptimizationResults', JSON.stringify(updatedResults));
          addNotification("success", "New optimization result saved successfully");
        }
        
      } catch (localError) {
        console.error('Error saving to localStorage:', localError);
        addNotification("error", "Failed to save optimization data locally");
      }
      
    } else if (itemType === 'component') {
      const componentData = {
        _id: isEditingComponent ? originalId : undefined,
        name: saveAsName.trim(),
        customName: saveAsName.trim(),
        timestamp: isEditingComponent ? editingComponent?.timestamp : Date.now(),
        createdAt: isEditingComponent ? editingComponent?.createdAt : new Date().toISOString(),
        components: components.map(comp => {
          const cleanComp = {
            name: String(comp.name || 'Unknown Component'),
            cost: Number(comp.cost) || 0,
            min: Number(comp.min) || 0,
            max: Number(comp.max) || 1000,
            availability: Number(comp.availability) || 1000,
            density: Number(comp.density) || 0,
            gravity: Number(comp.gravity) || 0,
            basis: comp.basis || 'volume'
          };

          selectedPropertyColumns.forEach(col => {
            if (col && !['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'].includes(col.toLowerCase())) {
              cleanComp[col] = Number(comp[col]) || 0;
            }
          });

          return cleanComp;
        }),
        modelBasis: modelBasis || 'volume',
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : []
      };

      if (apiConnected) {
        try {
          if (isEditingComponent && originalId) {
            const response = await api.components.update(originalId, {
              name: saveAsName.trim(),
              components: componentData.components,
              basis: modelBasis || 'volume',
              updatedAt: new Date().toISOString()
            });
            
            if (response.success) {
              addNotification("success", "Component set updated in database");
            } else {
              throw new Error(response.message || 'Update failed');
            }
          } else {
            const response = await api.components.create({
              name: saveAsName.trim(),
              cost: componentData.components.reduce((sum, comp) => sum + (Number(comp.cost) || 0), 0),
              minVolume: Math.min(...componentData.components.map(comp => Number(comp.min) || 0)),
              maxVolume: Math.max(...componentData.components.map(comp => Number(comp.max) || 1000)),
              availableVolume: Math.max(...componentData.components.map(comp => Number(comp.availability) || 1000)),
              density: componentData.components[0]?.density || 0,
              otherProductList: componentData.components.flatMap(comp => 
                Object.keys(comp)
                  .filter(key => !['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis'].includes(key.toLowerCase()))
                  .map(key => ({ name: key, value: comp[key] || 0 }))
              ),
              basis: modelBasis || 'volume',
              createdAt: new Date().toISOString()
            });
            
            if (response.success) {
              addNotification("success", "Component set saved to database");
            }
          }
        } catch (error) {
          console.error('Error saving component to API:', error);
          addNotification("warning", "Failed to save component to database, saving locally only");
        }
      }
      
      try {
        const existingComponents = JSON.parse(localStorage.getItem('savedComponents') || '[]');
        
        if (isEditingComponent && originalId) {
          const updatedComponents = existingComponents.map(comp => 
            comp._id === originalId ? componentData : comp
          );
          localStorage.setItem('savedComponents', JSON.stringify(updatedComponents));
          addNotification("success", "Component set updated successfully");
          clearPersistedModelData();
        } else {
          const updatedComponents = [componentData, ...existingComponents.slice(0, 49)];
          localStorage.setItem('savedComponents', JSON.stringify(updatedComponents));
          addNotification("success", "New component set saved successfully");
        }
      } catch (localError) {
        console.error('Error saving component to localStorage:', localError);
        addNotification("error", "Failed to save component data locally");
      }
      
    } else if (itemType === 'property') {
      // Save property set
      const propertyData = {
        _id: isEditingProperty ? originalId : undefined,
        gradeName: saveAsName.trim(),
        customName: saveAsName.trim(),
        timestamp: isEditingProperty ? editingProperty?.timestamp : Date.now(),
        createdAt: isEditingProperty ? editingProperty?.createdAt : new Date().toISOString(),
        constraintsProperties: propertySpecs.map(prop => ({
          name: prop.property || 'Unknown Property',
          property: prop.property || 'Unknown Property',
          uom: prop.uom || 'units',
          minVolume: Number(prop.min) || 0,
          maxVolume: Number(prop.max) || 100,
          basis: prop.basis || 'volume',
          linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
          indexFormula: prop.index_formula || '',
          reverseFormula: prop.reverse_formula || ''
        })),
        selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : []
      };

      // Save property to API
      if (apiConnected) {
        try {
          if (isEditingProperty && originalId) {
            const response = await api.gradeProperties.edit(originalId, {
              gradeName: saveAsName.trim(),
              constraintsProperties: propertyData.constraintsProperties,
              updatedAt: new Date().toISOString()
            });
            
            if (response.success) {
              addNotification("success", "Property set updated in database");
            } else {
              throw new Error(result.message || 'Update failed');
            }
          } else {
            const response = await api.gradeProperties.add({
              gradeName: saveAsName.trim(),
              constraintsProperties: propertyData.constraintsProperties,
              createdAt: new Date().toISOString()
            });
            
            if (response.success) {
              addNotification("success", "Property set saved to database");
            }
          }
        } catch (error) {
          console.error('Error saving property to API:', error);
          addNotification("warning", "Failed to save property to database, saving locally only");
        }
      }
      
      try {
        const existingProperties = JSON.parse(localStorage.getItem('savedProperties') || '[]');
        
        if (isEditingProperty && originalId) {
          const updatedProperties = existingProperties.map(prop => 
            prop._id === originalId ? propertyData : prop
          );
          localStorage.setItem('savedProperties', JSON.stringify(updatedProperties));
          addNotification("success", "Property set updated successfully");
          clearPersistedModelData();
        } else {
          const updatedProperties = [propertyData, ...existingProperties.slice(0, 49)];
          localStorage.setItem('savedProperties', JSON.stringify(updatedProperties));
          addNotification("success", "New property set saved successfully");
          clearPersistedModelData();
        }
      } catch (localError) {
        console.error('Error saving property to localStorage:', localError);
        addNotification("error", "Failed to save property data locally");
      }
    }

    setEditingResult(null);
    setEditingComponent(null);
    setEditingProperty(null);
    localStorage.removeItem('blendOptimizerEditData');

    setLoadingSaveAll(false);
    setShowSaveAsPopup(false);
  };

  const handleSaveAllConfirm = async () => {
    if (!saveAllName.trim()) {
      addNotification("error", "Please enter a name for saving");
      return;
    }

    setLoadingSaveAll(true);

    const optimizationData = {
      blend: blend || {},
      totalCost: totalCost || 0,
      status: status || "Success",
      message: message || "Optimization completed successfully",
      costBreakdown: costBreakdown || [],
      chartData: chartData || [],
      volume: Number(volume) || 0,
      gap: Number(gap) || 0.01,
      threads: Number(threads) || 1,
      timestamp: new Date().toISOString(),
      customTextField: saveAllName.trim(),
      customName: saveAllName.trim(),
      customColumns: Array.isArray(customColumns) ? customColumns : [],
      modelBasis: modelBasis || 'volume',
      currency: currency || 'USD',
      blendingType: blendingType || 'linear',
      indexFormula: indexFormula || '',
      reverseFormula: reverseFormula || '',
      componentBasis: componentBasis || 'volume',
      componentBlendingType: componentBlendingType || 'linear',
      components: components
        .filter((_, index) => selectedComponents[index])
        .map(comp => {
          const cleanComp = {
            name: String(comp.name || 'Unknown Component'),
            originalName: String(comp.name || 'Unknown Component'),
            cost: Number(comp.cost) || 0,
            costUnit: comp.costUnit || currency || 'USD',
            min: Number(comp.min) || 0,
            max: Number(comp.max) || 1000,
            availability: Number(comp.availability) || 1000,
            volumeUnit: comp.volumeUnit || getCurrentUnit(),
            density: Number(comp.density) || 0,
            gravity: Number(comp.gravity) || 0,
            basis: comp.basis || 'volume',
            blending_type: comp.blending_type || comp.blendingType || 'linear'
          };

          if (selectedPropertyColumns && Array.isArray(selectedPropertyColumns)) {
            selectedPropertyColumns.forEach(col => {
              if (col && !['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'availability', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype'].includes(col.toLowerCase())) {
                cleanComp[col] = Number(comp[col]) || 0;
              }
            });
          }

          if (customColumns && Array.isArray(customColumns)) {
            customColumns.forEach(col => {
              if (col && !['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'availability', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype'].includes(col.toLowerCase()) && !cleanComp.hasOwnProperty(col)) {
                cleanComp[col] = Number(comp[col]) || 0;
              }
            });
          }

          return cleanComp;
        }),
      propertySpecs: propertySpecs.map(prop => ({
        property: String(prop.property || 'Unknown Property'),
        uom: String(prop.uom || 'units'),
        min: Number(prop.min) || 0,
        max: Number(prop.max) || 100,
        basis: prop.basis || 'volume',
        linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
        index_formula: String(prop.index_formula || ''),
        reverse_formula: String(prop.reverse_formula || ''),
        actualResult: Number(propertyResultsForDisplay[prop.property]) || 0
      })),
      selectedComponents: selectedComponents.filter(Boolean),
      weight: Number(weight) || 0,
      propertyResults: propertyResultsForDisplay || {},
      selectedPropertyColumns: Array.isArray(selectedPropertyColumns) ? selectedPropertyColumns : [],
      componentPropertyValues: components
        .filter((_, index) => selectedComponents[index])
        .map(comp => {
          const propertyValues = {};
          const fixedFields = ['name', 'originalName', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type', '_id', 'id'];
          
          if (selectedPropertyColumns && Array.isArray(selectedPropertyColumns)) {
            selectedPropertyColumns.forEach(propName => {
              if (propName && !fixedFields.includes(propName.toLowerCase()) && comp[propName] !== undefined && comp[propName] !== null) {
                propertyValues[propName] = Number(comp[propName]) || 0;
              }
            });
          }
          
          Object.keys(comp).forEach(key => {
            if (!fixedFields.includes(key.toLowerCase()) && comp[key] !== undefined && comp[key] !== null && !propertyValues.hasOwnProperty(key)) {
              propertyValues[key] = Number(comp[key]) || 0;
            }
          });
          
          return {
            componentName: comp.name || 'Unknown Component',
            propertyValues: propertyValues
          };
        }).filter(cpv => Object.keys(cpv.propertyValues).length > 0)
    };

    let apiSaveSuccess = false;

    if (apiConnected) {
      try {
        apiSaveSuccess = await saveResultToAPI(optimizationData);

        if (optimizationData.components && optimizationData.components.length > 0) {
          const componentsSaved = await saveAllComponentsToMongoDB(optimizationData.components);
          console.log(`Successfully saved ${componentsSaved} components to MongoDB`);
        }
        
        if (optimizationData.propertySpecs && optimizationData.propertySpecs.length > 0) {
          const propertySaveSuccess = await savePropertiesToAPI(optimizationData.propertySpecs);
          if (propertySaveSuccess) {
            console.log(`Successfully saved ${optimizationData.propertySpecs.length} properties to MongoDB`);
          }
        }
      } catch (error) {
        console.error('Error saving to API:', error);
        addNotification("warning", "Failed to save to database, saving locally only");
      }
    }

    try {
      const existingResults = JSON.parse(localStorage.getItem('savedOptimizationResults') || '[]');
      const updatedResults = [optimizationData, ...existingResults.slice(0, 49)];
      localStorage.setItem('savedOptimizationResults', JSON.stringify(updatedResults));

      if (optimizationData.components.length > 0) {
        const componentsData = {
          components: optimizationData.components,
          selectedComponents: selectedComponents.filter(Boolean),
          volume: optimizationData.volume,
          gap: optimizationData.gap,
          threads: optimizationData.threads,
          timestamp: Date.now(),
          customName: saveAllName.trim(),
          customColumns: optimizationData.customColumns,
          modelBasis: optimizationData.modelBasis,
          blendingType: optimizationData.blendingType,
          indexFormula: optimizationData.indexFormula,
          reverseFormula: optimizationData.reverseFormula,
          componentBasis: optimizationData.componentBasis,
          componentBlendingType: optimizationData.componentBlendingType,
          selectedPropertyColumns: optimizationData.selectedPropertyColumns
        };
        saveComponentsToStorage(componentsData);
      }

      if (optimizationData.propertySpecs.length > 0) {
        const propertiesData = {
          properties: optimizationData.propertySpecs,
          customName: saveAllName.trim(),
          timestamp: Date.now(),
          customColumns: optimizationData.customColumns,
          blendingType: optimizationData.blendingType,
          indexFormula: optimizationData.indexFormula,
          reverseFormula: optimizationData.reverseFormula,
          selectedPropertyColumns: optimizationData.selectedPropertyColumns
        };
        savePropertiesToStorage(propertiesData);
      }

      if (optimizationData.costBreakdown.length > 0) {
        saveCostBreakdown({
          breakdown: optimizationData.costBreakdown,
          totalCost: optimizationData.totalCost,
          totalVolume: Object.values(optimizationData.blend || {}).reduce((a, b) => a + Number(b || 0), 0),
          status: optimizationData.status,
          timestamp: Date.now(),
          customColumns: optimizationData.customColumns,
          selectedPropertyColumns: optimizationData.selectedPropertyColumns
        });
      }

      if (optimizationData.chartData.length > 0) {
        saveVisualization({
          chartData: optimizationData.chartData,
          blend: optimizationData.blend || {},
          totalVolume: Object.values(optimizationData.blend || {}).reduce((a, b) => a + Number(b || 0), 0),
          status: optimizationData.status,
          timestamp: Date.now(),
          componentSetName: saveAllName.trim(),
          customColumns: optimizationData.customColumns,
          selectedPropertyColumns: optimizationData.selectedPropertyColumns
        });
      }

      if (apiSaveSuccess) {
        addNotification("success", "All data saved successfully to database and local storage");
        const ts = new Date().toISOString();
        setLastSavedTimestamp(ts);
        localStorage.setItem('blendOptimizerLastSaved', ts);
        // Set currentModelId for future Save operations
        setCurrentModelId(optimizationData._id);
        localStorage.setItem('blendOptimizerCurrentModelId', optimizationData._id);
        // Update model name
        setModelName(saveAllName.trim());
        localStorage.setItem('blendOptimizerModelName', saveAllName.trim());
      } else {
        addNotification("success", "All data saved successfully to local storage");
        // Set currentModelId for future Save operations
        setCurrentModelId(optimizationData._id);
        localStorage.setItem('blendOptimizerCurrentModelId', optimizationData._id);
        // Update model name
        setModelName(saveAllName.trim());
        localStorage.setItem('blendOptimizerModelName', saveAllName.trim());
      }

    } catch (localError) {
      console.error('Error saving to localStorage:', localError);
      addNotification("error", "Failed to save data locally");
    }

    setLoadingSaveAll(false);
    setShowSaveAllPopup(false);
  };

  const handleSaveAllCancel = () => {
    setShowSaveAllPopup(false);
  };

  const saveResultToAPI = async (resultData) => {
    try {
      console.log("Starting API save process...");

      if (!apiConnected) {
        console.log("API not connected, cannot save to database");
        addNotification("error", "Backend not connected. Cannot save to database.");
        return false;
      }

      let calculatedTotalCost = 0;

      if (resultData.blend && Object.keys(resultData.blend).length > 0) {
        calculatedTotalCost = Object.entries(resultData.blend).reduce((total, [componentName, volume]) => {
          const component = resultData.components?.find(c => c.name === componentName);
          const unitCost = Number(component?.cost) || 0;
          const componentVolume = Number(volume) || 0;
          const componentTotalCost = componentVolume * unitCost;
          return total + componentTotalCost;
        }, 0);
      } else {
        calculatedTotalCost = Number(resultData.totalCost) || 0;
      }

      const componentVisualizations = [];

      if (chartData && chartData.length > 0) {
        const totalVolume = Object.values(resultData.blend || {}).reduce((a, b) => a + Number(b || 0), 0);
        const percentageVisualization = {
          visualizationType: "Percentage Distribution",
          componentPercentage: chartData.map(item => ({
            name: item.name,
            percentage: totalVolume > 0 ? (item.volume / totalVolume) * 100 : 0
          }))
        };
        componentVisualizations.push(percentageVisualization);
      }

      if (chartData && chartData.length > 0) {
        const volumeVisualization = {
          visualizationType: "Volume Distribution",
          componentVolume: chartData.map(item => ({
            name: item.name,
            volume: Number(item.volume) || 0,
            volumeUnit: getCurrentUnit()
          }))
        };
        componentVisualizations.push(volumeVisualization);
      }

      const currentPropertyResults = propertyResultsForDisplay;

      const componentPropertyValues = (resultData.components || []).map((comp) => {
        const propertyValues = {};
        const fixedFields = ['name', 'originalName', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type', '_id', 'id'];
        
        if (selectedPropertyColumns && Array.isArray(selectedPropertyColumns)) {
          selectedPropertyColumns.forEach(propName => {
            if (propName && !fixedFields.includes(propName.toLowerCase()) && comp[propName] !== undefined && comp[propName] !== null) {
              propertyValues[propName] = Number(comp[propName]) || 0;
            }
          });
        }
        
        Object.keys(comp).forEach(key => {
          if (!fixedFields.includes(key.toLowerCase()) && comp[key] !== undefined && comp[key] !== null && !propertyValues.hasOwnProperty(key)) {
            propertyValues[key] = Number(comp[key]) || 0;
          }
        });
        
        return {
          componentName: comp.name || 'Unknown Component',
          propertyValues: propertyValues
        };
      }).filter(cpv => Object.keys(cpv.propertyValues).length > 0);

      const apiPayload = {
        optimizeName: resultData.customTextField?.trim() || 'Optimization',
        totalBlendVolume: Object.values(resultData.blend || {}).reduce((a, b) => a + Number(b || 0), 0),
        totalCost: calculatedTotalCost,
        status: resultData.status || 'OPTIMAL',
        remarks: resultData.customTextField?.trim() || `Optimization - ${new Date().toLocaleString()}`,
        components: (resultData.components || []).map((comp, index) => {
          return {
            name: comp.name || `Component_${index + 1}`,
            originalName: comp.name || `Component_${index + 1}`,
            cost: Math.max(0, Number(comp.cost) || 0),
            costUnit: comp.costUnit || currency || 'USD',
            min: Math.max(0, Number(comp.min) || 0),
            max: Math.max(0, Number(comp.max) || 1000),
            availability: Math.max(0, Number(comp.availability) || 1000),
            volumeUnit: comp.volumeUnit || getCurrentUnit(),
            density: comp.density !== undefined && comp.density !== null && comp.density !== '' ? Number(comp.density) : null,
            gravity: Number(comp.gravity) || 0,
            basis: comp.basis || 'volume',
            blending_type: comp.blending_type || comp.blendingType || 'linear'
          };
        }),
        constraintsProperties: (resultData.propertySpecs || []).map((prop, index) => {
          const actualResult = currentPropertyResults[prop.property] || 0;
          
          return {
            name: prop.property || 'Unknown_Property',
            originalName: prop.property || 'Unknown_Property',
            property: prop.property || 'Unknown_Property',
            uom: prop.uom || 'units',
            min: Number(prop.min) || 0,
            max: Number(prop.max) || 100,
            basis: prop.basis || 'volume',
            linear: prop.linear !== undefined ? Boolean(prop.linear) : true,
            index_formula: String(prop.index_formula || ''),
            reverse_formula: String(prop.reverse_formula || ''),
            actualResult: Number(actualResult) || 0
          };
        }),
        blend: resultData.blend || {},
        costBreakdown: Array.isArray(resultData.costBreakdown) ? resultData.costBreakdown.map(item => ({
          name: String(item.name || 'Unknown'),
          volume: Number(item.volume) || 0,
          unitCost: Number(item.unitCost) || 0,
          totalCost: Number(item.totalCost) || 0
        })) : [],
        componentVisualizations: componentVisualizations,
        modelBasis: resultData.modelBasis || 'volume',
        currency: resultData.currency || 'USD',
        blendingType: resultData.blendingType || 'linear',
        selectedPropertyColumns: Array.isArray(resultData.selectedPropertyColumns) ? selectedPropertyColumns : [],
        componentPropertyValues: componentPropertyValues,
        createdBy: currentUser?.username || 'system'
      };

      console.log("Sending optimization data to API with property results:", currentPropertyResults);

      const response = await api.optimizedResults.create(apiPayload);

      console.log("Optimization API Response:", response);

      if (response.success) {
        if (resultData.propertySpecs && resultData.propertySpecs.length > 0) {
          console.log("Starting separate property save to MongoDB...");
          const propertySaveSuccess = await savePropertiesToAPI(resultData.propertySpecs);

          if (propertySaveSuccess) {
            addNotification("success", `Optimization result saved successfully with ${resultData.propertySpecs.length} properties and their results!`);
          } else {
            addNotification("success", `Optimization result saved successfully! (Properties already exist in database)`);
          }
        } else {
          addNotification("success", `Optimization result saved successfully!`);
        }

        return true;
      } else {
        throw new Error(response.message || 'Unknown API error');
      }
    } catch (error) {
      console.error('Error saving result to API:', error);
      addNotification("error", `Failed to save: ${error.message}`);
      return false;
    }
  };

  const SaveAsPopup = () => {
    if (!showSaveAsPopup) return null;

    const isEditMode = editingResult !== null || editingComponent !== null || editingProperty !== null;
    let originalName = '';
    let itemType = '';
    
    if (editingResult) {
      originalName = editingResult.customName || editingResult.customTextField;
      itemType = 'optimization';
    } else if (editingComponent) {
      originalName = editingComponent.customName;
      itemType = 'component set';
    } else if (editingProperty) {
      originalName = editingProperty.customName;
      itemType = 'property set';
    }

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className={`rounded-t-lg p-6 text-white ${isEditMode ? 'bg-gradient-to-r from-gray-500 to-orange-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                {isEditMode ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {isEditMode ? `Edit ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}` : 'Save As New Optimization'}
                </h2>
                <p className="text-opacity-100 text-sm mt-1">
                  {isEditMode 
                    ? `Editing: ${originalName}`
                    : 'Enter a name to save as a new optimization'}
                </p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="save-as-name" className="block text-sm font-medium text-gray-700 mb-2">
                  {isEditMode ? 'Updated Name *' : 'Save As Name *'}
                </label>
                <input
                  id="save-as-name"
                  type="text"
                  value={saveAsName}
                  onChange={(e) => setSaveAsName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-base"
                  placeholder={isEditMode ? "Enter updated name..." : "Enter a name for your new optimization..."}
                  required
                  autoFocus
                />
                {!saveAsName?.trim() && <p className="text-red-500 text-xs mt-1">Name is required</p>}
              </div>
              
              {isEditMode && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-slate-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1 -18 0 9 9 0 0 1 18 0z" />
                    </svg>
                    <span className="text-slate-600 text-sm">
                      {`Editing existing ${itemType}. Changes will update the original item.`}
                    </span>
                  </div>
                </div>
              )}
              
              {!apiConnected && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-slate-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-slate-600 text-sm">Backend not connected. Data will be saved locally only.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="bg-gray-50 rounded-b-lg px-6 py-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleSaveAsCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAsConfirm}
              disabled={!saveAsName.trim() || loadingSaveAll}
              className={`px-6 py-2 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${
                isEditMode 
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600' 
                  : 'bg-gradient-to-r from-blue-600 to-blue-700'
              }`}
            >
              {loadingSaveAll ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isEditMode ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    )}
                  </svg>
                  {isEditMode ? `Update ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}` : 'Save As New'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const savePropertiesToAPI = async (propertiesData) => {
    try {
      console.log("Starting property save to MongoDB...");

      if (!apiConnected) {
        console.log("API not connected, cannot save properties to database");
        return false;
      }

      let savedCount = 0;
      let skippedCount = 0;

      for (const property of propertiesData) {
        try {
          const existingProperties = await api.globalProperties.getAll();
          const propertyExists = existingProperties.data?.some(
            existingProp =>
              existingProp.property === property.property &&
              existingProp.uom === property.uom
          );

          if (propertyExists) {
            console.log(`Property ${property.property} with UOM ${property.uom} already exists, skipping`);
            skippedCount++;
            continue;
          }

          const apiProperty = {
            property: property.property || 'Unknown Property',
            uom: property.uom || 'units',
            basis: property.basis || 'volume',
            linear: property.linear !== undefined ? Boolean(property.linear) : true,
            min: Number(property.min) || null,
            max: Number(property.max) || null,
            description: property.description || '',
            index_formula: property.index_formula || '',
            reverse_formula: property.reverse_formula || ''
          };

          console.log("📤 Saving property to MongoDB:", apiProperty);

          const response = await api.globalProperties.create(apiProperty);

          if (response.success) {
            savedCount++;
            console.log(`Property saved: ${property.property}`);
          } else {
            console.log(`Failed to save property ${property.property}:`, response.message);
          }
        } catch (propertyError) {
          console.error(`Error saving property ${property.property}:`, propertyError);
        }
      }

      if (savedCount > 0) {
        console.log(`Successfully saved ${savedCount} properties to MongoDB`);
        addNotification("success", `${savedCount} properties saved to database${skippedCount > 0 ? `, ${skippedCount} skipped (already exist)` : ''}`);
        return true;
      } else if (skippedCount > 0) {
        console.log(`All properties already exist in database`);
        addNotification("info", `All properties already exist in database`);
        return true;
      } else {
        console.log("No properties were saved");
        return false;
      }
    } catch (error) {
      console.error('Error in property save process:', error);
      addNotification("error", `Failed to save properties: ${error.message}`);
      return false;
    }
  };

 
  const saveAllComponentsToMongoDB = async (components) => {
  if (!apiConnected || !components || components.length === 0) {
    console.log("API not connected or no components to save");
    return 0;
  }

  let savedCount = 0;
  let skippedCount = 0;
  let errorCount = 0;

  console.log(`Starting to save ${components.length} components to MongoDB...`);

  for (const [index, component] of components.entries()) {
    try {
      const existingComponents = await api.components.getAll();
      const componentExists = existingComponents.data?.some(
        existingComp => existingComp.name === component.name
      );

      if (componentExists) {
        console.log(`Component "${component.name}" already exists, skipping`);
        skippedCount++;
        continue;
      }

      const apiComponent = {
        name: component.name || `Component_${Date.now()}_${index}`,
        cost: Number(component.cost) || 0,
        costUnit: currency,
        minVolume: Number(component.min) || 0,
        maxVolume: Number(component.max) || 1000,
        availableVolume: Number(component.availability) || 1000,
        volumeUnit: getCurrentUnit(),
        density: Number(component.density) || 0, 
        otherProductList: [],
        selectedProperties: [],
        selectedGroups: [],
        selectedProperty: "",
        source: "app-optimizer"
      };

      const allProperties = new Set();

      if (selectedPropertyColumns && Array.isArray(selectedPropertyColumns)) {
        selectedPropertyColumns.forEach(prop => {
          if (prop && prop.trim() !== '' && prop.toLowerCase() !== 'density') {
            allProperties.add(prop.trim());
          }
        });
      }

      if (customColumns && Array.isArray(customColumns)) {
        customColumns.forEach(col => {
          if (col && col.trim() !== '' && col.toLowerCase() !== 'density') {
            allProperties.add(col.trim());
          }
        });
      }
      
      Object.keys(component).forEach(key => {
        const fixedFields = ['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type', 'selectedpropertycolumns'];
        if (!fixedFields.includes(key.toLowerCase()) &&
          component[key] !== undefined &&
          component[key] !== null &&
          component[key] !== '') {
          allProperties.add(key);
        }
      });

      const allPropertiesArray = Array.from(allProperties);

      allPropertiesArray.forEach(prop => {
        const shouldAddToSelectedProperties = prop.toLowerCase() !== 'gravity' && prop.toLowerCase() !== 'density';
        const propValue = component[prop] !== undefined && component[prop] !== null && component[prop] !== ''
          ? Number(component[prop]) || 0
          : 0;

        apiComponent.otherProductList.push({
          name: prop,
          value: propValue
        });
        if (shouldAddToSelectedProperties) {
          apiComponent.selectedProperties.push({
            name: prop,
            value: propValue,
            uom: getPropertyUOM(prop) || '',
            minValue: 0,
            maxValue: 100,
            defaultValue: propValue
          });
        }
      });

      const fixedProperties = ['density'];
      fixedProperties.forEach(prop => {
        if (component[prop] !== undefined && component[prop] !== null && component[prop] !== '') {
          const propValue = Number(component[prop]) || 0;

          const alreadyExists = apiComponent.otherProductList.some(item => item.name === prop);
          if (!alreadyExists) {
            apiComponent.otherProductList.push({
              name: prop,
              value: propValue
            });
          }

          const alreadyInSelected = apiComponent.selectedProperties.some(item => item.name === prop);
          if (!alreadyInSelected) {
            apiComponent.selectedProperties.push({
              name: prop,
              value: propValue,
              uom: prop === 'density' ? 'g/cm³' : 'API',
              minValue: 0,
              maxValue: prop === 'density' ? 2 : 100,
              defaultValue: propValue
            });
          }
        }
      });

      if (component.gravity !== undefined && component.gravity !== null && component.gravity !== '') {
        const gravityValue = Number(component.gravity) || 0;

        const gravityExists = apiComponent.otherProductList.some(item => item.name === 'gravity');
        if (!gravityExists) {
          apiComponent.otherProductList.push({
            name: 'gravity',
            value: gravityValue
          });
        }
      }

      console.log(`Saving component to MongoDB: ${apiComponent.name}`, {
        propertiesCount: apiComponent.otherProductList.length,
        properties: apiComponent.otherProductList.map(p => p.name)
      });

      const response = await api.components.create(apiComponent);
      if (response.success) {
        savedCount++;
        console.log(`Component saved: ${apiComponent.name} with ${apiComponent.otherProductList.length} properties`);
      } else {
        console.log(`Failed to save component ${apiComponent.name}:`, response.message);
        errorCount++;
      }

      await new Promise(resolve => setTimeout(resolve, 100));

    } catch (error) {
      console.error(`Error saving component ${component.name}:`, error);
      errorCount++;
    }
  }

  if (savedCount > 0) {
    addNotification("success", `${savedCount} components saved to MongoDB with all properties${skippedCount > 0 ? `, ${skippedCount} skipped (already exist)` : ''}${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
  } else if (skippedCount > 0) {
    addNotification("info", `All ${skippedCount} components already exist in database`);
  } else if (errorCount > 0) {
    addNotification("error", `Failed to save ${errorCount} components to MongoDB`);
  }

  console.log(`Save Summary: ${savedCount} saved, ${skippedCount} skipped, ${errorCount} errors`);
  return savedCount;
};
  
  
  const downloadTemplate = async () => {
    setLoadingDownloadTemplate(true);
    try {
      const response = await fetch(`${apiBasepy}/template.xlsx`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "blend_optimizer_template.xlsx";
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);

      addNotification("success", "Template downloaded successfully");
    } catch (error) {
      console.error("Download template error:", error);
      addNotification("error", `Failed to download template: ${error.message}`);

      try {
        const a = document.createElement("a");
        a.href = `${apiBasepy}/template.xlsx`;
        a.download = "blend_optimizer_template.xlsx";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (fallbackError) {
        console.error("Fallback download failed:", fallbackError);
      }
    } finally {
      setLoadingDownloadTemplate(false);
    }
  };

  const downloadReport = () => {
    if (!reportB64) {
      addNotification("error", "No report available to download");
      return;
    }

    setLoadingDownloadReport(true);
    try {
      addNotification("info", "Preparing report download...");

      const cleanBase64 = reportB64.replace(/\s/g, '');

      const byteCharacters = atob(cleanBase64);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `blend_report_${new Date().getTime()}.xlsx`;
      a.style.display = "none";

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);

      addNotification("success", "Report downloaded successfully");
    } catch (error) {
      console.error("Download report error:", error);
      addNotification("error", `Failed to download report: ${error.message}`);

      try {
        const a = document.createElement("a");
        a.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${reportB64}`;
        a.download = `blend_report_${new Date().getTime()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (fallbackError) {
        console.error("Fallback download failed:", fallbackError);
      }
    } finally {
      setLoadingDownloadReport(false);
    }
  };

  // User Management
  const handleLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem('blendOptimizerCurrentUser', JSON.stringify(user));
    addNotification("success", `Welcome back, ${user.username}!`);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('blendOptimizerCurrentUser');
    setCurrentUser(null);
    setShowUserManagement(false);
    setShowLogoutConfirm(false);
    addNotification("info", "You have been logged out");
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
    setShowUserManagement(false);

    const pageStates = {
      components: setShowComponentsPage,
      properties: setShowPropertiesPage,
      'optimization-results': setShowOptimizationResultsPage,
      'model-basis': setShowModelBasisPage
    };

    Object.values(pageStates).forEach(setter => setter(false));
    if (pageStates[tab]) pageStates[tab](true);
    
    if (tab === 'optimizer') {
      const editData = localStorage.getItem('blendOptimizerEditData');
      if (editData) {
        try {
          const parsedData = JSON.parse(editData);
          loadEditData(parsedData);
          
          setTimeout(() => {
            localStorage.removeItem('blendOptimizerEditData');
            localStorage.removeItem('componentsToEdit');
            localStorage.removeItem('propertiesToEdit');
          }, 1000);
        } catch (error) {
          console.error('Error loading edit data:', error);
          addNotification("error", "Failed to load edit data");
          localStorage.removeItem('blendOptimizerEditData');
          localStorage.removeItem('componentsToEdit');
          localStorage.removeItem('propertiesToEdit');
        }
      }
    }
  };

  const handleToggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const handleShowUserManagement = () => {
    setShowUserManagement(true);
    [setShowComponentsPage, setShowPropertiesPage,
      setShowOptimizationResultsPage, setShowModelBasisPage].forEach(setter => setter(false));
  };

  const handleBlendingTypeChange = (e) => {
    const newType = e.target.value;
    setBlendingType(newType);
    localStorage.setItem('blendOptimizerBlendingType', newType);

    if (propertySpecs.length > 0) {
      setPropertySpecs(prev => prev.map(prop => ({
        ...prop,
        blending_type: newType,
        index_formula: newType === 'non-linear' ? indexFormula : "",
        reverse_formula: newType === 'non-linear' ? reverseFormula : ""
      })));
    }

    addNotification("success", `Blending type changed to ${newType === 'linear' ? 'Linear' : 'Non-Linear'}`);
  };

  const handleIndexFormulaChange = (e) => {
    const newFormula = e.target.value;
    setIndexFormula(newFormula);
    localStorage.setItem('blendOptimizerIndexFormula', newFormula);

    if (blendingType === 'non-linear' && propertySpecs.length > 0) {
      setPropertySpecs(prev => prev.map(prop => ({
        ...prop,
        index_formula: newFormula
      })));
    }
  };

  const handleReverseFormulaChange = (e) => {
    const newFormula = e.target.value;
    setReverseFormula(newFormula);
    localStorage.setItem('blendOptimizerReverseFormula', newFormula);

    if (blendingType === 'non-linear' && propertySpecs.length > 0) {
      setPropertySpecs(prev => prev.map(prop => ({
        ...prop,
        reverse_formula: newFormula
      })));
    }
  };

  const handleComponentBasisChange = (e) => {
    const newBasis = e.target.value;
    setComponentBasis(newBasis);
    localStorage.setItem('blendOptimizerComponentBasis', newBasis);

    setComponents(prev => prev.map(comp => ({
      ...comp,
      basis: newBasis
    })));

    addNotification("success", `Component basis changed to ${newBasis}`);
  };

  const handleComponentBlendingTypeChange = (e) => {
    const newBlendingType = e.target.value;
    setComponentBlendingType(newBlendingType);
    localStorage.setItem('blendOptimizerComponentBlendingType', newBlendingType);

    setComponents(prev => prev.map(comp => ({
      ...comp,
      blending_type: newBlendingType
    })));

    addNotification("success", `Component blending type changed to ${newBlendingType === 'linear' ? 'Linear' : 'Non-Linear'}`);
  };

  // Render Modals
  const AddPropertyModal = () => {
    if (!showAddPropertyPopup) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              {editingIndex !== null ? 'Edit Property' : 'Add New Property'}
            </h2>
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Name *
                </label>
                <input
                  type="text"
                  value={newProperty.property}
                  onChange={(e) => handlePropertyChange('property', e.target.value)}
                  placeholder="e.g., RON, Density, Viscosity"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UOM Name *
                </label>
                <input
                  type="text"
                  value={newProperty.uom}
                  onChange={(e) => handlePropertyChange('uom', e.target.value)}
                  placeholder="e.g., %, ppm, mg/L"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Value
                </label>
                <input
                  type="number"
                  value={newProperty.min}
                  onChange={(e) => handlePropertyChange('min', e.target.value)}
                  placeholder="e.g., 0, 10, 50"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Value
                </label>
                <input
                  type="number"
                  value={newProperty.max}
                  onChange={(e) => handlePropertyChange('max', e.target.value)}
                  placeholder="e.g., 100, 500, 1000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleClosePopup}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProperty}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {editingIndex !== null ? 'Update Property' : 'Save Property'}
              </button>
            </div>  
          </div>
        </div>
      </div>
    );
  };

  const GlobalPropertiesModal = () => {
    if (!showGlobalPropertiesModal) return null;

    const handleTableClick = (e) => {
      if (e.target.type === 'checkbox') {
        e.stopPropagation();
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold">Global Properties Report</h2>
                  <p className="text-purple-100 text-sm mt-1">Select properties to add to your current constraints</p>
                </div>
              </div>
              <button
                onClick={closeGlobalPropertiesModal}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div 
            ref={globalPropertiesModalScrollRef}
            className="p-4 overflow-y-auto flex-1"
            style={{ scrollBehavior: 'auto' }}
            onClick={(e) => {
              if (e.target.type === 'checkbox') {
                e.stopPropagation();
              }
            }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <span className="text-sm text-gray-600">
                {globalProperties.length} properties found • {selectedGlobalProperties.filter(Boolean).length} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const scrollTop = globalPropertiesModalScrollRef.current?.scrollTop || 0;
                    selectAllGlobalProperties();
                    setTimeout(() => {
                      if (globalPropertiesModalScrollRef.current) {
                        globalPropertiesModalScrollRef.current.scrollTop = scrollTop;
                      }
                    }, 0);
                  }}
                  className="px-3 py-1 text-purple-600 hover:text-purple-800 font-medium text-sm border border-purple-200 rounded-lg hover:bg-purple-50"
                >
                  Select All
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const scrollTop = globalPropertiesModalScrollRef.current?.scrollTop || 0;
                    deselectAllGlobalProperties();
                    setTimeout(() => {
                      if (globalPropertiesModalScrollRef.current) {
                        globalPropertiesModalScrollRef.current.scrollTop = scrollTop;
                      }
                    }, 0);
                  }}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 font-medium text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="w-12 px-4 py-3 text-left bg-gray-50">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                        checked={selectedGlobalProperties && selectedGlobalProperties.length > 0 && selectedGlobalProperties.every(val => Boolean(val))}
                        onChange={(e) => {
                          const scrollTop = globalPropertiesModalScrollRef.current?.scrollTop || 0;
                          if (e.target.checked) {
                            selectAllGlobalProperties();
                          } else {
                            deselectAllGlobalProperties();
                          }
                          setTimeout(() => {
                            if (globalPropertiesModalScrollRef.current) {
                              globalPropertiesModalScrollRef.current.scrollTop = scrollTop;
                            }
                          }, 0);
                        }}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Property
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      UOM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Blending Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Min
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Max
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {globalProperties && globalProperties.map((property, index) => (
                    <tr key={property.property || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500"
                          checked={selectedGlobalProperties && selectedGlobalProperties[index] || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            const scrollTop = globalPropertiesModalScrollRef.current?.scrollTop || 0;
                            toggleGlobalPropertySelection(index);
                            setTimeout(() => {
                              if (globalPropertiesModalScrollRef.current) {
                                globalPropertiesModalScrollRef.current.scrollTop = scrollTop;
                              }
                            }, 0);
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {property.property}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {property.uom || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${property.linear ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {property.linear ? 'Linear' : 'Non-Linear'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {property.min || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {property.max || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={closeGlobalPropertiesModal}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSelectedGlobalProperties}
              disabled={selectedGlobalProperties.filter(Boolean).length === 0 || loadingGlobalProperties}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loadingGlobalProperties ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                `Add Selected (${selectedGlobalProperties.filter(Boolean).length})`
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PropertyColumnModal = () => {
    if (!showPropertyColumnModal) return null;

    const handleTableClick = (e) => {
      if (e.target.type === 'checkbox') {
        e.stopPropagation();
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-3xl xs:max-w-xl sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold">Add Property Columns</h2>
                  <p className="text-purple-100 text-sm mt-1">Select properties to add as columns in components table</p>
                </div>
              </div>
              <button
                onClick={closePropertyColumnModal}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div 
            ref={propertyColumnModalScrollRef}
            className="p-4 overflow-y-auto flex-1"
            style={{ scrollBehavior: 'auto' }}
            onClick={(e) => {
              if (e.target.type === 'checkbox') {
                e.stopPropagation();
              }
            }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <span className="text-sm text-gray-600">
                {globalProperties.length} properties found • {selectedGlobalProperties.filter(Boolean).length} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const scrollTop = propertyColumnModalScrollRef.current?.scrollTop || 0;
                    selectAllGlobalProperties();
                    setTimeout(() => {
                      if (propertyColumnModalScrollRef.current) {
                        propertyColumnModalScrollRef.current.scrollTop = scrollTop;
                      }
                    }, 0);
                  }}
                  className="px-3 py-1 text-purple-600 hover:text-purple-800 font-medium text-sm border border-purple-200 rounded-lg hover:bg-purple-50"
                >
                  Select All
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const scrollTop = propertyColumnModalScrollRef.current?.scrollTop || 0;
                    deselectAllGlobalProperties();
                    setTimeout(() => {
                      if (propertyColumnModalScrollRef.current) {
                        propertyColumnModalScrollRef.current.scrollTop = scrollTop;
                      }
                    }, 0);
                  }}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 font-medium text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="overflow-x-auto" onClick={handleTableClick}>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="w-12 px-4 py-3 text-left bg-gray-50">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                        checked={selectedGlobalProperties && selectedGlobalProperties.length > 0 && selectedGlobalProperties.every(val => Boolean(val))}
                        onChange={(e) => {
                          e.stopPropagation();
                          const scrollTop = propertyColumnModalScrollRef.current?.scrollTop || 0;
                          if (e.target.checked) {
                            selectAllGlobalProperties();
                          } else {
                            deselectAllGlobalProperties();
                          }
                          setTimeout(() => {
                            if (propertyColumnModalScrollRef.current) {
                              propertyColumnModalScrollRef.current.scrollTop = scrollTop;
                            }
                          }, 0);
                        }}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Property
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      UOM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Blending Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Min
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                      Max
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {globalProperties && globalProperties.map((property, index) => (
                    <tr key={property.property || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500"
                          checked={selectedGlobalProperties && selectedGlobalProperties[index] || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            const scrollTop = propertyColumnModalScrollRef.current?.scrollTop || 0;
                            toggleGlobalPropertySelectionFixed(index);
                            setTimeout(() => {
                              if (propertyColumnModalScrollRef.current) {
                                propertyColumnModalScrollRef.current.scrollTop = scrollTop;
                              }
                            }, 0);
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {property.property}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {property.uom || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${property.linear ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {property.linear ? 'Linear' : 'Non-Linear'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {property.min || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {property.max || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={closePropertyColumnModal}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSelectedPropertyColumns}
              disabled={selectedGlobalProperties.filter(Boolean).length === 0 || loadingPropertyColumn}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loadingPropertyColumn ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                `Add Selected (${selectedGlobalProperties.filter(Boolean).length})`
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const BlendComponentsModal = () => {
    if (!showBlendComponentsModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-3xl xs:max-w-xl sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold">Add Components from Global Report</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    Select components from the global report to add to your current blend
                    {loadingAddComponent && <span className="ml-2">Loading...</span>}
                  </p>
                </div>
              </div>
              <button
                onClick={closeBlendComponentsModal}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div 
            ref={globalPropertiesModalScrollRef}
            className="p-4 overflow-y-auto flex-1"
            style={{ scrollBehavior: 'auto' }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <span className="text-sm text-gray-600">
                {blendComponentsModalData.length} components found • {selectedBlendComponents.filter(Boolean).length} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={selectAllBlendComponents}
                  className="px-3 py-1 text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-200 rounded-lg hover:bg-blue-50"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllBlendComponents}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 font-medium text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mr-2"
                          checked={selectedBlendComponents && selectedBlendComponents.length > 0 && selectedBlendComponents.every(val => Boolean(val))}
                          onChange={(e) => e.target.checked ? selectAllBlendComponents() : deselectAllBlendComponents()}
                        />
                        Component
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Min Volume
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Max Volume
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Available Volume
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {blendComponentsModalData && blendComponentsModalData.map((component, index) => (
                    <tr key={component.id || component._id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 mr-2"
                            checked={selectedBlendComponents[index] || false}
                            onChange={() => toggleBlendComponentSelection(index)}
                          />
                          {component.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {component.cost && !isNaN(Number(component.cost)) ? getCurrencySymbol(component.costUnit) + Number(component.cost).toFixed(2) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {component.minVolume || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {component.maxVolume || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {component.availableVolume || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={closeBlendComponentsModal}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSelectedBlendComponents}
              disabled={selectedBlendComponents.filter(Boolean).length === 0 || loadingAddComponent}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loadingAddComponent ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                `Add Selected (${selectedBlendComponents.filter(Boolean).length})`
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const SaveAllPopup = () => {
    if (!showSaveAllPopup) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-t-lg p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">Save Optimization Data</h2>
                <p className="text-green-100 text-sm mt-1">Enter a name to save your optimization results</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="save-all-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Save As Name *
                </label>
                <input
                  ref={saveAllNameInputRef}
                  id="save-all-name"
                  type="text"
                  value={saveAllName}
                  onChange={(e) => setSaveAllName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-white text-base"
                  placeholder="Enter a name for your optimization data..."
                  required
                  autoFocus
                />
                {!saveAllName?.trim() && <p className="text-red-500 text-xs mt-1">Name is required</p>}
              </div>
              {!apiConnected && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-slate-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-slate-600 text-sm">Backend not connected. Data will be saved locally only.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="bg-gray-50 rounded-b-lg px-6 py-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleSaveAllCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAllConfirm}
              disabled={!saveAllName.trim() || loadingSaveAll}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loadingSaveAll ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save All Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LogoutConfirmModal = () => {
    if (!showLogoutConfirm) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-sm w-full mx-4">
          <div className="bg-gradient-to-r from-red-500 to-orange-600 rounded-t-lg p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">Confirm Logout</h2>
                <p className="text-red-100 text-sm mt-1">Are you sure you want to logout?</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center space-x-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
              <svg className="w-5 h-5 text-slate-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-slate-700">
                Any unsaved changes will be lost. Make sure to save your work before logging out.
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-b-lg px-6 py-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={cancelLogout}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              className="px-6 py-2 bg-gradient-to-r from-red-600 to-orange-700 text-white rounded-lg font-semibold hover:from-red-700 hover:to-orange-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  };

  // PropertyChip Component
  const PropertyChip = ({ property, onRemove }) => (
    <div className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
      <span>{property}</span>
      <button
        onClick={onRemove}
        className="text-blue-600 hover:text-blue-800 focus:outline-none"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  if (!currentUser) return <Login onLogin={handleLogin} users={users} setUsers={setUsers} />;
  if (showUserManagement) {
    return (<UserManagement users={users} setUsers={setUsers} currentUser={currentUser} onLogout={handleLogoutConfirm} onShowUserManagement={handleShowUserManagement} onTabChange={handleTabChange} activeTab={activeTab} sidebarOpen={sidebarOpen} onToggleSidebar={handleToggleSidebar} />
    );
  }
  if (showComponentsPage) return <ComponentsPage currentUser={currentUser} onLogout={handleLogoutConfirm} onShowUserManagement={handleShowUserManagement} onTabChange={handleTabChange} activeTab={activeTab} sidebarOpen={sidebarOpen} onToggleSidebar={handleToggleSidebar} />;
  if (showPropertiesPage) return <PropertiesPage currentUser={currentUser} onLogout={handleLogoutConfirm} onShowUserManagement={handleShowUserManagement} onTabChange={handleTabChange} activeTab={activeTab} sidebarOpen={sidebarOpen} onToggleSidebar={handleToggleSidebar} />;
  if (showOptimizationResultsPage) return <OptimizationResultsPage currentUser={currentUser} onLogout={handleLogoutConfirm} onShowUserManagement={handleShowUserManagement} onTabChange={handleTabChange} activeTab={activeTab} sidebarOpen={sidebarOpen} onToggleSidebar={handleToggleSidebar} />;
  if (showModelBasisPage) return <ModelBasisPage currentUser={currentUser} onLogout={handleLogoutConfirm} onShowUserManagement={handleShowUserManagement} onTabChange={handleTabChange} activeTab={activeTab} sidebarOpen={sidebarOpen} onToggleSidebar={handleToggleSidebar} />;

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <NotificationToaster notifications={notifications} />
      <SaveAllPopup />
      <SaveAsPopup />
      <AddPropertyModal />
      <BlendComponentsModal />
      <PropertyColumnModal />
      <GlobalPropertiesModal />

      {/* Rename Model Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">Rename Model</h3>
              <p className="text-sm text-gray-500 mt-0.5">Enter a new name for this model</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Model name..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameValue.trim()) {
                    setModelName(renameValue.trim());
                    setShowRenameModal(false);
                    addNotification('success', `Model renamed to "${renameValue.trim()}"`);
                  }
                }}
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button onClick={() => setShowRenameModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={() => {
                  if (renameValue.trim()) {
                    setModelName(renameValue.trim());
                    setShowRenameModal(false);
                    addNotification('success', `Model renamed to "${renameValue.trim()}"`);
                  }
                }}
                disabled={!renameValue.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      <LogoutConfirmModal />

      <Navigation 
        currentUser={currentUser} 
        activeTab={activeTab} 
        onTabChange={handleTabChange} 
        onLogout={handleLogoutConfirm} 
        onShowUserManagement={handleShowUserManagement} 
        sidebarOpen={sidebarOpen} 
        onToggleSidebar={handleToggleSidebar} 
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
        <Header 
          currentUser={currentUser} 
          onToggleSidebar={handleToggleSidebar}
        />

        <main className="flex-1 overflow-auto">
          {/* ── Sticky Top Action Bar ───────────────────────────────── */}
          <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm px-6 py-3">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              {/* Left: Model Name & Actions */}
              <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <div>
                    <h1 className="text-base font-bold text-gray-900 leading-tight">
                      {modelName || 'Untitled Model'}
                    </h1>
                    <div className="flex items-center gap-2">
                      {lastSavedTimestamp && (
                        <span className="text-[11px] text-gray-400">Saved {new Date(lastSavedTimestamp).toLocaleString()}</span>
                      )}

                    </div>
                  </div>
                </div>
              </div>

              {/* Center: Key Inputs */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 bg-white rounded-lg px-3.5 py-2 border border-gray-300 shadow-sm">
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>
                  <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">Total Blend Qty</label>
                  <input type="number" min={0} value={volume} onChange={(e) => setVolume(Number(e.target.value || 0))}
                    className="w-24 px-2 py-1 border border-gray-200 rounded-md text-sm text-right font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50" />
                  <span className="text-xs font-medium text-gray-500">{getCurrentUnit()}</span>
                </div>
                <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3.5 py-2 border border-blue-200 shadow-sm">
                  <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  <label className="text-xs font-semibold text-blue-700">Basis</label>
                  <span className="text-xs font-bold text-blue-800 bg-blue-100 px-2.5 py-0.5 rounded-md uppercase tracking-wide">{modelBasis}</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3.5 py-2 border border-gray-300 shadow-sm">
                  <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">Infeasibility Breaker</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={autoRelax} onChange={(e) => setAutoRelax(e.target.checked)} />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-4 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all shadow-inner"></div>
                  </label>
                </div>
              </div>

              {/* Right: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Model Menu */}
                <div className="relative">
                  <button onClick={() => setShowModelMenu(!showModelMenu)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    File
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showModelMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowModelMenu(false)}></div>
                      <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-200 z-50 py-1.5 overflow-hidden">
                        <button onClick={() => { handleNewModel(); setShowModelMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 transition-colors">
                          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          </div>
                          <span className="font-medium">New Model</span>
                        </button>
                        <button onClick={() => { handleOpenModel(); setShowModelMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 transition-colors">
                          <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                          </div>
                          <span className="font-medium">Open Model</span>
                        </button>
                        <div className="border-t border-gray-100 my-1.5 mx-3" />
                        <button onClick={() => { handleSaveClick(); setShowModelMenu(false); }}
                          disabled={!volume || loadingSaveAll}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${currentModelId ? 'bg-green-100' : 'bg-gray-100'}`}>
                            <svg className={`w-4 h-4 ${currentModelId ? 'text-green-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                          </div>
                          <div className="flex-1 text-left">
                            <span className="font-medium">Save</span>
                            {!currentModelId && <span className="block text-xs text-gray-400">Creates new model</span>}
                          </div>
                          {loadingSaveAll && <svg className="animate-spin h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>}
                        </button>
                        <button onClick={() => { handleSaveAllClick(); setShowModelMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 transition-colors">
                          <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          </div>
                          <div className="flex-1 text-left">
                            <span className="font-medium">Save As</span>
                            <span className="block text-xs text-gray-400">Creates a copy</span>
                          </div>
                        </button>
                        <div className="border-t border-gray-100 my-1.5 mx-3" />
                        <button onClick={() => { setRenameValue(modelName); setShowRenameModal(true); setShowModelMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 transition-colors">
                          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </div>
                          <span className="font-medium">Rename Model</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={runManualOptimization}
                  disabled={loadingOptimization || components.length === 0 || !volume || hasBlockingErrors}
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-purple-600 to-blue-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg text-sm"
                >
                  {loadingOptimization ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Running...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      Run Optimization
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Validation Errors Bar */}
            {validationErrors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {validationErrors.map((err, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                    err.severity === 'warning' ? 'bg-amber-100 text-slate-700 border border-amber-200' : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {err.severity === 'warning' ? '⚠' : '✗'} {err.message}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="p-6">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* Left Section - Optimization Summary */}
            <section className="xl:col-span-4 space-y-4">
              {/* Optimization Summary Card */}
              <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-50 rounded-lg">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                    </div>
                    Optimization Summary
                  </h2>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`rounded-xl p-4 border ${
                      status.toUpperCase() === "OPTIMAL" || status.toUpperCase() === "SUCCESS"
                        ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
                        : status.toUpperCase() === "RELAXED OPTIMAL"
                        ? "bg-gradient-to-br from-gray-50 to-amber-100 border-amber-300"
                        : status.toUpperCase() === "INFEASIBLE"
                        ? "bg-gradient-to-br from-red-50 to-red-100 border-red-200"
                        : status
                        ? "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200"
                        : "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200"
                    }`}>
                      <div className={`text-lg font-bold truncate ${
                        status.toUpperCase() === "OPTIMAL" || status.toUpperCase() === "SUCCESS"
                          ? "text-emerald-700"
                          : status.toUpperCase() === "RELAXED OPTIMAL"
                          ? "text-amber-700"
                          : status.toUpperCase() === "INFEASIBLE"
                          ? "text-red-700"
                          : status
                          ? "text-blue-700"
                          : "text-gray-500"
                      }`}>
                        {status || "—"}
                        {status.toUpperCase() === "RELAXED OPTIMAL" && (
                          <span className="ml-2 text-xs bg-amber-200 text-slate-700 px-2 py-0.5 rounded-full font-semibold">
                            ⚠ Relaxed
                          </span>
                        )}
                      </div>
                      <div className={`text-sm font-medium mt-1 ${
                        status.toUpperCase() === "RELAXED OPTIMAL" ? "text-amber-700" : "text-blue-700"
                      }`}>Status</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                      <div className="text-2xl font-bold text-green-800">
                        {totalVolume > 0
                          ? totalVolume.toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : (Object.values(blend).reduce((a, b) => a + Number(b || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-sm text-green-700 font-medium mt-1">Total Volume ({volumeUnit})</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                      <div className="text-2xl font-bold text-purple-800">{totalCost ? `${getCurrencySymbol()}${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</div>
                      <div className="text-sm text-purple-700 font-medium mt-1">Total Cost</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                      <div className="text-2xl font-bold text-orange-800">{totalCost && volume ? `${getCurrencySymbol()}${(totalCost / Number(volume)).toFixed(2)}` : "—"}</div>
                      <div className="text-sm text-orange-700 font-medium mt-1">Unit Cost ({getCostUnit()})</div>
                    </div>
                  </div>

                  {/* Total Mass/Weight Card — always shows mass in massUnit */}
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border border-indigo-200">
                    <div className="text-3xl font-bold text-indigo-800">
                      {weight > 0 ? `${weight.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${massUnit.toUpperCase()}` : "—"}
                    </div>
                    <div className="text-sm text-indigo-700 font-medium mt-2">
                      Total Mass ({massUnit.toUpperCase()})
                      {modelBasis === 'volume' && <span className="ml-1 text-indigo-500">= Vol × SG × ρ_water</span>}
                    </div>
                  </div>

                  {message && (
                    <div className={`border rounded-xl p-4 ${
                      status.toUpperCase() === "RELAXED OPTIMAL"
                        ? "bg-amber-50 border-amber-200"
                        : "bg-blue-50 border-blue-200"
                    }`}>
                      <div className="flex items-start">
                        <svg className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${status.toUpperCase() === "RELAXED OPTIMAL" ? "text-amber-500" : "text-blue-400"}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 1 1 -16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className={`text-sm font-medium break-words ${status.toUpperCase() === "RELAXED OPTIMAL" ? "text-slate-700" : "text-blue-700"}`}>{message}</span>
                      </div>
                    </div>
                  )}

                  {/* ── Violation / Infeasibility Report ─────────────────────────── */}
                  {Object.keys(violations).length > 0 && (
                    <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-slate-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h3 className="text-sm font-bold text-slate-700">
                          Constraint Violation Report
                          {infeasibilityDrivers.length > 0 && (
                            <span className="ml-2 text-xs bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-semibold">
                              {infeasibilityDrivers.length} driver{infeasibilityDrivers.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </h3>
                      </div>

                      {/* Cost breakdown row if penalty > 0 */}
                      {penaltyCost > 0 && (
                        <div className="flex items-center gap-3 mb-3 p-2 bg-white rounded-lg border border-amber-200 text-xs">
                          <div className="flex-1">
                            <span className="text-gray-500">Blend cost:</span>
                            <span className="ml-1 font-semibold text-gray-800">{getCurrencySymbol()}{blendCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="text-gray-300">+</div>
                          <div className="flex-1">
                            <span className="text-red-500">Penalty cost:</span>
                            <span className="ml-1 font-semibold text-red-700">{getCurrencySymbol()}{penaltyCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        {Object.entries(violations).map(([pname, v]) => (
                          <div key={pname} className={`rounded-lg p-3 border text-xs ${
                            v.infeasibility_driver
                              ? "bg-red-50 border-red-200"
                              : "bg-green-50 border-green-200"
                          }`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${v.infeasibility_driver ? "bg-red-500" : "bg-green-500"}`} />
                                <span className="font-bold text-gray-800 uppercase tracking-wide">{pname}</span>
                                {v.infeasibility_driver && (
                                  <span className="bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-xs font-semibold">
                                    {v.violation_side === "below_min" ? "↓ Below Min"
                                      : v.violation_side === "above_max" ? "↑ Above Max"
                                      : "⚠ Both Sides"}
                                  </span>
                                )}
                              </div>
                              {v.penalty_contribution > 0 && (
                                <span className="text-red-600 font-semibold">
                                  {getCurrencySymbol()}{v.penalty_contribution.toLocaleString(undefined, {maximumFractionDigits: 0})} penalty
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="bg-white rounded p-1.5 border border-gray-100">
                                <div className="text-gray-400 font-medium">Min</div>
                                <div className="font-semibold text-gray-700">{v.spec_min !== null ? v.spec_min : "—"}</div>
                              </div>
                              <div className={`rounded p-1.5 border font-bold ${
                                v.infeasibility_driver
                                  ? "bg-red-100 border-red-200 text-red-800"
                                  : "bg-green-100 border-green-200 text-green-800"
                              }`}>
                                <div className="text-xs font-medium opacity-70">Achieved</div>
                                <div>{v.achieved_value !== null ? Number(v.achieved_value).toFixed(3) : "—"}</div>
                              </div>
                              <div className="bg-white rounded p-1.5 border border-gray-100">
                                <div className="text-gray-400 font-medium">Max</div>
                                <div className="font-semibold text-gray-700">{v.spec_max !== null ? v.spec_max : "—"}</div>
                              </div>
                            </div>

                            {v.infeasibility_driver && (
                              <div className="mt-1.5 text-red-600 font-medium text-center">
                                {v.violation_below_min > 0 && `Short by ${v.violation_below_min.toFixed(3)} units`}
                                {v.violation_above_max > 0 && `Exceeds by ${v.violation_above_max.toFixed(3)} units`}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {status.toUpperCase() === "INFEASIBLE" && infeasibilityDrivers.length > 0 && (
                        <p className="mt-3 text-xs text-amber-700 bg-amber-100 rounded-lg p-2 border border-amber-200">
                          💡 <strong>Tip:</strong> Enable "Add Infeasibility Breaker" above and re-run to automatically accept
                          a relaxed solution that satisfies all other constraints while minimising property violations.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                  <button
                    onClick={downloadTemplate}
                    disabled={loadingDownloadTemplate}
                    className="flex-1 py-2.5 w-full px-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingDownloadTemplate ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span>Download Template</span>
                      </>
                    )}
                  </button>
                  {reportB64 && (
                    <button onClick={downloadReport} disabled={loadingDownloadReport}
                      className="w-full py-2 px-3 rounded-lg font-medium flex items-center justify-center gap-2 text-xs bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                      {loadingDownloadReport ? (
                        <><svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Downloading...</>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg> Download Report</>
                      )}
                    </button>
                  )}
                  {(Object.keys(blend).length > 0 || status) && (
                    <button onClick={downloadCSVPack}
                      className="w-full py-2 px-3 rounded-lg font-medium flex items-center justify-center gap-2 text-xs bg-indigo-600 text-white hover:bg-indigo-700 transition-all">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      CSV Download Pack
                    </button>
                  )}
                </div>
              </div>

              {/* Cost Breakdown */}
              {costBreakdown.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 7a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">Cost Breakdown</h2>
                        <p className="text-xs text-gray-500">{costBreakdown.length} component{costBreakdown.length !== 1 ? 's' : ''} · basis: {modelBasis}</p>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Component</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Volume ({volumeUnit})
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Mass ({massUnit.toUpperCase()})
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Vol %</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Wt %</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Unit Cost ({getCostUnit()})
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Total Cost ({getCurrencySymbol()})
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(() => {
                          const totalVolumeBreakdown = costBreakdown.reduce((s, i) => s + (i.volume || 0), 0);
                          const totalMassBreakdown = costBreakdown.reduce((s, i) => s + (i.mass || 0), 0);
                          const CHART_COLORS_BD = ['#2563eb','#16a34a','#ea580c','#9333ea','#0891b2','#dc2626','#ca8a04','#0d9488'];
                          return costBreakdown.map((item, index) => {
                            const volPct = totalVolumeBreakdown > 0 ? ((item.volume || 0) / totalVolumeBreakdown * 100) : 0;
                            const wtPct = totalMassBreakdown > 0 ? ((item.mass || 0) / totalMassBreakdown * 100) : 0;
                            return (
                              <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS_BD[index % CHART_COLORS_BD.length] }} />
                                    <span className="font-medium text-gray-900">{item.name}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-gray-700">
                                  {item.volume !== null && item.volume !== undefined ? item.volume.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '—'}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-gray-700">
                                  {item.mass !== null && item.mass !== undefined ? item.mass.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <div className="w-16 bg-gray-100 rounded-full h-1.5">
                                      <div className="h-1.5 rounded-full" style={{ width: `${volPct}%`, backgroundColor: CHART_COLORS_BD[index % CHART_COLORS_BD.length] }} />
                                    </div>
                                    <span className="text-xs font-medium text-gray-600 w-10 text-right">{volPct.toFixed(1)}%</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className="text-xs font-medium text-gray-600">{wtPct > 0 ? wtPct.toFixed(1) + '%' : '—'}</span>
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-gray-700">
                                  {getCurrencySymbol()}{item.unitCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>
                                <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">
                                  {getCurrencySymbol()}{item.totalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                        <tr>
                          <td className="px-4 py-3 font-bold text-gray-900">Total</td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">
                            {costBreakdown.reduce((s,i) => s + (i.volume||0), 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">
                            {weight > 0 ? weight.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) : costBreakdown.reduce((s,i) => s + (i.mass||0), 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">100%</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">100%</td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">
                            {getCurrencySymbol()}{costBreakdown.reduce((s,i) => s + (i.totalCost||0), 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Blend Visualization - Full Redesign */}
              {chartData.length > 0 && (() => {
                const CHART_COLORS = ['#2563eb','#16a34a','#ea580c','#9333ea','#0891b2','#dc2626','#ca8a04','#0d9488','#7c3aed','#be185d'];
                const totalChartVol = chartData.reduce((s, d) => s + (d.value || 0), 0);

                // Property radar data for analysis
                const propertyChartData = Object.entries(propertyResultsForDisplay)
                  .filter(([k, v]) => v !== undefined && !isNaN(Number(v)))
                  .map(([k, v]) => ({ name: k.toUpperCase(), value: Number(v) }))
                  .slice(0, 6);

                // Cost per unit analysis
                const costAnalysisData = costBreakdown
                  .filter(d => d.unitCost > 0)
                  .map(d => ({ name: d.name, cost: d.unitCost, share: totalChartVol > 0 ? (d.volume||0) / totalChartVol * 100 : 0 }));

                return (
                  <div className="space-y-4">
                    {/* Row 1: Composition Donut + Stacked % */}
                    <div className="grid grid-cols-1 gap-4">
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h2 className="text-base font-semibold text-gray-900">Blend Composition</h2>
                            <p className="text-xs text-gray-500">Volume distribution by component</p>
                          </div>
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">{volumeUnit}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                          {/* Donut */}
                          <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={chartData}
                                  cx="50%" cy="50%"
                                  innerRadius={55} outerRadius={85}
                                  paddingAngle={2}
                                  dataKey="value"
                                  labelLine={false}
                                  label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                                >
                                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Pie>
                                <Tooltip formatter={(v, n) => [`${v.toLocaleString(undefined,{maximumFractionDigits:1})} ${volumeUnit}`, n]} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          {/* Legend table */}
                          <div className="space-y-2">
                            {chartData.map((d, i) => {
                              const pct = totalChartVol > 0 ? d.value / totalChartVol * 100 : 0;
                              return (
                                <div key={i} className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                  <span className="text-sm text-gray-700 flex-1 truncate">{d.name}</span>
                                  <span className="text-xs font-mono font-medium text-gray-900">{d.value.toLocaleString(undefined,{maximumFractionDigits:1})}</span>
                                  <span className="text-xs font-medium text-gray-500 w-12 text-right">{pct.toFixed(1)}%</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Component Volumes Bar + Cost Analysis */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Volume Bar */}
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Component Volumes</h3>
                        <p className="text-xs text-gray-500 mb-4">{volumeUnit} per component</p>
                        <div className="h-52">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" interval={0} />
                              <YAxis tick={{ fontSize: 11 }} width={50} />
                              <Tooltip formatter={(v) => [`${v.toLocaleString(undefined,{maximumFractionDigits:1})} ${volumeUnit}`, 'Volume']} />
                              <Bar dataKey="value" radius={[4,4,0,0]}>
                                {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Cost per unit bar */}
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Unit Cost Comparison</h3>
                        <p className="text-xs text-gray-500 mb-4">{getCostUnit()} per component</p>
                        <div className="h-52">
                          {costAnalysisData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={costAnalysisData} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={70} />
                                <Tooltip formatter={(v) => [`${getCurrencySymbol()}${v.toFixed(2)}`, 'Unit Cost']} />
                                <Bar dataKey="cost" radius={[0,4,4,0]}>
                                  {costAnalysisData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full text-sm text-gray-400">No cost data</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Cumulative cost contribution + Property Results */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Cumulative cost waterfall */}
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Cost Contribution</h3>
                        <p className="text-xs text-gray-500 mb-4">Total cost share per component</p>
                        <div className="space-y-2.5">
                          {(() => {
                            const totalC = costBreakdown.reduce((s,i) => s + i.totalCost, 0);
                            return costBreakdown.map((item, i) => {
                              const pct = totalC > 0 ? item.totalCost / totalC * 100 : 0;
                              return (
                                <div key={i}>
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="font-medium text-gray-700 flex items-center gap-1.5">
                                      <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                      {item.name}
                                    </span>
                                    <span className="font-mono text-gray-600">{getCurrencySymbol()}{item.totalCost.toLocaleString(undefined,{maximumFractionDigits:0})} ({pct.toFixed(1)}%)</span>
                                  </div>
                                  <div className="w-full bg-gray-100 rounded-full h-2">
                                    <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Property Results Summary */}
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Blend Properties</h3>
                        <p className="text-xs text-gray-500 mb-4">Calculated blend property results</p>
                        {Object.keys(propertyResultsForDisplay).length > 0 ? (
                          <div className="space-y-2">
                            {Object.entries(propertyResultsForDisplay).slice(0, 8).map(([propKey, val], i) => {
                              const spec = propertySpecs.find(p => p.property && p.property.trim().toLowerCase() === propKey.toLowerCase());
                              const minV = spec ? Number(spec.min) : null;
                              const maxV = spec ? Number(spec.max) : null;
                              const inRange = minV !== null && maxV !== null ? (val >= minV && val <= maxV) : null;
                              return (
                                <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{propKey}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-mono font-semibold text-gray-900">{Number(val).toFixed(3)}</span>
                                    {inRange !== null && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${inRange ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {inRange ? '✓' : '✗'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-32 text-sm text-gray-400">
                            Run optimization to see property results
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            </section>

            {/* Right Section - Tabbed Cards */}
            <section className="xl:col-span-8 space-y-0">
              {/* Tab Navigation */}
              <div className="bg-white rounded-t-xl border border-b-0 border-gray-200 shadow-sm">
                <div className="flex">
                  {[
                    { key: 'components', label: 'Model', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
                    { key: 'results', label: 'Detailed Results', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                    { key: 'diagnostics', label: 'Diagnostics', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setRightPanelTab(tab.key)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
                        rightPanelTab === tab.key
                          ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                      </svg>
                      {tab.label}
                      {tab.key === 'diagnostics' && Object.keys(violations).length > 0 && (
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── COMPONENTS TAB ── */}
              {rightPanelTab === 'components' && (
    <>
    <div className="bg-white rounded-b-xl shadow-lg border border-t-0 border-gray-200">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200">
    <div className="flex items-center mb-2 sm:mb-0">
      <div>
        <h2 className="text-lg font-bold text-gray-900">
          Blend Components
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {components.length} component{components.length !== 1 ? 's' : ''} loaded
        </p>
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-2 justify-end">
      <div className="relative w-56">
        <select
          id="component-group"
          value={selectedComponentGroup}
          onChange={handleComponentGroupChange}
          className="w-full px-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white text-sm appearance-none"
          disabled={loadingComponentGroups || loadingComponentGroup}
        >
          <option value="">Component Group...</option>
          {getComponentGroups().map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.components.length} components)
            </option>
          ))}
        </select>

        <svg
          className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        {loadingComponentGroup && (
          <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
            <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>

      <button
        onClick={openBlendComponentsModal}
        disabled={loadingAddComponent}
        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loadingAddComponent ? (
          <>
            <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            Add Component
          </>
        )}
      </button>

      <button
        onClick={openPropertyColumnModal}
        disabled={loadingPropertyColumn}
        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loadingPropertyColumn ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Column
          </>
        )}
      </button>
    </div>
  </div>

  {/* Table Section */}
  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
    <table className="w-full table-auto">
      <thead className="bg-gray-100 sticky top-0 z-10">
        <tr>
          {/* Sticky Serial Number Column Header */}
          <th className="sticky left-0 bg-gray-100 z-20 w-12 px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
            #
          </th>
          {/* Sticky Component Name Column Header */}
          <th className="sticky left-12 bg-gray-100 z-20 min-w-[140px] max-w-[160px] px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase border-r border-gray-200">
            Component
          </th>
          <th className="min-w-[120px] px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
            Cost ({getCurrencySymbol()})
          </th>
          <th className="min-w-[120px] px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
            Min {getCurrentUnitWithBrackets()}
          </th>
          <th className="min-w-[120px] px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
            Max {getCurrentUnitWithBrackets()}
          </th>
          <th className="min-w-[140px] px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
            Available {getCurrentUnitWithBrackets()}
          </th>
          <th className="min-w-[100px] px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
            Solution
          </th>
          <th className="min-w-[100px] px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
            SG
          </th>
          {selectedPropertyColumns.map((prop) => (
            <th
              key={prop}
              className="min-w-[120px] px-3 py-3 text-right text-xs font-semibold text-gray-700"
            >
              {prop}
            </th>
          ))}
          <th className="w-20 px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
            Actions
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-100">
        {components.length === 0 ? (
          <tr>
            <td
              colSpan={9 + selectedPropertyColumns.length}
              className="px-4 py-6 text-center text-gray-500"
            >
              <p className="text-sm">No components loaded</p>
              <p className="text-xs text-gray-400 mt-1">
                Add components manually, select a group, or upload an Excel file
              </p>
            </td>
          </tr>
        ) : (
          components.map((component, index) => (
            <tr
              key={index}
              className="hover:bg-gray-50 transition-colors"
            >
              <td className="sticky left-0 z-10 w-12 px-2 py-3 bg-white text-xs text-gray-400 font-medium text-center">
                {index + 1}
              </td>

              {/* Component Name Column - Sticky */}
              <td className="sticky left-12 z-10 px-3 py-3 text-sm font-medium text-gray-900 min-w-[140px] max-w-[160px] bg-white border-r border-gray-100">
                <div className="truncate" title={component.name || "Unnamed Component"}>
                  {component.name || "Unnamed Component"}
                </div>
              </td>

              {["cost", "min", "max", "availability"].map((field) => (
                <td key={field} className="px-3 py-3 min-w-[120px]">
                  <input
                    type="number"
                    step={field === 'cost' ? '0.01' : '0.1'}
                    className={`w-full px-3 py-2 border rounded-lg text-right text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${field === 'cost' && (!component.cost || Number(component.cost) <= 0)
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                      }`}
                    value={component[field] ?? ""}
                    onChange={(e) => editComp(index, field, e.target.value)}
                    placeholder={field === 'cost' ? '0.00' : '0.0'}
                  />
                </td>
              ))}

              <td className="px-3 py-3 min-w-[100px]">
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-right text-sm bg-gray-50 text-gray-600"
                  value={blend[component.name] || 0}
                  readOnly
                />
              </td>

              {/* Specific Gravity Column — editable */}
              <td className="px-3 py-3 min-w-[100px]">
                <input
                  type="number"
                  step="0.0001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  value={component.gravity ?? ""}
                  onChange={(e) => editComp(index, 'gravity', e.target.value)}
                  placeholder="SG"
                />
              </td>

              {/* Dynamic Property Columns */}
              {selectedPropertyColumns.map((prop) => (
                <td key={prop} className="px-3 py-3 min-w-[120px]">
                  <input
                    type="number"
                    step="0.001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    value={component[prop] ?? 0}
                    onChange={(e) => editComp(index, prop, e.target.value)}
                    placeholder={prop}
                  />
                </td>
              ))}

              <td className="px-3 py-3 text-center w-20">
                <button
                  onClick={() => removeComponent(index)}
                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove Component"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
</div>

              {/* ── PROPERTY CONSTRAINTS SECTION (within Model tab) ── */}
              <div className="bg-white rounded-b-xl shadow-lg border border-t-0 border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center mb-2 sm:mb-0">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        Property Constraints
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {propertySpecs.length} properties loaded · Editable
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-end gap-2 w-full sm:w-auto">
                    {savedGroups.length > 0 && (
                      <div className="w-full sm:w-40">
                        <div className="relative w-full mt-1">
                          <select
                            id="property-group"
                            value={selectedGroup}
                            onChange={handleGroupChange}
                            disabled={loadingPropertyGroups || loadingPropertyGroup}
                            className="w-full px-4 pr-10 py-2 border border-gray-300 rounded-lg 
               focus:ring-2 focus:ring-purple-500 focus:border-transparent 
               transition-all bg-white text-sm appearance-none"
                          >
                            <option value="">Property Group...</option>
                            {getPropertyGroups().map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.name} ({group.properties.length})
                              </option>
                            ))}
                          </select>
                          <svg
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {loadingPropertyGroup && (
                            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                              <svg className="animate-spin h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                        </div>

                      </div>
                    )}

                    {savedPropertySets.length > 0 && (
                      <div className="w-full sm:w-40">
                        <div className="relative w-full mt-1">
                          <select
                            id="property-set"
                            value={selectedPropertySet}
                            onChange={handlePropertySetChange}
                            disabled={loadingPropertySet}
                            className="w-full px-4 pr-10 py-2 border border-gray-300 
               rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent 
               transition-all bg-white text-sm appearance-none"
                          >
                            <option value="">Property Set</option>
                            {savedPropertySets.map((set) => (
                              <option key={set._id || set.id} value={set._id || set.id}>
                                {set.name} ({set.properties?.length || 0})
                              </option>
                            ))}
                          </select>

                          <svg
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {loadingPropertySet && (
                            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                              <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                        </div>

                      </div>
                    )}

                    <button
                      onClick={openGlobalPropertiesModal}
                      disabled={loadingGlobalProperties}
                      className="flex items-center justify-center gap-2 px-4 py-2 
                 bg-purple-600 text-white rounded-lg hover:bg-purple-900 
                 transition-colors font-medium text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingGlobalProperties ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 0 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                          Add Property
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Property
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          UOM
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Min
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Max
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-amber-700 uppercase tracking-wider" title="Allow this constraint to be violated (soft constraint)">
                          Relax
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-amber-700 uppercase tracking-wider" title="Penalty per index-unit violation (higher = more strongly enforced)">
                          Penalty
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Result
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-100">
                      {/* ── Specific Gravity – editable constraint row ── */}
                      {(() => {
                        const blendedSG = getBlendedSG();
                        // Check if SG had a violation from the last run
                        const sgViol = violations["specific gravity"] || violations["specific_gravity"] || violations["gravity"];
                        const sgResult = blendedSG;
                        const sgMinNum = sgMin !== "" ? Number(sgMin) : null;
                        const sgMaxNum = sgMax !== "" ? Number(sgMax) : null;
                        const sgTolerance = 0.0005;
                        const sgInRange = sgResult !== null && sgResult !== undefined
                          && (sgMinNum === null || sgResult >= (sgMinNum - sgTolerance))
                          && (sgMaxNum === null || sgResult <= (sgMaxNum + sgTolerance));
                        const hasSgConstraint = sgMinNum !== null || sgMaxNum !== null;

                        return (
                          <tr className="bg-blue-50 hover:bg-blue-100 transition-colors">
                            <td className="px-3 py-2 text-sm font-semibold text-gray-800">
                              Specific Gravity
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-500">
                              <span className="text-xs text-gray-400">Vol.Wtd</span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                step="0.001"
                                className="w-full px-2 py-1 border border-gray-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                value={sgMin}
                                placeholder="Min"
                                onChange={(e) => setSgMin(e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                step="0.001"
                                className="w-full px-2 py-1 border border-gray-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                value={sgMax}
                                placeholder="Max"
                                onChange={(e) => setSgMax(e.target.value)}
                              />
                            </td>
                            {/* Relax toggle */}
                            <td className="px-3 py-2 text-center">
                              <label className="relative inline-flex items-center cursor-pointer" title={sgRelax ? "Soft constraint" : "Hard constraint"}>
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={sgRelax}
                                  onChange={(e) => setSgRelax(e.target.checked)}
                                />
                                <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer peer-checked:after:translate-x-4 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all"></div>
                              </label>
                            </td>
                            {/* Penalty */}
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                min="0"
                                step="10000"
                                className={`w-full px-2 py-1 border rounded-lg text-right text-xs focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors ${
                                  sgRelax
                                    ? "border-amber-300 bg-amber-50 text-slate-700"
                                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                                value={sgPenalty}
                                placeholder={sgRelax ? "1000000" : "—"}
                                disabled={!sgRelax}
                                onChange={(e) => setSgPenalty(e.target.value)}
                              />
                            </td>
                            {/* Result */}
                            <td className="px-3 py-2 text-center">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  sgInRange && sgResult !== null
                                    ? "bg-green-100 text-green-800 border border-green-200"
                                    : sgViol?.infeasibility_driver
                                    ? "bg-red-100 text-red-800 border border-red-300"
                                    : !hasSgConstraint && sgResult !== null
                                    ? "bg-blue-100 text-blue-800 border border-blue-200"
                                    : sgResult !== null
                                    ? "bg-red-100 text-red-800 border border-red-200"
                                    : "bg-blue-100 text-blue-800 border border-blue-200"
                                }`}
                                title={
                                  sgViol?.infeasibility_driver
                                    ? `Violated: ${sgViol.violation_side?.replace("_", " ")} by ${(sgViol.violation_below_min || sgViol.violation_above_max || 0).toFixed(4)}`
                                    : sgResult !== null
                                    ? `Blended SG: ${sgResult.toFixed(4)}`
                                    : "No result"
                                }
                              >
                                {sgResult !== null ? sgResult.toFixed(4) : "—"}
                                {sgResult !== null && hasSgConstraint && (
                                  <span className="ml-1">
                                    {sgInRange ? "✓" : sgViol?.infeasibility_driver ? "⚠" : "✗"}
                                  </span>
                                )}
                              </span>
                            </td>
                            {/* Actions — clear button */}
                            <td className="px-3 py-2 text-center">
                              {hasSgConstraint ? (
                                <button
                                  onClick={() => { setSgMin(""); setSgMax(""); setSgRelax(false); setSgPenalty(""); }}
                                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Clear SG Constraint"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })()}
                      {/* ── User-defined property constraint rows ── */}
                      {propertySpecs.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-4 py-6 text-center text-gray-500 bg-gray-50"
                          >
                            <p className="text-sm">No property constraints loaded</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Add properties manually or select a saved group
                            </p>
                          </td>
                        </tr>
                      ) : (
                        propertySpecs.map((p, i) => {
                          const propKey = (p.property || '').trim().toLowerCase();
                          const resultValue = propertyResultsForDisplay[propKey] !== undefined 
                            ? propertyResultsForDisplay[propKey] 
                            : propertyResultsForDisplay[p.property]; 
                          const minValue = Number(p.min) || 0;
                          const maxValue = Number(p.max) || 100;
                          
                          const tolerance = 0.0005;
                          const isWithinRange = resultValue !== undefined && 
                                               resultValue >= (minValue - tolerance) && 
                                               resultValue <= (maxValue + tolerance);
                          
                          const isFromComponentGroup = componentGroupProperties.some(
                            groupProp => groupProp.property === p.property
                          );

                          // Violation data from last optimization (if any)
                          const viol = violations[propKey] || violations[(p.property || '').toLowerCase()];

                          return (
                            <tr key={i} className={`transition-colors hover:bg-blue-100 ${isFromComponentGroup ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-blue-50'}`}>
                              <td className="px-3 py-2 text-sm font-medium text-gray-700">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    value={p.property || ""}
                                    placeholder="Property name"
                                    onChange={(e) => editProp(i, "property", e.target.value)}
                                  />
                                </div>
                              </td>

                              <td className="px-3 py-2 text-sm text-gray-500">
                                <span className="text-sm text-gray-500">{p.uom || "—"}</span>
                              </td>

                              <td className="px-3 py-2 text-right">
                                <input
                                  type="number"
                                  data-row={`prop-${i}`} data-field="min"
                                  className={`w-full px-2 py-1 border rounded-lg text-right text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                    p.min !== '' && p.max !== '' && Number(p.min) > Number(p.max) ? 'border-red-400 bg-red-50' : 'border-gray-300'
                                  }`}
                                  value={p.min || ""}
                                  placeholder="Min"
                                  onChange={(e) => editProp(i, "min", e.target.value)}
                                  onKeyDown={(e) => handleCellKeyDown(e, i, 'min', 'prop')}
                                />
                              </td>

                              <td className="px-3 py-2 text-right">
                                <input
                                  type="number"
                                  data-row={`prop-${i}`} data-field="max"
                                  className={`w-full px-2 py-1 border rounded-lg text-right text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                    p.min !== '' && p.max !== '' && Number(p.min) > Number(p.max) ? 'border-red-400 bg-red-50' : 'border-gray-300'
                                  }`}
                                  value={p.max || ""}
                                  placeholder="Max"
                                  onChange={(e) => editProp(i, "max", e.target.value)}
                                  onKeyDown={(e) => handleCellKeyDown(e, i, 'max', 'prop')}
                                />
                                {p.min !== '' && p.max !== '' && Number(p.min) > Number(p.max) && (
                                  <p className="text-red-500 text-[10px] mt-0.5">Min must be ≤ Max</p>
                                )}
                              </td>

                              {/* ── Relax toggle ── */}
                              <td className="px-3 py-2 text-center">
                                <label className="relative inline-flex items-center cursor-pointer" title={p.relax ? "Soft constraint (can be violated)" : "Hard constraint (must be satisfied)"}>
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={Boolean(p.relax)}
                                    onChange={(e) => editProp(i, "relax", e.target.checked)}
                                  />
                                  <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer peer-checked:after:translate-x-4 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all"></div>
                                </label>
                              </td>

                              {/* ── Penalty input (only enabled when relax=true) ── */}
                              <td className="px-3 py-2 text-right">
                                <input
                                  type="number"
                                  min="0"
                                  step="10000"
                                  className={`w-full px-2 py-1 border rounded-lg text-right text-xs focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors ${
                                    p.relax
                                      ? "border-amber-300 bg-amber-50 text-slate-700"
                                      : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                  }`}
                                  value={p.penalty || ""}
                                  placeholder={p.relax ? "1000000" : "—"}
                                  disabled={!p.relax}
                                  title={p.relax ? "Penalty per index-unit of violation" : "Enable Relax first"}
                                  onChange={(e) => editProp(i, "penalty", e.target.value)}
                                />
                              </td>

                              <td className="px-3 py-2 text-center">
                                <span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                    isWithinRange
                                      ? "bg-green-100 text-green-800 border border-green-200"
                                      : viol?.infeasibility_driver
                                      ? "bg-red-100 text-red-800 border border-red-300"
                                      : resultValue !== undefined
                                      ? "bg-red-100 text-red-800 border border-red-200"
                                      : "bg-blue-100 text-blue-800 border border-blue-200"
                                  }`}
                                  title={
                                    viol?.infeasibility_driver && !isWithinRange
                                      ? `Violated: ${viol.violation_side?.replace("_", " ")} by ${(viol.violation_below_min || viol.violation_above_max || 0).toFixed(3)} units`
                                      : resultValue !== undefined
                                      ? `Result: ${resultValue.toFixed(3)} (Min: ${minValue}, Max: ${maxValue})`
                                      : "No result calculated"
                                  }
                                >
                                  {resultValue !== undefined
                                    ? resultValue.toFixed(3)
                                    : "—"}
                                  {resultValue !== undefined && (
                                    <span className="ml-1">
                                      {isWithinRange ? "✓" : viol?.infeasibility_driver ? "⚠" : "✗"}
                                    </span>
                                  )}
                                </span>
                              </td>

                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => removeProp(i)}
                                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove Property"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })  
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              </>
              )} {/* end model tab (components + properties) */}

              {/* ── RESULTS TAB ── */}
              {rightPanelTab === 'results' && (
              <div className="bg-white rounded-b-xl shadow-lg border border-t-0 border-gray-200 p-5 space-y-5">
                {/* Results Summary */}
                {Object.keys(blend).length > 0 ? (
                  <>
                    {/* Blend Slate Table */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-gray-900">Blend Slate</h3>
                        <button onClick={downloadCSVPack}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                          Export CSV
                        </button>
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Component</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Volume ({volumeUnit})</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Mass ({massUnit.toUpperCase()})</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Vol %</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Wt %</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Cost ({getCurrencySymbol()})</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(() => {
                              const totalBlendVol = Object.values(blend).reduce((a, b) => a + Number(b || 0), 0);
                              const totalBlendMass = costBreakdown.reduce((s, c) => s + (c.mass || 0), 0);
                              const totalBlendVolume = costBreakdown.reduce((s, c) => s + (c.volume || 0), 0);
                              const totalBlendCost = costBreakdown.reduce((s, c) => s + (c.totalCost || 0), 0);
                              
                              const rows = Object.entries(blend).map(([name, vol], idx) => {
                                const cb = costBreakdown.find(c => c.name === name);
                                const compVol = cb ? (cb.volume || 0) : Number(vol);
                                const compMass = cb ? (cb.mass || 0) : 0;
                                const volPct = totalBlendVolume > 0 ? (compVol / totalBlendVolume * 100) : (totalBlendVol > 0 ? (Number(vol) / totalBlendVol * 100) : 0);
                                const wtPct = totalBlendMass > 0 ? (compMass / totalBlendMass * 100) : 0;
                                return (
                                  <tr key={idx} className={idx % 2 === 1 ? 'bg-gray-50/50' : ''}>
                                    <td className="px-3 py-2 font-medium text-gray-900">{name}</td>
                                    <td className="px-3 py-2 text-right font-mono text-gray-700">{compVol ? compVol.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono text-gray-700">{compMass ? compMass.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</td>
                                    <td className="px-3 py-2 text-right text-gray-600">{volPct.toFixed(1)}%</td>
                                    <td className="px-3 py-2 text-right text-gray-600">{wtPct > 0 ? wtPct.toFixed(1) + '%' : '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono text-gray-700">{cb ? cb.totalCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                                  </tr>
                                );
                              });
                              
                              rows.push(
                                <tr key="totals" className="bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-300 font-semibold">
                                  <td className="px-3 py-2.5 text-gray-900 font-bold">Total</td>
                                  <td className="px-3 py-2.5 text-right font-mono text-gray-900">{(totalBlendVolume || totalBlendVol).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                  <td className="px-3 py-2.5 text-right font-mono text-gray-900">{totalBlendMass > 0 ? totalBlendMass.toLocaleString(undefined, { maximumFractionDigits: 2 }) : (weight || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                  <td className="px-3 py-2.5 text-right text-gray-900">100%</td>
                                  <td className="px-3 py-2.5 text-right text-gray-900">100%</td>
                                  <td className="px-3 py-2.5 text-right font-mono text-emerald-700 font-bold">{getCurrencySymbol()}{(totalBlendCost || totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                              );
                              
                              return rows;
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Property Results */}
                    {Object.keys(propertyResultsForDisplay).length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-3">Property Results</h3>
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Property</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">UOM</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Result</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Min</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Max</th>
                                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {Object.entries(propertyResultsForDisplay).map(([name, val], idx) => {
                                const spec = propertySpecs.find(p => (p.property || '').toLowerCase() === name.toLowerCase() || p.property === name);
                                const minVal = spec ? Number(spec.min) : null;
                                const maxVal = spec ? Number(spec.max) : null;
                                const inRange = minVal !== null && maxVal !== null && val >= minVal && val <= maxVal;
                                return (
                                  <tr key={idx} className={idx % 2 === 1 ? 'bg-gray-50/50' : ''}>
                                    <td className="px-3 py-2 font-medium text-gray-900 uppercase">{name}</td>
                                    <td className="px-3 py-2 text-left text-gray-500 text-xs">{spec ? (spec.uom || spec.UOM || spec.unit || '—') : '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold text-gray-800">{Number(val).toFixed(3)}</td>
                                    <td className="px-3 py-2 text-right text-gray-600">{spec ? spec.min : '—'}</td>
                                    <td className="px-3 py-2 text-right text-gray-600">{spec ? spec.max : '—'}</td>
                                    <td className="px-3 py-2 text-center">
                                      {spec ? (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${inRange ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                          {inRange ? '✓ PASS' : '✗ FAIL'}
                                        </span>
                                      ) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Cost Summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div className={`rounded-xl p-3 border ${
                        status.toUpperCase() === 'OPTIMAL' || status.toUpperCase() === 'SUCCESS' 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : status.toUpperCase() === 'RELAXED OPTIMAL' 
                          ? 'bg-amber-50 border-amber-200' 
                          : status.toUpperCase() === 'INFEASIBLE'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className={`text-xs font-medium ${
                          status.toUpperCase() === 'OPTIMAL' || status.toUpperCase() === 'SUCCESS' 
                            ? 'text-emerald-600' 
                            : status.toUpperCase() === 'RELAXED OPTIMAL' 
                            ? 'text-amber-600' 
                            : status.toUpperCase() === 'INFEASIBLE'
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}>Status</div>
                        <div className={`text-sm font-bold mt-1 ${
                          status.toUpperCase() === 'OPTIMAL' || status.toUpperCase() === 'SUCCESS' 
                            ? 'text-emerald-700' 
                            : status.toUpperCase() === 'RELAXED OPTIMAL' 
                            ? 'text-amber-700' 
                            : status.toUpperCase() === 'INFEASIBLE'
                            ? 'text-red-700'
                            : 'text-gray-500'
                        }`}>{status || '—'}</div>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                        <div className="text-xs text-green-600 font-medium">Total Volume</div>
                        <div className="text-sm font-bold text-green-800 mt-1">{(totalVolume || Object.values(blend).reduce((a, b) => a + Number(b || 0), 0)).toLocaleString(undefined, { maximumFractionDigits: 1 })} {volumeUnit}</div>
                      </div>
                      <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-200">
                        <div className="text-xs text-indigo-600 font-medium">Total Weight</div>
                        <div className="text-sm font-bold text-indigo-800 mt-1">{weight > 0 ? `${weight.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${massUnit.toUpperCase()}` : '—'}</div>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                        <div className="text-xs text-purple-600 font-medium">Total Cost</div>
                        <div className="text-sm font-bold text-purple-800 mt-1">{totalCost ? `${getCurrencySymbol()}${totalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}</div>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                        <div className="text-xs text-orange-600 font-medium">Unit Cost</div>
                        <div className="text-sm font-bold text-orange-800 mt-1">{totalCost && volume ? `${getCurrencySymbol()}${(totalCost / Number(volume)).toFixed(2)}` : '—'}</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                    <p className="text-sm font-medium">No results yet</p>
                    <p className="text-xs mt-1">Run an optimization to see blend results here</p>
                  </div>
                )}
              </div>
              )} {/* end results tab */}

              {/* ── DIAGNOSTICS TAB ── */}
              {rightPanelTab === 'diagnostics' && (
              <div className="bg-white rounded-b-xl shadow-lg border border-t-0 border-gray-200 p-5 space-y-5">
                {Object.keys(violations).length > 0 ? (
                  <>
                    {/* Summary Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-slate-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        <h3 className="text-sm font-bold text-gray-900">Constraint Violation Report</h3>
                        {infeasibilityDrivers.length > 0 && (
                          <span className="text-xs bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-semibold">
                            {infeasibilityDrivers.length} driver{infeasibilityDrivers.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Cost breakdown if penalty > 0 */}
                    {penaltyCost > 0 && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs">
                        <div className="flex-1"><span className="text-gray-500">Blend cost:</span><span className="ml-1 font-semibold text-gray-800">{getCurrencySymbol()}{blendCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                        <div className="text-gray-300">+</div>
                        <div className="flex-1"><span className="text-red-500">Penalty:</span><span className="ml-1 font-semibold text-red-700">{getCurrencySymbol()}{penaltyCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                        <div className="text-gray-300">=</div>
                        <div><span className="font-bold text-gray-900">{getCurrencySymbol()}{totalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                      </div>
                    )}

                    {/* Violation Cards */}
                    <div className="space-y-2">
                      {Object.entries(violations).map(([pname, v]) => (
                        <div key={pname} className={`rounded-xl p-4 border text-xs ${v.infeasibility_driver ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${v.infeasibility_driver ? 'bg-red-500' : 'bg-green-500'}`} />
                              <span className="font-bold text-gray-800 uppercase tracking-wide text-sm">{pname}</span>
                              {v.infeasibility_driver && (
                                <span className="bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded text-xs font-semibold">
                                  {v.violation_side === "below_min" ? "↓ Below Min" : v.violation_side === "above_max" ? "↑ Above Max" : "⚠ Both Sides"}
                                </span>
                              )}
                            </div>
                            {v.penalty_contribution > 0 && (
                              <span className="text-red-600 font-semibold">{getCurrencySymbol()}{v.penalty_contribution.toLocaleString(undefined, {maximumFractionDigits: 0})} penalty</span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-white rounded-lg p-2 border border-gray-100"><div className="text-gray-400 font-medium">Min</div><div className="font-semibold text-gray-700">{v.spec_min !== null ? v.spec_min : '—'}</div></div>
                            <div className={`rounded-lg p-2 border font-bold ${v.infeasibility_driver ? 'bg-red-100 border-red-200 text-red-800' : 'bg-green-100 border-green-200 text-green-800'}`}><div className="text-xs font-medium opacity-70">Achieved</div><div>{v.achieved_value !== null ? Number(v.achieved_value).toFixed(3) : '—'}</div></div>
                            <div className="bg-white rounded-lg p-2 border border-gray-100"><div className="text-gray-400 font-medium">Max</div><div className="font-semibold text-gray-700">{v.spec_max !== null ? v.spec_max : '—'}</div></div>
                          </div>
                          {v.infeasibility_driver && (
                            <div className="mt-2 text-red-600 font-medium text-center text-sm">
                              {v.violation_below_min > 0 && `Short by ${v.violation_below_min.toFixed(3)} units`}
                              {v.violation_above_max > 0 && `Exceeds by ${v.violation_above_max.toFixed(3)} units`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {status.toUpperCase() === "INFEASIBLE" && infeasibilityDrivers.length > 0 && (
                      <div className="text-xs text-amber-700 bg-amber-100 rounded-xl p-3 border border-amber-200">
                        💡 <strong>Tip:</strong> Enable "Add Infeasibility Breaker" and re-run to automatically accept
                        a relaxed solution that satisfies all other constraints while minimising property violations.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p className="text-sm font-medium">No diagnostics</p>
                    <p className="text-xs mt-1">Run an optimization to see violation reports here</p>
                  </div>
                )}
              </div>
              )} {/* end diagnostics tab */}

            </section>
          </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;